import { useState, useRef, useCallback, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Volume2, VolumeX, User, Clock, X, Send, Play, Download, MessageSquare, ExternalLink, Shield, AlertTriangle, CheckCircle, Keyboard, Globe, History, FileText, ChevronRight, Plus, Phone, Mail, UserCheck, Star, Trash2, Edit2, Check, Bell, Bookmark, Flag, Users } from "lucide-react";
import axios from "axios";
import { useSession } from "@/App";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "@/i18n";
import { useTheme } from "@/themes/themes";
import ThemePicker, { pushThemeToExtension, syncUserToExtension } from "@/components/ThemePicker";
import HavnTalkWave from "@/components/HavnTalkWave";
import useAudioAmplitude from "@/hooks/useAudioAmplitude";
import { FullSiteReport } from "@/components/FullSiteReport";
import "@/components/FullSiteReport.css";
import "@/styles/havn-ambient.css";

// Voice is ElevenLabs-only. The browser's built-in speechSynthesis (the
// robotic "computer voice") is intentionally not used anywhere in the web
// app — it used to overlap ElevenLabs and sound off, so it was removed.

const API = '/api';

// CANONICAL ASSETS - Using absolute paths for routing compatibility
// Theme orb: defaults to SCANNING state (not CAUTION) for unknown states
const HERO_ORB_URL = "/assets/orb_havn_v4.png";
const WORDMARK_URL = "/havn-logo-white.png";
const BG_IMAGE_URL = "/assets/Smooth_blue_gradient_backdrop.png";

// Protection states - CANONICAL: Orb reflects ONLY protection layer state
const PROTECTION_STATES = {
  SCANNING: 'scanning',  // Default state (NOT caution)
  SAFE: 'safe',
  CAUTION: 'caution',
  DANGER: 'danger'
};

// Background style with soft glow and vignette
const BG_STYLE = {
  backgroundImage: `
    radial-gradient(ellipse at center, transparent 0%, transparent 40%, rgba(0,0,0,0.12) 100%),
    radial-gradient(circle at center, rgba(255,250,235,0.2) 0%, rgba(255,250,235,0.08) 25%, transparent 50%),
    url(${BG_IMAGE_URL})
  `,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
};

// Greeting configuration — personalized with user's first name, translated
function getGreetingText(authUser, tFn) {
  const firstName = authUser?.user_metadata?.first_name
    || authUser?.user_metadata?.full_name?.split(' ')[0]
    || '';
  return firstName
    ? tFn('greeting', { name: ` ${firstName}` })
    : tFn('greeting_generic');
}

const STATES = {
  IDLE: 'idle',
  LISTENING: 'listening',
  PROCESSING: 'processing',
  SPEAKING: 'speaking',
  ERROR: 'error'
};

// Generate a short, meaningful session title from the first user message
function generateSessionTitle(text) {
  const t = (text || '').trim().toLowerCase();

  // Filler words to strip
  const FILLER = new Set(['i', 'me', 'my', 'a', 'an', 'the', 'is', 'are', 'was', 'were', 'am',
    'it', 'its', 'this', 'that', 'what', 'how', 'do', 'does', 'did', 'can', 'could',
    'just', 'got', 'get', 'have', 'had', 'has', 'been', 'being', 'about', 'from',
    'with', 'for', 'and', 'but', 'or', 'so', 'to', 'of', 'on', 'in', 'at', 'by',
    'up', 'if', 'no', 'not', 'be', 'we', 'they', 'you', 'he', 'she', 'some',
    'like', 'know', 'think', 'want', 'need', 'said', 'says', 'saying',
    'really', 'very', 'also', 'too', 'there', 'here', 'then', 'than',
  ]);

  // Topic keywords that make good titles
  const TOPIC_PATTERNS = [
    { re: /scam|fraud|phishing|fake|suspicious/i, title: 'Possible Scam' },
    { re: /purolator|fedex|ups|canada\s*post|dhl|delivery|package|tracking/i, title: null }, // extract brand
    { re: /amazon|ebay|walmart|costco|shopping|order|refund|return/i, title: null },
    { re: /bank|cibc|rbc|td|bmo|scotiabank|etransfer|e-transfer/i, title: null },
    { re: /password|login|hack|account|security|locked/i, title: 'Account Security' },
    { re: /virus|malware|popup|computer|tech\s*support/i, title: 'Tech Support Issue' },
    { re: /email|text|message|call|phone/i, title: null },
    { re: /website|site|link|url/i, title: 'Website Check' },
    { re: /check/i, title: 'Website Check' },
  ];

  // Generic greetings → skip to fallback
  const GREETINGS = /^(hi|hello|hey|good\s*(morning|afternoon|evening)|howdy|sup|yo)\b/i;
  if (GREETINGS.test(t)) return 'New Conversation';

  // Try topic patterns first
  for (const { re, title } of TOPIC_PATTERNS) {
    const match = t.match(re);
    if (match) {
      if (title) return title;
      // Extract the matched word as the core topic
      const keyword = match[0].charAt(0).toUpperCase() + match[0].slice(1);
      // Get surrounding context (1-2 words around the keyword)
      const words = text.trim().split(/\s+/);
      const idx = words.findIndex(w => re.test(w));
      if (idx >= 0) {
        const context = words.slice(Math.max(0, idx - 1), idx + 2)
          .filter(w => !FILLER.has(w.toLowerCase()))
          .slice(0, 3)
          .join(' ');
        if (context.length > 2) return context.charAt(0).toUpperCase() + context.slice(1);
      }
      return keyword + ' Question';
    }
  }

  // Fallback: extract first 2-4 meaningful words
  const meaningful = text.trim().split(/\s+/)
    .filter(w => !FILLER.has(w.toLowerCase()) && w.length > 1)
    .slice(0, 4)
    .join(' ');
  if (meaningful.length > 2) {
    return meaningful.charAt(0).toUpperCase() + meaningful.slice(1);
  }

  return 'New Conversation';
}

const STATUS_MESSAGES = {
  [STATES.IDLE]: 'Tap to talk',
  [STATES.LISTENING]: 'Listening — tap again to send',
  [STATES.PROCESSING]: 'Thinking...',
  [STATES.SPEAKING]: 'Speaking...',
  [STATES.ERROR]: 'Let me try again'
};

const TIMEOUTS = {
  STT: 30000,
  CHAT: 45000,
  TTS: 30000,  // base — overridden dynamically for longer scripts
  AUDIO_PLAY: 15000,  // max time to wait for audio ended event (iOS/Safari safety net)
  THINKING_SAFETY: 50000  // auto-exit if stuck (must be > CHAT timeout)
};

// Dynamic TTS timeout based on word count: words / 2.5 wps + 2s buffer, minimum 30s
const getTtsTimeout = (text) => {
  if (!text) return TIMEOUTS.TTS;
  const words = text.split(/\s+/).length;
  const estimated = Math.ceil((words / 2.5) + 2) * 1000;
  return Math.max(TIMEOUTS.TTS, estimated);
};

const generateTurnId = () => `turn_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;

const logTurn = (turnId, action, details = '') => {
  console.log(`[${turnId}] ${action}`, details);
};

const playTtsBlob = async (blob, contentType) => {
  // ElevenLabs-only: ElevenLabs returns audio/mpeg. Anything else (the
  // fallback JSON) means ElevenLabs is unavailable — stay silent rather
  // than speaking with the browser's robotic computer voice.
  if (contentType?.includes('application/json')) {
    return;
  }

  const url = URL.createObjectURL(blob);
  if (audioRef.current) {
    audioRef.current.src = url;
    // Ignore autoplay failures — no computer-voice fallback.
    await audioRef.current.play().catch(() => {});
  }
};

// ============================================
// INTENT INFERENCE SYSTEM
// Routes user messages to appropriate conversation flow
// ============================================

const INTENTS = {
  WEBSITE_RESEARCH: 'WEBSITE_RESEARCH',
  PHONE_CALL_INCIDENT: 'PHONE_CALL_INCIDENT',
  EMAIL_OR_TEXT_SCAM: 'EMAIL_OR_TEXT_SCAM',
  PAYMENT_OR_SUBSCRIPTION: 'PAYMENT_OR_SUBSCRIPTION',
  GENERAL_HELP: 'GENERAL_HELP'
};

// ============================================
// v3.2: SENIOR-FRIENDLY TEXT FORMATTER
// Converts technical jargon to simple language
// ============================================

const toSeniorFriendly = (text) => {
  if (!text) return text;
  
  const replacements = {
    'domain age': 'how long the website has existed',
    'HTTPS': 'secure connection',
    'SSL certificate': 'security certificate',
    'verifiable contact information': 'clear contact details',
    'phishing': 'scam website pretending to be something else',
    'malware': 'harmful software',
    'suspicious indicators': 'warning signs',
    'risk baseline': 'safety level',
    'domain trust': 'website trustworthiness',
    'WHOIS': 'website registration',
    'registrar': 'website registration company',
    'domain': 'website address',
    'TLD': 'website ending',
    'DNS': 'website address',
    'IP address': 'internet location',
    'redirect': 'sends you to another page',
    'URL': 'web address',
    'authentication': 'login security',
    'credential': 'login information',
    'compromised': 'not safe anymore',
  };
  
  let result = text;
  for (const [technical, friendly] of Object.entries(replacements)) {
    const regex = new RegExp(technical, 'gi');
    result = result.replace(regex, friendly);
  }
  return result;
};

// Format website research results for voice output
const formatForVoice = (riskData, hostname) => {
  if (!riskData) return null;
  
  const siteName = hostname || 'this website';
  let response = `This is about ${siteName}. `;
  
  const riskLevel = riskData.risk_level || 'UNKNOWN';
  const score = riskData.score || 50;
  
  if (riskLevel === 'SAFE' || score < 30) {
    response += "This looks like a legitimate website. ";
    if (riskData.recommendation) {
      response += toSeniorFriendly(riskData.recommendation);
    } else {
      response += "You can proceed normally, but always be careful with personal information.";
    }
  } else if (riskLevel === 'DANGER' || score >= 70) {
    response += "I'm concerned about this site. ";
    if (riskData.recommendation) {
      response += toSeniorFriendly(riskData.recommendation);
    } else {
      response += "I wouldn't enter any personal information or payment details here.";
    }
  } else {
    response += "I'd be careful here. ";
    if (riskData.recommendation) {
      response += toSeniorFriendly(riskData.recommendation);
    } else {
      response += "Don't enter personal information until you're sure this is a real website.";
    }
  }
  
  // Add follow-up if available
  if (riskData.follow_up_question) {
    response += ` ${riskData.follow_up_question}`;
  }
  
  return response;
};

const INTENT_FIRST_QUESTIONS = {
  [INTENTS.WEBSITE_RESEARCH]: "Send me the website link and tell me what you were about to do there.",
  [INTENTS.PHONE_CALL_INCIDENT]: "What did the caller say they wanted, and did they ask for money or personal info?",
  [INTENTS.EMAIL_OR_TEXT_SCAM]: "Can you paste the message here, or tell me who it claimed to be from?",
  [INTENTS.PAYMENT_OR_SUBSCRIPTION]: "Were you about to pay, subscribe, or enter your card details? What was the amount?",
  [INTENTS.GENERAL_HELP]: "Tell me what happened — I'm listening."
};

/**
 * Extracts URLs from text message
 */
const extractUrlFromMessage = (text) => {
  // Match URLs with various patterns
  const urlPatterns = [
    // Full URLs with protocol
    /https?:\/\/[^\s<>"{}|\\^`[\]]+/gi,
    // URLs starting with www
    /www\.[^\s<>"{}|\\^`[\]]+/gi,
    // Domain-only patterns (e.g., sottoc.com, example.org)
    /(?:^|\s)([a-zA-Z0-9][-a-zA-Z0-9]*\.(?:com|org|net|io|co|gov|edu|info|biz|us|uk|ca|au|de|fr|jp|cn|in|br|ru|mx|es|it|nl|se|no|fi|dk|pl|cz|at|ch|be|pt|gr|ie|nz|sg|hk|kr|tw|th|my|ph|id|vn|za|ae|sa|eg|ng|ke|gh|tz|ug|rw|et|ma|dz|tn|ly|sd|tr|il|pk|bd|lk|np|mm|kh|la|bn)(?:\/[^\s<>"{}|\\^`[\]]*)?)/gi
  ];
  
  for (const pattern of urlPatterns) {
    const matches = text.match(pattern);
    if (matches && matches.length > 0) {
      let url = matches[0].trim();
      // Ensure URL has protocol
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        url = 'https://' + url;
      }
      return url;
    }
  }
  return null;
};

/**
 * Infers the user's intent from their message text.
 * This is invisible to the user - no mode selection UI.
 */
const inferIntent = (userText) => {
  const text = userText.toLowerCase();

  // Scam/fraud REPORT signals — user is describing something that happened to them.
  // Must take priority so a .com company name doesn't hijack routing.
  const fraudReport = /scam|fraud|phish|suspicious|fake|impersonat|hack|identity theft|stole|stolen|charged without|unauthorized charge|threatening|got robbed|been tricked|mislead|deceived/i.test(text);

  // Broader scam phrasing seniors actually use — often no explicit "scam" word
  const scamPhrasing = /\b(they said my (account|computer|password|card|number|bank)|told me (to pay|to call|to download|to verify|i owe)|asked me (to move|for my|to buy)|my (account|computer) (is locked|has a virus|was hacked)|gift card|wire transfer|i got a (call|text|email) (about|from|saying)|someone (called|texted|emailed) (me|saying)|they wanted (me to|my)|i think (i was|someone)|they (claimed|pretended|pretended to be))\b/i.test(text);

  // Explicit URL (http/https/www) — user pasted an actual link
  const hasExplicitUrl = /https?:\/\/|www\./i.test(text);

  // Website CHECK intent — user is asking HAVN to evaluate a SPECIFIC URL/site
  // Must have an actual domain pattern (.com/.org/etc) OR explicit check keywords with a site name
  const hasDomainPattern = /\.(com|org|net|io|co|gov|edu|uk|de|fr|jp|cn|au|ca|in)\b/i.test(text);
  const websiteCheckIntent = hasDomainPattern && /\b(check|look up|is this safe|can i trust|is .{0,30} legit|is .{0,30} legitimate|is .{0,30} real|safe to (use|buy|order))\b/i.test(text);

  const phoneKeywords = /called me|phone call|voicemail|rang me|caller|dialed|on the phone|someone called|got a call|received a call/i.test(text);
  const emailTextKeywords = /email|text message|sms|got a text|received a text|whatsapp|messenger|texted me|suspicious text|suspicious email/i.test(text);
  const paymentKeywords = /paid|payment|subscribe|subscription|card|checkout|charged|billing|invoice|credit card|debit|transaction|purchase|buy|bought/i.test(text);

  // Priority order:
  // 1. Explicit URL pasted → always evaluate it
  if (hasExplicitUrl) return INTENTS.WEBSITE_RESEARCH;

  // 2. Fraud report OR scam phrasing → route to correct fraud intent regardless of .com mentions
  if (fraudReport || scamPhrasing) {
    if (phoneKeywords)      return INTENTS.PHONE_CALL_INCIDENT;
    if (emailTextKeywords)  return INTENTS.EMAIL_OR_TEXT_SCAM;
    if (paymentKeywords)    return INTENTS.PAYMENT_OR_SUBSCRIPTION;
    return INTENTS.GENERAL_HELP;
  }

  // 3. User explicitly asking HAVN to check a specific site (has domain pattern)
  if (websiteCheckIntent) return INTENTS.WEBSITE_RESEARCH;

  // 4. Standard intents
  if (phoneKeywords)     return INTENTS.PHONE_CALL_INCIDENT;
  if (emailTextKeywords) return INTENTS.EMAIL_OR_TEXT_SCAM;
  if (paymentKeywords)   return INTENTS.PAYMENT_OR_SUBSCRIPTION;

  return INTENTS.GENERAL_HELP;
};

// ============================================
// RISK RESEARCH CARD COMPONENT
// Renders structured website risk analysis
// ============================================

const RiskResearchCard = ({ data, onFollowUp }) => {
  if (!data) return null;
  
  const getRiskBadgeStyle = (level) => {
    const styles = {
      SAFE: { bg: 'rgba(45, 165, 99, 0.15)', border: 'rgba(45, 165, 99, 0.4)', color: '#2DA563', label: 'Low Risk' },
      CAUTION_SOFT: { bg: 'rgba(251, 191, 36, 0.15)', border: 'rgba(251, 191, 36, 0.4)', color: '#fbbf24', label: 'Caution' },
      CAUTION: { bg: 'rgba(251, 146, 60, 0.15)', border: 'rgba(251, 146, 60, 0.4)', color: '#fb923c', label: 'Caution' },
      CAUTION_HARD: { bg: 'rgba(251, 146, 60, 0.15)', border: 'rgba(251, 146, 60, 0.4)', color: '#fb923c', label: 'High Caution' },
      DANGER: { bg: 'rgba(239, 68, 68, 0.15)', border: 'rgba(239, 68, 68, 0.4)', color: '#ef4444', label: 'High Risk' },
      UNKNOWN: { bg: 'rgba(156, 163, 175, 0.15)', border: 'rgba(156, 163, 175, 0.4)', color: '#8BAEC8', label: 'Unknown' }
    };
    return styles[level] || styles.UNKNOWN;
  };
  
  const badgeStyle = getRiskBadgeStyle(data.risk_level);
  
  return (
    <motion.div 
      className="risk-research-card"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      data-testid="risk-research-card"
    >
      {/* Risk Badge */}
      <div className="risk-badge-row">
        <div 
          className="risk-badge"
          style={{ 
            background: badgeStyle.bg, 
            borderColor: badgeStyle.border,
            color: badgeStyle.color 
          }}
          data-testid="risk-badge"
        >
          <span className="risk-badge-dot" style={{ background: badgeStyle.color }} />
          <span className="risk-badge-label">{badgeStyle.label}</span>
          <span className="risk-badge-score">Score: {data.score != null ? 100 - data.score : '—'}/100</span>
        </div>
      </div>
      
      {/* Reasons List */}
      {data.reasons && data.reasons.length > 0 && (
        <div className="risk-reasons" data-testid="risk-reasons">
          <h4 className="risk-reasons-title">What I found:</h4>
          <ul className="risk-reasons-list">
            {data.reasons.slice(0, 3).map((reason, idx) => (
              <li key={idx} className="risk-reason-item">
                <span className="risk-reason-bullet" style={{ color: badgeStyle.color }}>•</span>
                {toSeniorFriendly(reason)}
              </li>
            ))}
          </ul>
        </div>
      )}
      
      {/* Recommendation */}
      {data.recommendation && (
        <div className="risk-recommendation" data-testid="risk-recommendation">
          <h4 className="risk-recommendation-title">My recommendation:</h4>
          <p className="risk-recommendation-text">{toSeniorFriendly(data.recommendation)}</p>
        </div>
      )}
      
      {/* What People Report - v3.2 Enhancement */}
      {data.what_people_report && (
        <div className="risk-people-report" data-testid="risk-people-report">
          <h4 className="risk-people-report-title">What others say:</h4>
          <p className="risk-people-report-text">{toSeniorFriendly(data.what_people_report)}</p>
        </div>
      )}
      
      {/* Follow-up Question */}
      {data.follow_up_question && (
        <div className="risk-followup" data-testid="risk-followup">
          <p className="risk-followup-text">{data.follow_up_question}</p>
          {onFollowUp && (
            <button 
              className="risk-followup-btn"
              onClick={() => onFollowUp(data.follow_up_question)}
              data-testid="risk-followup-btn"
            >
              Tell me more
            </button>
          )}
        </div>
      )}
    </motion.div>
  );
};

// ============================================
// WEBSITE SIDE PANEL COMPONENT
// Senior-friendly, non-blocking right panel
// ============================================

// ─── Complaint theme → plain English map ─────────────────────────────────────
const COMPLAINT_LABELS = {
  shipping_delays:            { label: "Slow or lost shipping",          icon: "📦" },
  refund_difficulty:          { label: "Refunds refused or delayed",     icon: "💸" },
  subscription_cancellation:  { label: "Hard to cancel — charges continue", icon: "🔁" },
  billing_disputes:           { label: "Unexpected charges reported",    icon: "💳" },
  support_unresponsive:       { label: "Customer service hard to reach", icon: "📵" },
  product_quality_issues:     { label: "Product quality concerns",       icon: "⚠️" },
  damaged_items:              { label: "Items arrive damaged",           icon: "📦" },
  misleading_marketing:       { label: "Advertised price not real price",icon: "🏷️" },
  return_fees:                { label: "High restocking or return fees", icon: "↩️" },
  tech_support_scam:          { label: "Fake tech support — do not call",icon: "🚫" },
  phishing:                   { label: "Pretending to be a real company",icon: "🎣" },
  cancellation:               { label: "Hard to cancel subscription",    icon: "🔁" },
  refunds:                    { label: "Refunds delayed or denied",      icon: "💸" },
  billing:                    { label: "Unexpected billing reported",    icon: "💳" },
  shipping:                   { label: "Shipping delays reported",       icon: "📦" },
  support:                    { label: "Poor customer service",          icon: "📵" },
};

// Pre-proceed checklist items derived from complaint themes
const PRECHECKLIST = {
  subscription_cancellation:  "Can you find a clear cancellation option?",
  cancellation:               "Can you find a clear cancellation option?",
  billing_disputes:           "Read the payment terms before entering your card",
  billing:                    "Read the payment terms before entering your card",
  refund_difficulty:          "Check the return and refund policy page",
  refunds:                    "Check the return and refund policy page",
  support_unresponsive:       "Look for a real phone number or live chat",
  support:                    "Look for a real phone number or live chat",
  misleading_marketing:       "Confirm the final checkout price before paying",
  shipping_delays:            "Check estimated delivery times before ordering",
  shipping:                   "Check estimated delivery times before ordering",
  return_fees:                "Check if returns are free or have restocking fees",
  tech_support_scam:          "Do not call any number shown on this page",
  phishing:                   "Check the full URL — look for misspellings",
};

// Industry → readable description
const INDUSTRY_LABELS = {
  MARKETPLACE_ECOM:   "Online marketplace",
  ECOMMERCE:          "Online store",
  HEALTH_SUPPLEMENTS: "Health & supplements",
  FINANCIAL_SERVICES: "Financial services",
  TECH_SERVICES:      "Tech services",
  TRAVEL:             "Travel & bookings",
  SUBSCRIPTION_BOX:   "Subscription service",
  MEDIA_STREAMING:    "Streaming service",
  INSURANCE:          "Insurance",
  EDUCATION:          "Education",
  REAL_ESTATE:        "Real estate",
  GAMING:             "Gaming",
  SOCIAL_MEDIA:       "Social platform",
  GOVERNMENT:         "Government / official",
  HEALTHCARE:         "Healthcare",
  BANKING:            "Banking & finance",
};


// Debug Overlay Component
const GreetingDebugOverlay = ({ debugState }) => {
  return (
    <div style={{
      position: 'fixed',
      top: '10px',
      left: '10px',
      background: 'rgba(0, 0, 0, 0.85)',
      color: '#fff',
      padding: '12px 16px',
      borderRadius: '8px',
      fontSize: '11px',
      fontFamily: 'monospace',
      zIndex: 99999,
      maxWidth: '320px',
      lineHeight: 1.5,
      border: '1px solid rgba(255,255,255,0.2)'
    }} data-testid="greeting-debug-overlay">
      <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#5DB8F5' }}>
        🔍 Greeting Debug
      </div>
      <div>Route: <span style={{ color: '#93C5FD' }}>{debugState.pathname}</span></div>
      <div>forceGreet: <span style={{ color: debugState.forceGreet ? '#4ADE80' : '#F87171' }}>{String(debugState.forceGreet)}</span></div>
      <div>sessionStorage.greeted: <span style={{ color: '#FBBF24' }}>{debugState.sessionGreeted || 'null'}</span></div>
      <div>shouldShowGreeting: <span style={{ color: debugState.shouldShowGreeting ? '#4ADE80' : '#F87171' }}>{String(debugState.shouldShowGreeting)}</span></div>
      <div>greetingRendered: <span style={{ color: debugState.greetingRendered ? '#4ADE80' : '#F87171' }}>{String(debugState.greetingRendered)}</span></div>
      <div>userInteracted: <span style={{ color: debugState.userInteracted ? '#4ADE80' : '#F87171' }}>{String(debugState.userInteracted)}</span></div>
      <div>ttsAttempted: <span style={{ color: debugState.ttsAttempted ? '#4ADE80' : '#F87171' }}>{String(debugState.ttsAttempted)}</span></div>
      <div>ttsStatus: <span style={{ color: '#C084FC' }}>{debugState.ttsStatus}</span></div>
      <div>volume: <span style={{ color: '#93C5FD' }}>{debugState.volume}</span></div>
      <div>isMuted: <span style={{ color: debugState.isMuted ? '#F87171' : '#4ADE80' }}>{String(debugState.isMuted)}</span></div>
    </div>
  );
};

// Extract links from markdown text
const extractLinks = (text) => {
  const linkPattern = /\[([^\]]+)\]\(([^)]+)\)/g;
  const links = [];
  let match;
  
  while ((match = linkPattern.exec(text)) !== null) {
    links.push({
      title: match[1],
      url: match[2]
    });
  }
  
  // Also extract plain URLs
  const urlPattern = /(https?:\/\/[^\s]+)/g;
  const plainText = text.replace(linkPattern, '');
  while ((match = urlPattern.exec(plainText)) !== null) {
    // Extract domain for title
    try {
      const url = new URL(match[1]);
      links.push({
        title: url.hostname.replace('www.', ''),
        url: match[1]
      });
    } catch {
      links.push({
        title: 'Link',
        url: match[1]
      });
    }
  }
  
  return links;
};

// Link Card Component
const LinkCard = ({ links, onDismiss }) => {
  if (!links || links.length === 0) return null;
  
  const primaryLink = links[0];
  const secondaryLinks = links.slice(1, 3);
  
  // Generate a friendly title from URL
  const getFriendlyTitle = (url, fallbackTitle) => {
    try {
      const urlObj = new URL(url);
      const domain = urlObj.hostname.replace('www.', '');
      // Capitalize first letter of domain
      return fallbackTitle || domain.charAt(0).toUpperCase() + domain.slice(1).split('.')[0];
    } catch {
      return fallbackTitle || 'Link';
    }
  };

  return (
    <motion.div
      className="link-card-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onDismiss}
      data-testid="link-card-overlay"
    >
      <motion.div
        className="link-card"
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
        onClick={(e) => e.stopPropagation()}
        data-testid="link-card"
      >
        {/* Icon */}
        <div className="link-card-icon">
          <ExternalLink />
        </div>
        
        {/* Title */}
        <h3 className="link-card-title">
          {getFriendlyTitle(primaryLink.url, primaryLink.title)}
        </h3>
        
        {/* Description */}
        <p className="link-card-description">
          I found a helpful link for you. Tap below to open it.
        </p>
        
        {/* Primary Button */}
        <a
          href={primaryLink.url}
          target="_blank"
          rel="noopener noreferrer"
          className="link-card-primary-btn"
          data-testid="link-card-primary"
        >
          Open {primaryLink.title || 'Link'}
        </a>
        
        {/* Secondary Links */}
        {secondaryLinks.map((link, idx) => (
          <a
            key={idx}
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            className="link-card-secondary-btn"
            data-testid={`link-card-secondary-${idx}`}
          >
            {link.title || 'Alternative Link'}
          </a>
        ))}
        
        {/* Dismiss */}
        <button
          onClick={onDismiss}
          className="link-card-dismiss"
          data-testid="link-card-dismiss"
        >
          Dismiss
        </button>
      </motion.div>
    </motion.div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// Extension Install Modal — chooser → waitlist or beta tester flow
// ═══════════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════════
// Complete Your Profile Modal — shown to existing users missing fields
// ═══════════════════════════════════════════════════════════════════
const AGE_RANGES = ['Under 18', '18-29', '30-39', '40-49', '50-59', '60-69', '70-79', '80+'];
const REFERRAL_OPTIONS = ['Family member', 'Friend', 'My bank', 'Caregiver', 'Social media', 'News article', 'Other'];

function ProfileCompleteModal({ onClose, onComplete }) {
  const { user: authUser } = useAuth();
  const existingMeta = authUser?.user_metadata || {};
  const existingName = existingMeta.full_name || '';
  const [firstName, setFirstName] = useState(existingMeta.first_name || existingName.split(' ')[0] || '');
  const [lastName, setLastName] = useState(existingMeta.last_name || existingName.split(' ').slice(1).join(' ') || '');
  const [phone, setPhone] = useState(existingMeta.phone || '');
  const [ageRange, setAgeRange] = useState(existingMeta.age_range || '');
  const [referral, setReferral] = useState(existingMeta.referral_source || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const inputStyle = {
    width: '100%', padding: '12px 14px', fontSize: '15px',
    background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: '10px', color: '#fff', outline: 'none', boxSizing: 'border-box',
  };
  const selectStyle = {
    ...inputStyle, appearance: 'none',
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='rgba(255,255,255,0.4)' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat', backgroundPosition: 'right 14px center', paddingRight: '36px',
  };
  const labelStyle = { display: 'block', fontSize: '12px', fontWeight: 600, color: 'rgba(255,255,255,0.5)', marginBottom: '4px' };

  const formatPhone = (v) => {
    const d = v.replace(/\D/g, '').slice(0, 10);
    if (d.length <= 3) return d;
    if (d.length <= 6) return `(${d.slice(0,3)}) ${d.slice(3)}`;
    return `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6)}`;
  };

  const handleSave = async () => {
    if (!firstName.trim() || !lastName.trim()) { setError('Name is required'); return; }
    if (!ageRange) { setError('Age range is required'); return; }
    setSaving(true);
    try {
      const { supabase } = await import('@/lib/supabase');
      const phoneDigits = phone.replace(/\D/g, '');
      const fullPhone = phoneDigits.length >= 10 ? `+1${phoneDigits}` : '';
      await supabase.auth.updateUser({
        data: {
          full_name: `${firstName.trim()} ${lastName.trim()}`,
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          phone: fullPhone,
          age_range: ageRange,
          referral_source: referral,
        }
      });
      localStorage.removeItem('havn_profile_dismissed');
      localStorage.removeItem('havn_profile_dismiss_count');
      if (onComplete) onComplete();
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to save');
      setSaving(false);
    }
  };

  const handleSkip = () => {
    const count = parseInt(localStorage.getItem('havn_profile_dismiss_count') || '0', 10);
    localStorage.setItem('havn_profile_dismissed', String(Date.now()));
    localStorage.setItem('havn_profile_dismiss_count', String(count + 1));
    onClose();
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
      <motion.div initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }}
        onClick={e => e.stopPropagation()}
        style={{ background: 'var(--havn-surface)', borderRadius: '20px', border: '1px solid var(--havn-border-subtle, rgba(100,160,255,0.12))', padding: '28px', maxWidth: '420px', width: '100%', boxShadow: '0 24px 80px rgba(0,0,0,0.5)', maxHeight: '85vh', overflowY: 'auto' }}>

        <h2 style={{ fontSize: '19px', fontWeight: 700, color: 'var(--havn-text)', margin: '0 0 6px', textAlign: 'center' }}>
          Complete Your Profile
        </h2>
        <p style={{ fontSize: '13px', color: 'var(--havn-text-muted, rgba(232,244,255,0.5))', textAlign: 'center', margin: '0 0 20px' }}>
          Help us personalize your experience
        </p>

        {error && <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '8px', padding: '8px 12px', marginBottom: '12px', fontSize: '12px', color: '#F87171' }}>{error}</div>}

        <div style={{ display: 'flex', gap: '10px', marginBottom: '12px' }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>First Name *</label>
            <input type="text" value={firstName} onChange={e => setFirstName(e.target.value)} placeholder="Margaret" style={inputStyle} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Last Name *</label>
            <input type="text" value={lastName} onChange={e => setLastName(e.target.value)} placeholder="Smith" style={inputStyle} />
          </div>
        </div>

        <div style={{ marginBottom: '12px' }}>
          <label style={labelStyle}>Phone <span style={{ fontWeight: 400, color: 'rgba(255,255,255,0.3)' }}>(optional)</span></label>
          <div style={{ display: 'flex', gap: '8px' }}>
            <div style={{ ...inputStyle, width: '52px', flexShrink: 0, textAlign: 'center', padding: '12px 6px', color: 'rgba(255,255,255,0.5)' }}>+1</div>
            <input type="tel" value={phone} onChange={e => setPhone(formatPhone(e.target.value))} placeholder="(555) 123-4567" style={inputStyle} />
          </div>
          <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.25)', marginTop: '3px' }}>For safety alerts only</div>
        </div>

        <div style={{ marginBottom: '12px' }}>
          <label style={labelStyle}>Age Range *</label>
          <select value={ageRange} onChange={e => setAgeRange(e.target.value)}
            style={{ ...selectStyle, color: ageRange ? '#fff' : 'rgba(255,255,255,0.35)' }}>
            <option value="" disabled>Select</option>
            {AGE_RANGES.map(r => <option key={r} value={r} style={{ background: 'var(--havn-surface)', color: '#fff' }}>{r}</option>)}
          </select>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={labelStyle}>How did you hear about us? <span style={{ fontWeight: 400, color: 'rgba(255,255,255,0.3)' }}>(optional)</span></label>
          <select value={referral} onChange={e => setReferral(e.target.value)}
            style={{ ...selectStyle, color: referral ? '#fff' : 'rgba(255,255,255,0.35)' }}>
            <option value="">Select</option>
            {REFERRAL_OPTIONS.map(r => <option key={r} value={r} style={{ background: 'var(--havn-surface)', color: '#fff' }}>{r}</option>)}
          </select>
        </div>

        <button onClick={handleSave} disabled={saving}
          style={{ width: '100%', padding: '13px', fontSize: '15px', fontWeight: 600, background: saving ? 'var(--havn-primary-20)' : 'var(--havn-primary)', color: '#000', border: 'none', borderRadius: '10px', cursor: saving ? 'not-allowed' : 'pointer', marginBottom: '10px' }}>
          {saving ? 'Saving...' : 'Save'}
        </button>
        <button onClick={handleSkip}
          style={{ width: '100%', padding: '10px', fontSize: '13px', fontWeight: 500, background: 'none', border: 'none', color: 'var(--havn-text-muted, rgba(232,244,255,0.4))', cursor: 'pointer' }}>
          Skip for now
        </button>
      </motion.div>
    </motion.div>
  );
}


function ExtensionModal({ onClose }) {
  const [view, setView] = useState('chooser'); // 'chooser' | 'waitlist' | 'beta'
  const [waitlistEmail, setWaitlistEmail] = useState('');
  const [waitlistStatus, setWaitlistStatus] = useState(null); // null | 'sending' | 'done' | 'error'
  const [waitlistError, setWaitlistError] = useState('');

  const handleWaitlistSubmit = async (e) => {
    e.preventDefault();
    if (!waitlistEmail || !waitlistEmail.includes('@')) return;
    setWaitlistStatus('sending');
    try {
      await axios.post(`${API}/extension/waitlist`, { email: waitlistEmail });
      setWaitlistStatus('done');
    } catch (err) {
      setWaitlistError(err.response?.data?.detail || 'Something went wrong');
      setWaitlistStatus('error');
    }
  };

  const modalBg = {
    position: 'fixed', inset: 0, zIndex: 9999,
    background: 'rgba(0, 0, 0, 0.6)',
    backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '20px',
  };

  const cardStyle = {
    background: 'var(--havn-surface)',
    borderRadius: '20px',
    border: '1px solid var(--havn-border-subtle, var(--havn-border-subtle, rgba(100,160,255,0.12)))',
    padding: '32px',
    maxWidth: '480px',
    width: '100%',
    position: 'relative',
    boxShadow: '0 24px 80px rgba(0, 0, 0, 0.5)',
  };

  const closeBtnStyle = {
    position: 'absolute', top: '16px', right: '16px',
    background: 'rgba(255,255,255,0.06)', border: 'none',
    borderRadius: '50%', width: '32px', height: '32px',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer', transition: 'background 0.2s',
  };

  const optionBtnStyle = (accent) => ({
    width: '100%', padding: '18px 20px',
    background: `rgba(${accent}, 0.06)`,
    border: `1px solid rgba(${accent}, 0.2)`,
    borderRadius: '14px', cursor: 'pointer',
    textAlign: 'left', transition: 'all 0.2s',
  });

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }} onClick={onClose} style={modalBg}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        transition={{ duration: 0.25, ease: 'easeOut' }}
        onClick={e => e.stopPropagation()}
        style={cardStyle}
      >
        <button onClick={onClose} style={closeBtnStyle}
          onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.12)'}
          onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.06)'}>
          <X size={16} style={{ color: 'rgba(255,255,255,0.5)' }} />
        </button>

        {/* ── Chooser view ─────────────────────────── */}
        {view === 'chooser' && (
          <>
            <div style={{ textAlign: 'center', marginBottom: '28px', paddingRight: '32px' }}>
              <Shield size={36} style={{ color: 'var(--havn-primary)', marginBottom: '12px' }} />
              <h2 style={{ fontFamily: "'Inter', sans-serif", fontSize: '20px', fontWeight: 700, color: 'var(--havn-text)', margin: '0 0 8px' }}>
                Get the HAVN Extension
              </h2>
              <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '14px', color: 'var(--havn-text-muted, rgba(232,244,255,0.5))', margin: 0 }}>
                Real-time protection while you browse
              </p>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <button onClick={() => setView('waitlist')} style={optionBtnStyle('63,175,209')}
                onMouseEnter={e => { e.currentTarget.style.background = 'var(--havn-primary-10)'; e.currentTarget.style.borderColor = 'var(--havn-primary-20)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'var(--havn-primary-10)'; e.currentTarget.style.borderColor = 'var(--havn-primary-10)'; }}>
                <div style={{ fontFamily: "'Inter', sans-serif", fontSize: '16px', fontWeight: 600, color: 'var(--havn-text)', marginBottom: '4px' }}>
                  Join the Waitlist
                </div>
                <div style={{ fontFamily: "'Inter', sans-serif", fontSize: '13px', color: 'var(--havn-text-muted, rgba(232,244,255,0.5))' }}>
                  Get notified when it's available on the Chrome Web Store
                </div>
              </button>

              <button onClick={() => setView('beta')} style={optionBtnStyle('251,191,36')}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(251,191,36,0.12)'; e.currentTarget.style.borderColor = 'rgba(251,191,36,0.4)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(251,191,36,0.06)'; e.currentTarget.style.borderColor = 'rgba(251,191,36,0.2)'; }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                  <span style={{ fontFamily: "'Inter', sans-serif", fontSize: '16px', fontWeight: 600, color: 'var(--havn-text)' }}>
                    I'm a Beta Tester
                  </span>
                  <span style={{ fontSize: '10px', fontWeight: 700, color: '#FBBF24', background: 'rgba(251,191,36,0.15)', padding: '2px 8px', borderRadius: '6px', letterSpacing: '0.5px' }}>BETA</span>
                </div>
                <div style={{ fontFamily: "'Inter', sans-serif", fontSize: '13px', color: 'var(--havn-text-muted, rgba(232,244,255,0.5))' }}>
                  Install now with step-by-step instructions
                </div>
              </button>
            </div>
          </>
        )}

        {/* ── Waitlist view ────────────────────────── */}
        {view === 'waitlist' && (
          <>
            <button onClick={() => setView('chooser')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--havn-text-muted, rgba(232,244,255,0.5))', fontSize: '13px', fontFamily: "'Inter', sans-serif", padding: 0, marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <ChevronRight size={14} style={{ transform: 'rotate(180deg)' }} /> Back
            </button>

            <h2 style={{ fontFamily: "'Inter', sans-serif", fontSize: '18px', fontWeight: 600, color: 'var(--havn-text)', margin: '0 0 8px', paddingRight: '40px' }}>
              Join the Waitlist
            </h2>
            <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '13px', color: 'var(--havn-text-muted, rgba(232,244,255,0.5))', margin: '0 0 20px' }}>
              We'll email you as soon as HAVN is on the Chrome Web Store.
            </p>

            {waitlistStatus === 'done' ? (
              <div style={{ background: 'rgba(45,165,99,0.1)', border: '1px solid rgba(45,165,99,0.3)', borderRadius: '12px', padding: '20px', textAlign: 'center' }}>
                <CheckCircle size={32} style={{ color: '#4ADE80', marginBottom: '8px' }} />
                <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '15px', fontWeight: 600, color: '#4ADE80', margin: 0 }}>You're on the list!</p>
                <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '13px', color: 'var(--havn-text-muted, rgba(232,244,255,0.5))', margin: '6px 0 0' }}>We'll let you know when it's ready.</p>
              </div>
            ) : (
              <form onSubmit={handleWaitlistSubmit} style={{ display: 'flex', gap: '10px' }}>
                <input
                  type="email" value={waitlistEmail} onChange={e => setWaitlistEmail(e.target.value)}
                  placeholder="your@email.com" required
                  style={{
                    flex: 1, padding: '12px 14px', fontSize: '15px',
                    background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)',
                    borderRadius: '10px', color: '#fff', outline: 'none',
                  }}
                />
                <button type="submit" disabled={waitlistStatus === 'sending'}
                  style={{
                    padding: '12px 20px', fontSize: '14px', fontWeight: 600,
                    background: waitlistStatus === 'sending' ? 'var(--havn-primary-20)' : 'var(--havn-primary)',
                    color: '#000', border: 'none', borderRadius: '10px',
                    cursor: waitlistStatus === 'sending' ? 'not-allowed' : 'pointer',
                    whiteSpace: 'nowrap',
                  }}>
                  {waitlistStatus === 'sending' ? 'Joining...' : 'Join'}
                </button>
              </form>
            )}
            {waitlistStatus === 'error' && (
              <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '12px', color: '#F87171', marginTop: '8px' }}>{waitlistError}</p>
            )}
          </>
        )}

        {/* ── Beta tester view ─────────────────────── */}
        {view === 'beta' && (
          <>
            <button onClick={() => setView('chooser')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--havn-text-muted, rgba(232,244,255,0.5))', fontSize: '13px', fontFamily: "'Inter', sans-serif", padding: 0, marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <ChevronRight size={14} style={{ transform: 'rotate(180deg)' }} /> Back
            </button>

            <h2 style={{ fontFamily: "'Inter', sans-serif", fontSize: '18px', fontWeight: 600, color: 'var(--havn-text)', margin: '0 0 6px', paddingRight: '40px' }}>
              Install HAVN Extension
            </h2>
            <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '13px', color: 'var(--havn-text-muted, rgba(232,244,255,0.5))', margin: '0 0 20px' }}>
              Follow these steps to install the beta extension in Chrome.
              {extVersion ? <> Latest version: <strong>v{extVersion}</strong>.</> : null}
            </p>

            {/* Step 1: Download.
                Downloads from the LIVE /api/extension/download endpoint, which
                zips the current browser-extension folder on every request — so
                it is always the version that is actually deployed, never a
                stale static file. The ?v= query busts any browser/CDN cache
                when the version changes. */}
            <a href={`${API}/extension/download?v=${extVersion || 'latest'}`} download="havn-extension.zip"
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
                width: '100%', padding: '14px', marginBottom: '20px',
                background: 'var(--havn-primary)', color: '#000', border: 'none', borderRadius: '12px',
                fontSize: '15px', fontWeight: 600, textDecoration: 'none', cursor: 'pointer',
                transition: 'background 0.2s',
              }}
              onMouseEnter={e => e.currentTarget.style.background = '#4fc3e6'}
              onMouseLeave={e => e.currentTarget.style.background = 'var(--havn-primary)'}>
              <Download size={18} />
              Step 1: Download Extension{extVersion ? ` (v${extVersion})` : ''}
            </a>

            {/* Steps 2-5 */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {[
                { num: 2, icon: '\uD83D\uDDDC\uFE0F', title: 'Unzip the downloaded file', desc: 'Find "havn-extension.zip" in your Downloads folder and double-click it' },
                { num: 3, icon: '\uD83C\uDF10', title: 'Open Chrome Extensions', desc: 'Type chrome://extensions into your Chrome address bar and press Enter' },
                { num: 4, icon: '\uD83D\uDD27', title: 'Turn on Developer Mode', desc: 'Find the "Developer mode" toggle in the top-right corner and turn it ON' },
                { num: 5, icon: '\uD83D\uDCC2', title: 'Load the extension', desc: 'Click "Load unpacked", then select the "havn-extension" folder you unzipped' },
              ].map(step => (
                <div key={step.num} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                  <div style={{
                    width: '32px', height: '32px', borderRadius: '10px', flexShrink: 0,
                    background: 'var(--havn-primary-10)', border: '1px solid var(--havn-primary-10)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '16px',
                  }}>{step.icon}</div>
                  <div>
                    <div style={{ fontFamily: "'Inter', sans-serif", fontSize: '13px', fontWeight: 600, color: 'var(--havn-text)', marginBottom: '2px' }}>
                      Step {step.num}: {step.title}
                    </div>
                    <div style={{ fontFamily: "'Inter', sans-serif", fontSize: '12px', color: 'var(--havn-text-muted, rgba(232,244,255,0.5))', lineHeight: '1.4' }}>
                      {step.desc}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Success note */}
            <div style={{
              marginTop: '20px', padding: '14px 16px', borderRadius: '12px',
              background: 'rgba(45,165,99,0.06)', border: '1px solid rgba(45,165,99,0.15)',
              display: 'flex', alignItems: 'center', gap: '10px',
            }}>
              <CheckCircle size={20} style={{ color: '#4ADE80', flexShrink: 0 }} />
              <span style={{ fontFamily: "'Inter', sans-serif", fontSize: '13px', color: 'var(--havn-text-muted, rgba(232,244,255,0.7))' }}>
                That's it! The HAVN shield icon will appear in your Chrome toolbar.
              </span>
            </div>
          </>
        )}
      </motion.div>
    </motion.div>
  );
}


export default function HomePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user: authUser, signOut } = useAuth();
  const { t, lang, setLang, languages } = useTranslation();
  const { theme, themeName, setTheme } = useTheme();
  const {
    currentSessionId,
    recordInteraction,
    checkSessionEnd,
    startNewSession,
    sessionLogger,
    conversationStorage,
    caregiverStorage
  } = useSession();
  
  // Force greeting mode: ?greet=1
  const forceGreet = searchParams.get('greet') === '1';
  
  const [state, setState] = useState(STATES.IDLE);
  const { amplitude: waveAmplitude, attachAudioElement, detach: detachAudio } = useAudioAmplitude();
  // Map HomePage state values to HavnTalkWave state prop.
  // HAVN's state machine resets to IDLE before TTS starts — the voice
  // is fire-and-forget with no SPEAKING state during playback. We detect
  // speaking from the live amplitude value instead: when the
  // useAudioAmplitude hook reports amplitude > 0.08 (above idle floor
  // of 0.02, below typical speech at 0.2-0.7), the wave enters speaking
  // mode with full amplitude reactivity.
  const waveState =
    state === 'listening' ? 'listening' :
    state === 'processing' ? 'thinking' :
    waveAmplitude > 0.08 ? 'speaking' :
    'idle';
  const [messages, setMessages] = useState([]);
  const [showTranscript, setShowTranscript] = useState(false);
  const [showKeyboard, setShowKeyboard] = useState(false);
  const [showVolume, setShowVolume] = useState(false);
  const [volume, setVolume] = useState(1);
  const [keyboardInput, setKeyboardInput] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [linkCardData, setLinkCardData] = useState(null);
  const [riskResearchData, setRiskResearchData] = useState(null);  // Website research results
  const [sidePanelData, setSidePanelData] = useState(null);  // New side panel data
  const [isWebsiteSidePanelOpen, setIsWebsiteSidePanelOpen] = useState(false);  // v3.9.6: Decoupled panel visibility
  const [isVoicePlaying, setIsVoicePlaying] = useState(false);  // Voice playback state
  const [isSidePanelMuted, setIsSidePanelMuted] = useState(false);  // Default false, loaded via useEffect
  const [isFetchingWebsitePanel, setIsFetchingWebsitePanel] = useState(false);  // v3.9.7: Loading state for fetch
  const [sidePanelError, setSidePanelError] = useState(null);  // v3.9.7: Error state for fetch failures
  const [isDeepScanning, setIsDeepScanning] = useState(false);  // Phase 3: Tier 2 deep scan in progress
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);  // Phase 3B: Lazy full report generation
  const [showExtensionModal, setShowExtensionModal] = useState(false);  // Extension install instructions modal
  const [extVersion, setExtVersion] = useState(null);  // Latest extension version (from backend manifest)
  const [showProfileModal, setShowProfileModal] = useState(false);  // Complete-your-profile prompt
  const sidePanelAudioRef = useRef(null);  // Audio URL ref for side panel TTS
  const sidePanelAudioElementRef = useRef(null);  // Dedicated audio element for side panel
  const sidePanelTtsRequestIdRef = useRef(0);  // v3.9.7: TTS concurrency guard
  // Patience voice refs — reassures user every 15s during deep scan
  const patienceTimerRef = useRef(null);
  const patienceCountRef = useRef(0);

  // Refs to avoid stale closures in WebSocket callbacks
  const isSidePanelMutedRef = useRef(isSidePanelMuted);
  const volumeRef = useRef(volume);
  const playSidePanelTTSRef = useRef(null); // set after playSidePanelTTS is defined



  // Load mute preference from localStorage on mount (not at render time)
  useEffect(() => {
    try {
      const stored = localStorage.getItem('havn_side_panel_muted');
      if (stored === 'true') {
        setIsSidePanelMuted(true);
      }
    } catch (err) {
      // localStorage unavailable - use default
    }
  }, []);
  
  // v3.5: Website Check Mode state
  const [showWebsiteCheckPanel, setShowWebsiteCheckPanel] = useState(false);
  const [websiteCheckInput, setWebsiteCheckInput] = useState('');
  const [websiteCheckConfirm, setWebsiteCheckConfirm] = useState(null); // { hostname: string, needsConfirm: boolean }
  const [isCheckingWebsite, setIsCheckingWebsite] = useState(false);
  const [lastCheckedHostname, setLastCheckedHostname] = useState(null); // v3.9.6: Track last checked hostname
  const [isInWebsiteResearchFlow, setIsInWebsiteResearchFlow] = useState(false); // Track if user asked about a website via voice
  
  // v3.6: Session & Transcript state
  const [activeSession, setActiveSession] = useState(null);
  const [liveTranscript, setLiveTranscript] = useState([]);
  const [sessionHistory, setSessionHistory] = useState([]);
  const [showHistoryPanel, setShowHistoryPanel] = useState(false);
  const [showTranscriptPanel, setShowTranscriptPanel] = useState(false);
  const [selectedHistorySession, setSelectedHistorySession] = useState(null);
  const [isRecallingSession, setIsRecallingSession] = useState(false);
  const [pendingRecallQuestion, setPendingRecallQuestion] = useState(null);
  const transcriptEndRef = useRef(null);
  
  // FIX 2: History transcript viewer state
  const [historyTranscriptView, setHistoryTranscriptView] = useState(null); // { session, events }
  const [isLoadingHistoryTranscript, setIsLoadingHistoryTranscript] = useState(false);
  
  // FIX 3: Session ended visual flag
  const [sessionJustEnded, setSessionJustEnded] = useState(false);

  // Bug 4: Inactivity auto-end. Two-stage timer:
  //   1. 30s with no messages + no voice playing → nudge ("Continue?")
  //   2. 15s more with no response → farewell + end session.
  // `waitingForContinue` drives the quick-reply buttons in the transcript UI.
  const [waitingForContinue, setWaitingForContinue] = useState(false);
  const waitingForContinueRef = useRef(false);

  // Bug 5: Session delete confirmation state.
  const [confirmDeleteSessionId, setConfirmDeleteSessionId] = useState(null);
  const [confirmClearAll, setConfirmClearAll] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  // Language handoff state for the graceful EN/FR switch flow. When set,
  // the orb's status label displays the overlay string and the talk button
  // is blocked via sessionJustEnded. Cleared when the re-intro finishes.
  const [langHandoffOverlay, setLangHandoffOverlay] = useState(null);
  const langHandoffInFlightRef = useRef(false);
  
  // v3.7: Contacts Panel state
  const [showContactsPanel, setShowContactsPanel] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [isLoadingContacts, setIsLoadingContacts] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [newContactForm, setNewContactForm] = useState({
    display_name: '',
    phone: '',
    email: '',
    relationship: '',
    is_caregiver: false,
    is_primary_caregiver: false,
    notes: ''
  });
  const [editForm, setEditForm] = useState({
    display_name: '',
    phone: '',
    email: '',
    relationship: '',
    is_caregiver: false,
    is_primary_caregiver: false,
    notes: ''
  });
  const [showAddContactForm, setShowAddContactForm] = useState(false);
  const [contactError, setContactError] = useState('');
  const [primaryCaregiver, setPrimaryCaregiver] = useState(null);
  const [callModalContact, setCallModalContact] = useState(null); // contact object | null
  
  // Caregiver portal integration
  const [accessCode, setAccessCode] = useState(null);          // user's own access code
  const [pendingReminders, setPendingReminders] = useState([]); // reminders due now
  const [siteListCache, setSiteListCache] = useState({});       // domain → {status, note, caregiver_name}
  const [reminderDismissed, setReminderDismissed] = useState(false); // hide after ack
  
  // v4.5.24: Trusted Sites & Community Flagging state
  const [savedFavorites, setSavedFavorites] = useState(new Set()); // Set of saved domains
  const [flaggedDomains, setFlaggedDomains] = useState(new Set()); // Domains flagged this session
  const [panelActionState, setPanelActionState] = useState({
    saving: false, saved: false,
    flagging: false, flagged: false,
    askingCaregiver: false, askedCaregiver: false,
    notifyingCaregiver: false, notifiedCaregiver: false,
  });
  
  // v3.2: Voice-first mode state
  const [voiceFirstMode, setVoiceFirstMode] = useState(true);  // Default: voice-first
  const [currentSpokenText, setCurrentSpokenText] = useState('');  // What's being spoken
  const [lastResponse, setLastResponse] = useState(null);  // Full response for transcript
  
  // Greeting state
  const [greetingRendered, setGreetingRendered] = useState(false);
  const [shouldShowGreeting, setShouldShowGreeting] = useState(false);
  const [userInteracted, setUserInteracted] = useState(false);
  const [ttsAttempted, setTtsAttempted] = useState(false);
  const [ttsStatus, setTtsStatus] = useState('pending');
  const [greetingVisible, setGreetingVisible] = useState(false);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioRef = useRef(null);
  const streamRef = useRef(null);
  const audioContextRef = useRef(null);
  const greetingAudioRef = useRef(null);
  // Synchronous guard against double greeting playback. `ttsAttempted` is React
  // state and updates asynchronously, so the mount-timer and the first user
  // interaction can both pass a state-based check and play overlapping greeting
  // audio ("double voice"). A ref is read/written synchronously at call time.
  const greetingTtsStartedRef = useRef(false);
  const recognitionRef = useRef(null);
  
  const activeTurnRef = useRef(null);
  const abortControllerRef = useRef(null);
  const isProcessingRef = useRef(false);
  const hasPlayedGreetingRef = useRef(false);
  const sessionTitleSetRef = useRef(false);  // Tracks whether title was generated for current session

  // User ID for session management - initialized safely via useEffect
  const userIdRef = useRef(null);
  const userIdInitializedRef = useRef(false);
  
  // Safe getUserId — prefers Supabase auth user ID, falls back to localStorage
  const getUserId = useCallback(() => {
    // Prefer Supabase authenticated user ID
    if (authUser?.id) {
      userIdRef.current = authUser.id;
      return authUser.id;
    }
    // If already initialized from localStorage, return cached value
    if (userIdRef.current) {
      return userIdRef.current;
    }
    // Fallback: try to read from localStorage (may fail in some contexts)
    try {
      let userId = localStorage.getItem('havn_user_id');
      if (!userId) {
        userId = `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        localStorage.setItem('havn_user_id', userId);
      }
      userIdRef.current = userId;
      return userId;
    } catch (err) {
      // localStorage unavailable - generate ephemeral ID
      if (!userIdRef.current) {
        userIdRef.current = `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      }
      return userIdRef.current;
    }
  }, [authUser?.id]);
  
  // Initialize userId from localStorage on mount (not at render time)
  useEffect(() => {
    if (userIdInitializedRef.current) return;
    userIdInitializedRef.current = true;
    
    try {
      let userId = localStorage.getItem('havn_user_id');
      if (!userId) {
        userId = `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        localStorage.setItem('havn_user_id', userId);
      }
      userIdRef.current = userId;
    } catch (err) {
      console.warn("[HAVN] localStorage unavailable:", err);
      userIdRef.current = `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }
  }, []);

  // Send user email to backend via header so MongoDB can store it
  useEffect(() => {
    if (authUser?.email) {
      axios.defaults.headers.common['x-user-email'] = authUser.email;
    } else {
      delete axios.defaults.headers.common['x-user-email'];
    }
  }, [authUser?.email]);

  // x-user-lang header is set globally in I18nProvider

  // Sync user ID to extension for favorites (on login or session restore)
  useEffect(() => {
    if (authUser?.id) {
      syncUserToExtension(authUser.id);
    }
  }, [authUser?.id]);

  // Sync theme from backend when authenticated user loads
  useEffect(() => {
    if (!authUser?.id) return;
    (async () => {
      try {
        const res = await fetch(`/api/user/${authUser.id}/profile`, { credentials: 'include' });
        if (!res.ok) return;
        const data = await res.json();
        if (data.theme && data.theme !== themeName) {
          setTheme(data.theme);
          pushThemeToExtension(data.theme);
        }
      } catch (_) {}
    })();
  }, [authUser?.id]);

  // Show "Complete your profile" prompt for existing users missing first_name
  useEffect(() => {
    if (!authUser?.id) return;
    const meta = authUser.user_metadata || {};
    if (meta.first_name) return; // Already has profile data
    // Check if user has dismissed or completed recently
    const dismissed = localStorage.getItem('havn_profile_dismissed');
    if (dismissed) {
      const dismissedAt = parseInt(dismissed, 10);
      const threeDays = 3 * 24 * 60 * 60 * 1000;
      const dismissCount = parseInt(localStorage.getItem('havn_profile_dismiss_count') || '0', 10);
      if (dismissCount >= 2) return; // Stop asking after 2 dismissals
      if (Date.now() - dismissedAt < threeDays) return; // Wait 3 days
    }
    // Small delay so it doesn't block initial render
    const t = setTimeout(() => setShowProfileModal(true), 2000);
    return () => clearTimeout(t);
  }, [authUser?.id, authUser?.user_metadata?.first_name]);

  // ── iOS/WebKit audio unlock ────────────────────────────────────────────────
  // On iPhone (Safari AND Chrome — every iOS browser is WebKit), an
  // HTMLAudioElement may only start playback from a user gesture UNTIL that
  // specific element has played once inside a gesture. Our voice flow always
  // calls .play() AFTER an awaited /speak fetch — long outside the gesture —
  // so on iOS the TTS was silently blocked (the .catch(() => {}) swallowed the
  // NotAllowedError). Desktop Chrome tolerates this via its engagement
  // heuristics, which is why it only broke on the phone. Note that resuming
  // the AudioContext does NOT unlock <audio> elements — they are separate
  // gates. The fix: on the first real gesture, synchronously "prime" every
  // audio element we will ever speak through by playing a muted, 4-sample
  // silent WAV. A primed element keeps its gesture blessing across src
  // changes, so the post-fetch .play() calls succeed afterwards.
  const audioUnlockedRef = useRef(false);
  const unlockAudioPlayback = useCallback(() => {
    if (audioUnlockedRef.current) return;
    const SILENT_WAV = 'data:audio/wav;base64,UklGRigAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQQAAACAgICA';
    const prime = (el) => {
      if (!el) return;
      try {
        el.muted = true;
        el.src = SILENT_WAV;
        const p = el.play();
        if (p && p.then) {
          p.then(() => { try { el.pause(); el.muted = false; } catch (_) {} })
           .catch(() => { try { el.muted = false; } catch (_) {} });
        }
      } catch (_) { try { el.muted = false; } catch (_) {} }
    };
    try {
      prime(audioRef.current);
      // Pre-create the lazily-built elements so THEY get primed too — an
      // element constructed after an awaited fetch can never be unlocked.
      if (!sidePanelAudioElementRef.current) sidePanelAudioElementRef.current = new Audio();
      prime(sidePanelAudioElementRef.current);
      if (!greetingAudioRef.current) greetingAudioRef.current = new Audio();
      prime(greetingAudioRef.current);
      audioUnlockedRef.current = true;
      console.log('[AUDIO] Elements primed for iOS playback');
    } catch (_) {}
  }, []);

  const ensureAudioContext = useCallback(async () => {
    // Runs synchronously (pre-await) inside every tap handler that calls us —
    // exactly the gesture window WebKit requires for the unlock.
    unlockAudioPlayback();
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (audioContextRef.current.state === 'suspended') {
      try {
        await audioContextRef.current.resume();
      } catch (err) {
        console.error('Failed to resume AudioContext:', err);
      }
    }
    return audioContextRef.current;
  }, [unlockAudioPlayback]);

  // ═══════════════════════════════════════════════════════════════════════════
  // SESSION & TRANSCRIPT MANAGEMENT
  // ═══════════════════════════════════════════════════════════════════════════
  
  // Initialize session on mount
  useEffect(() => {
    const initSession = async (retryCount = 0) => {
      try {
        const userId = getUserId();
        console.log('[SESSION] Initializing for user:', userId);
        const response = await axios.get(`${API}/sessions/active/${userId}`);
        setActiveSession(response.data);
        sessionTitleSetRef.current = !response.data.is_new;  // Don't overwrite title of existing sessions
        console.log('[SESSION] Active session:', response.data?.id, 'is_new:', response.data?.is_new);

        // Load existing transcript if session has events
        if (!response.data.is_new && response.data.id) {
          const detailRes = await axios.get(`${API}/sessions/${response.data.id}`);
          if (detailRes.data?.transcript) {
            setLiveTranscript(detailRes.data.transcript);
          }
        }

        // Load session history
        const historyRes = await axios.get(`${API}/sessions/user/${userId}?limit=20`);
        setSessionHistory(historyRes.data || []);
      } catch (err) {
        console.error('[SESSION] Init error:', err?.message || err);
        // Retry once after 2s — backend may still be starting
        if (retryCount < 1) {
          console.log('[SESSION] Retrying init in 2s...');
          setTimeout(() => initSession(retryCount + 1), 2000);
        }
      }
    };

    initSession();
    // Pre-load contacts so voice contact lookup works without opening the panel
    loadContacts();
    // Load caregiver portal integration data
    loadAccessCode();
    loadPendingReminders();
    // Load saved favorites for panel actions
    loadSavedFavorites();
  }, []);

  // Load caregiver access code for display in contacts panel
  const loadAccessCode = useCallback(async () => {
    try {
      const userId = getUserId();
      const res = await axios.post(`${API}/caregiver/access-code/${userId}`);
      setAccessCode(res.data.code);
    } catch (err) {
      console.warn('[PORTAL] Access code load failed:', err.message);
    }
  }, []);

  // Load pending reminders due now
  const loadPendingReminders = useCallback(async () => {
    try {
      const userId = getUserId();
      const res = await axios.get(`${API}/caregiver/reminders/pending/${userId}`);
      if (res.data?.length > 0) {
        setPendingReminders(res.data);
        setReminderDismissed(false);
        // Request browser notification permission and fire one
        if ('Notification' in window && Notification.permission === 'default') {
          await Notification.requestPermission();
        }
        if ('Notification' in window && Notification.permission === 'granted') {
          const first = res.data[0];
          new Notification(`${first.title}`, {
            body: first.body,
            icon: '/havn-logo-white.png'
          });
        }
      }
    } catch (err) {
      console.warn('[PORTAL] Pending reminders load failed:', err.message);
    }
  }, []);

  // Acknowledge a reminder
  const handleAcknowledgeReminder = useCallback(async (reminderId) => {
    try {
      await axios.post(`${API}/caregiver/reminders/${reminderId}/acknowledge`);
      setPendingReminders(prev => prev.filter(r => r.id !== reminderId));
      if (pendingReminders.length <= 1) setReminderDismissed(true);
    } catch (err) {
      console.warn('[PORTAL] Ack failed:', err.message);
    }
  }, [pendingReminders]);
  
  // v4.5.24: Load set of already-saved favorite domains for current user
  const loadSavedFavorites = useCallback(async () => {
    try {
      const userId = getUserId();
      const res = await axios.get(`${API}/favorites/${userId}`);
      const domains = new Set((res.data || []).map(f => f.domain));
      setSavedFavorites(domains);
    } catch (_) {}
  }, []);
  
  // Scroll transcript to bottom when new events added
  useEffect(() => {
    if (transcriptEndRef.current) {
      transcriptEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [liveTranscript]);
  
  // Append event to transcript (local + API)
  const appendTranscriptEvent = useCallback(async (type, speaker, content, metadata = null) => {
    const sessionId = activeSession?.id;
    if (!sessionId) {
      console.warn('[TRANSCRIPT] Dropped event — no active session:', { type, speaker, content: content?.substring(0, 60) });
      return;
    }

    const event = {
      id: `evt_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
      timestamp: new Date().toISOString(),
      type,
      speaker,
      content,
      metadata,
    };

    // Update local state immediately
    setLiveTranscript(prev => [...prev, event]);

    // Persist to backend
    try {
      await axios.post(`${API}/sessions/${sessionId}/transcript`, {
        type,
        speaker,
        content,
        metadata,
      });
    } catch (err) {
      console.error('[TRANSCRIPT] Append error:', err?.response?.status, err?.message);
    }
  }, [activeSession]);
  
  // Load session history for history panel
  const loadSessionHistory = useCallback(async () => {
    try {
      const userId = getUserId();
      const response = await axios.get(`${API}/sessions/user/${userId}?limit=30`);
      setSessionHistory(response.data || []);
    } catch (err) {
      console.error('[SESSION] Load history error:', err);
    }
  }, []);

  // Bug 5: Delete a single session with optimistic update + rollback on
  // failure. Also clears any open history transcript view if it was
  // showing the deleted session, and short-circuits the delete confirm UI.
  const handleDeleteSession = useCallback(async (sessionId) => {
    if (!sessionId) return;
    const snapshot = sessionHistory;
    setSessionHistory(prev => prev.filter(s => s.id !== sessionId));
    setConfirmDeleteSessionId(null);
    setDeleteError(null);
    if (selectedHistorySession?.id === sessionId) setSelectedHistorySession(null);
    if (historyTranscriptView?.session?.id === sessionId) setHistoryTranscriptView(null);
    try {
      await axios.delete(`${API}/sessions/${sessionId}`);
    } catch (err) {
      console.error('[SESSION] Delete failed:', err?.message);
      setSessionHistory(snapshot);
      setDeleteError(sessionId);
      setTimeout(() => setDeleteError(null), 3000);
    }
  }, [sessionHistory, selectedHistorySession, historyTranscriptView]);

  // Bug 5: Clear ALL sessions for the current user. Same optimistic +
  // rollback pattern, with confirmation gated by `confirmClearAll`.
  const handleClearAllSessions = useCallback(async () => {
    const userId = getUserId();
    if (!userId) return;
    const snapshot = sessionHistory;
    setSessionHistory([]);
    setConfirmClearAll(false);
    setSelectedHistorySession(null);
    setHistoryTranscriptView(null);
    try {
      await axios.delete(`${API}/sessions/user/${userId}`);
    } catch (err) {
      console.error('[SESSION] Clear-all failed:', err?.message);
      setSessionHistory(snapshot);
      setDeleteError('__all__');
      setTimeout(() => setDeleteError(null), 3000);
    }
  }, [sessionHistory]);
  
  // FIX 2: Load transcript for a past session in history panel
  const historyTranscriptRef = useRef(null);
  const loadHistoryTranscript = useCallback(async (session) => {
    setIsLoadingHistoryTranscript(true);
    setHistoryTranscriptView({ session, events: [] });
    try {
      const res = await axios.get(`${API}/sessions/${session.id}`);
      setHistoryTranscriptView({ session, events: res.data?.transcript || [] });
    } catch (err) {
      console.error('[HISTORY] Transcript load error:', err);
      setHistoryTranscriptView({ session, events: [] });
    } finally {
      setIsLoadingHistoryTranscript(false);
      // Scroll transcript view into sight
      setTimeout(() => {
        historyTranscriptRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, []);
  
  // Continue/recall a past session
  const handleRecallSession = useCallback(async (sessionId) => {
    if (isRecallingSession) return;
    setIsRecallingSession(true);
    
    try {
      const userId = getUserId();
      
      // Activate the session
      await axios.post(`${API}/sessions/${sessionId}/activate?user_id=${userId}`);
      
      // Get session detail
      const detailRes = await axios.get(`${API}/sessions/${sessionId}`);
      setActiveSession(detailRes.data);
      setLiveTranscript(detailRes.data.transcript || []);
      setSelectedHistorySession(null);
      
      // Get recall summary
      const recallRes = await axios.post(`${API}/sessions/${sessionId}/summarize_for_recall`);
      
      if (recallRes.data) {
        // Store the recommended question for after voice playback
        setPendingRecallQuestion(recallRes.data.recommended_next_question);
        
        // Add system event to transcript
        await appendTranscriptEvent('system', 'system', `Continuing session: ${recallRes.data.summary}`);
        
        // Speak the recall voice script
        if (recallRes.data.voice_script && volume > 0) {
          try {
            const ttsRes = await axios.post(`${API}/speak`, {
              text: recallRes.data.voice_script,
              voice: 'nova',
            }, { responseType: 'blob' });

            await playTtsBlob(ttsRes.data, ttsRes.headers?.['content-type']);
          } catch (ttsErr) {
            console.error('[RECALL] TTS error:', ttsErr);
          }
        }
      }
      
      // Reload history
      await loadSessionHistory();
      
    } catch (err) {
      console.error('[SESSION] Recall error:', err);
      setErrorMessage('Could not continue that session');
    } finally {
      setIsRecallingSession(false);
    }
  }, [isRecallingSession, volume, appendTranscriptEvent, loadSessionHistory]);
  
  // Start a new session
  const handleNewSession = useCallback(async () => {
    try {
      const userId = getUserId();
      const response = await axios.post(`${API}/sessions`, {
        user_id: userId,
        title: 'New Session',
      });

      setActiveSession(response.data);
      sessionTitleSetRef.current = false;  // Reset title flag for new session
      setLiveTranscript([]);
      setMessages([]);
      setPendingRecallQuestion(null);
      setIsWebsiteSidePanelOpen(false);

      await loadSessionHistory();
    } catch (err) {
      console.error('[SESSION] New session error:', err);
    }
  }, [loadSessionHistory]);

  // ── Bug 4: Inactivity auto-end — two-stage 30s nudge + 15s farewell ──
  //
  // Replaces the old silent 5-minute idle timer. The previous behavior left
  // sessions dangling because seniors often stop talking without saying
  // "goodbye". Now we gently prompt at 30s ("Would you like to continue?")
  // and auto-end at 45s if no response. Any user activity (voice, text,
  // quick-reply button) cancels the flow and resets the timer.
  //
  // Refs so the timers survive re-renders without re-triggering the effect.
  const nudgeTimerRef = useRef(null);
  const farewellTimerRef = useRef(null);
  const inactivityEnabledRef = useRef(true); // disabled during voice playback

  const INACTIVITY_NUDGE_MS = 30 * 1000;
  const INACTIVITY_FAREWELL_MS = 15 * 1000;

  const clearInactivityTimers = useCallback(() => {
    if (nudgeTimerRef.current) { clearTimeout(nudgeTimerRef.current); nudgeTimerRef.current = null; }
    if (farewellTimerRef.current) { clearTimeout(farewellTimerRef.current); farewellTimerRef.current = null; }
  }, []);

  // Clears only the 30s nudge timer, leaving the farewell countdown intact.
  // Used by the effect cleanup so re-running the effect (e.g. when setMessages
  // injects the nudge message) doesn't tear down the 15s farewell that was
  // just started.
  const clearNudgeTimerOnly = useCallback(() => {
    if (nudgeTimerRef.current) { clearTimeout(nudgeTimerRef.current); nudgeTimerRef.current = null; }
  }, []);

  // Keep the ref in sync with state so callbacks can read it without
  // stale-closure bugs.
  useEffect(() => { waitingForContinueRef.current = waitingForContinue; }, [waitingForContinue]);

  // ── TTS helper for Bug 4 nudge / farewell prompts ──
  // Minimal wrapper around /api/speak that respects the current `lang`
  // so French users hear French voice output. Uses the same audio element
  // as playSidePanelTTS to avoid overlapping playback.
  const speakInactivityPrompt = useCallback(async (text) => {
    if (!text || volume <= 0) return;
    try {
      const ttsResponse = await axios.post(`${API}/speak`, {
        text,
        voice: 'nova',
        lang: lang || 'en',
      }, {
        timeout: getTtsTimeout(text),
        responseType: 'blob',
        validateStatus: (status) => status < 500,
      });
      if (ttsResponse.status === 200 && ttsResponse.data && ttsResponse.data.size > 100) {
        const audioBlob = new Blob([ttsResponse.data], { type: 'audio/mpeg' });
        const audioUrl = URL.createObjectURL(audioBlob);
        if (sidePanelAudioRef.current) URL.revokeObjectURL(sidePanelAudioRef.current);
        sidePanelAudioRef.current = audioUrl;
        if (!sidePanelAudioElementRef.current) {
          sidePanelAudioElementRef.current = new Audio();
        }
        sidePanelAudioElementRef.current.src = audioUrl;
        sidePanelAudioElementRef.current.volume = volume;
        await sidePanelAudioElementRef.current.play().catch(() => {});
      }
      // ElevenLabs-only: no browser speechSynthesis fallback.
    } catch (_) {
      // Silent on failure — no robotic computer-voice fallback.
    }
  }, [API, lang, volume]);

  // Shared end-session flow (Bug 4): marks the backend session inactive
  // (which triggers LLM title regen from Bug 3), reloads history, starts a
  // fresh session, and optionally plays the farewell aloud. `silent=true`
  // is used for page-unload / forced cleanup so we don't leak audio.
  const endSessionGracefully = useCallback(async ({ speakFarewell = false, reason = 'inactivity' } = {}) => {
    clearInactivityTimers();
    const endingId = activeSession?.id;
    if (!endingId) return;
    console.log('[SESSION] Auto-ending session:', reason, endingId);

    // Play farewell voice + render farewell bubble if requested
    if (speakFarewell) {
      const farewell = t('farewell_message');
      setMessages(prev => [...prev, { role: 'assistant', content: farewell, isSystemNotice: true }]);
      appendTranscriptEvent('message', 'assistant', farewell);
      // Don't await — fire-and-forget so UI feels snappy.
      speakInactivityPrompt(farewell);
      setSessionJustEnded(true);
    }

    // Clear nudge state before the end request lands.
    setWaitingForContinue(false);
    waitingForContinueRef.current = false;

    try {
      await axios.post(`${API}/sessions/${endingId}/end`).catch(() => {});
    } catch (_) {}

    try { await loadSessionHistory(); } catch (_) {}

    // Delay new-session creation a moment when we played the farewell so
    // the user can hear it before the UI resets.
    const startNext = async () => {
      try { await handleNewSession(); } catch (_) {}
      setLiveTranscript([]);
      setSessionJustEnded(false);
      setState(STATES.IDLE);
    };
    if (speakFarewell) {
      setTimeout(startNext, 3000);
    } else {
      startNext();
    }
  }, [API, activeSession, t, appendTranscriptEvent, speakInactivityPrompt, loadSessionHistory, handleNewSession, clearInactivityTimers]);

  // The nudge + farewell scheduler. Called on every message count change
  // and whenever voice playback ends. Guards against restarting when
  // `waitingForContinue` is already active — the 15s farewell timer owns
  // the timeline at that point.
  const armInactivityTimers = useCallback(() => {
    // CRITICAL: do the waitingForContinue guard BEFORE clearing timers.
    // The nudge-firing path sets `waitingForContinueRef.current = true`
    // and then starts the farewell timer, which in turn triggers a
    // setMessages/setState that re-runs this effect. If we cleared timers
    // here first we'd kill the farewell timer we just started.
    if (waitingForContinueRef.current) return;
    // Only clear the nudge timer; the farewell timer path only exists
    // when waitingForContinue=true, which we already bailed on above.
    clearNudgeTimerOnly();
    // Don't run before the user has said anything and don't run during
    // recall flows, farewell flashes, or voice playback.
    if (!activeSession?.id) return;
    if (messages.length === 0) return;
    if (isRecallingSession) return;
    if (sessionJustEnded) return;
    if (isVoicePlaying) return;
    if (!inactivityEnabledRef.current) return;

    nudgeTimerRef.current = setTimeout(async () => {
      nudgeTimerRef.current = null;
      if (waitingForContinueRef.current) return;
      const prompt = t('continue_question');
      console.log('[SESSION] Inactivity nudge firing');
      setMessages(prev => [...prev, { role: 'assistant', content: prompt, isNudge: true }]);
      try { appendTranscriptEvent('message', 'assistant', prompt); } catch (_) {}
      setWaitingForContinue(true);
      waitingForContinueRef.current = true;
      // Fire voice nudge — fire-and-forget, don't block farewell timer.
      speakInactivityPrompt(prompt);

      // Arm the farewell timer.
      farewellTimerRef.current = setTimeout(() => {
        farewellTimerRef.current = null;
        if (!waitingForContinueRef.current) return;
        console.log('[SESSION] Farewell firing — no response to nudge');
        endSessionGracefully({ speakFarewell: true, reason: 'nudge_timeout' });
      }, INACTIVITY_FAREWELL_MS);
    }, INACTIVITY_NUDGE_MS);
  }, [activeSession, messages.length, isRecallingSession, sessionJustEnded, isVoicePlaying, t, speakInactivityPrompt, endSessionGracefully, appendTranscriptEvent, clearInactivityTimers]);

  // Re-arm the inactivity timers whenever a new message arrives OR voice
  // playback stops. We intentionally don't include `armInactivityTimers`'
  // entire dep set here because the callback is already memoized and re-
  // created when its own deps change — this effect just triggers it.
  // The cleanup only nukes the nudge timer because the farewell timer is
  // owned by the nudge-firing path and must survive re-renders that happen
  // as a side-effect of the nudge's own setMessages/setState.
  useEffect(() => {
    armInactivityTimers();
    return () => clearNudgeTimerOnly();
  }, [messages.length, isVoicePlaying, activeSession?.id, armInactivityTimers, clearNudgeTimerOnly]);

  // Full cleanup on unmount — clears both timers so we don't leak.
  useEffect(() => {
    return () => clearInactivityTimers();
  }, [clearInactivityTimers]);

  // Quick-reply handlers wired to the nudge bubble.
  const handleContinueYes = useCallback(() => {
    console.log('[SESSION] User chose to continue');
    clearInactivityTimers();
    setWaitingForContinue(false);
    waitingForContinueRef.current = false;
    // Next user message will naturally re-arm the timer via the effect.
    armInactivityTimers();
  }, [clearInactivityTimers, armInactivityTimers]);

  const handleContinueNoEnd = useCallback(() => {
    console.log('[SESSION] User chose to end session');
    endSessionGracefully({ speakFarewell: true, reason: 'user_declined_continue' });
  }, [endSessionGracefully]);

  // Page-unload cleanup: if the user navigates away mid-session, fire a
  // best-effort end request so the title regen (Bug 3) still runs and the
  // session is marked inactive. Uses keepalive so the request survives the
  // navigation.
  useEffect(() => {
    const handler = () => {
      const id = activeSession?.id;
      if (!id) return;
      try {
        fetch(`${API}/sessions/${id}/end`, { method: 'POST', keepalive: true }).catch(() => {});
      } catch (_) {}
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [API, activeSession?.id]);

  // ── Language-aware TTS helper for the handoff flow ──
  //
  // Unlike speakInactivityPrompt, this accepts an explicit lang override
  // so we can speak the farewell in the OLD language after setLang has
  // been queued but before React re-renders with the new bundle. Returns
  // a Promise that resolves on audio 'ended' so the caller can serialize
  // handoff steps.
  const speakWithLang = useCallback(async (text, overrideLang) => {
    if (!text || volume <= 0) return;
    try {
      const ttsResponse = await axios.post(`${API}/speak`, {
        text,
        voice: 'nova',
        lang: overrideLang || 'en',
      }, {
        timeout: getTtsTimeout(text),
        responseType: 'blob',
        validateStatus: (status) => status < 500,
      });
      if (ttsResponse.status === 200 && ttsResponse.data && ttsResponse.data.size > 100) {
        const audioBlob = new Blob([ttsResponse.data], { type: 'audio/mpeg' });
        const audioUrl = URL.createObjectURL(audioBlob);
        if (sidePanelAudioRef.current) URL.revokeObjectURL(sidePanelAudioRef.current);
        sidePanelAudioRef.current = audioUrl;
        if (!sidePanelAudioElementRef.current) {
          sidePanelAudioElementRef.current = new Audio();
        }
        sidePanelAudioElementRef.current.src = audioUrl;
        sidePanelAudioElementRef.current.volume = volume;
        await new Promise((resolve) => {
          const el = sidePanelAudioElementRef.current;
          const onEnd = () => {
            el.removeEventListener('ended', onEnd);
            el.removeEventListener('error', onEnd);
            resolve();
          };
          el.addEventListener('ended', onEnd);
          el.addEventListener('error', onEnd);
          el.play().catch(onEnd);
          // Safety net — never hang the handoff on a stuck audio element.
          setTimeout(onEnd, getTtsTimeout(text) + 2000);
        });
      }
      // ElevenLabs-only: no browser speechSynthesis fallback.
    } catch (_) {
      // Silent on failure — handoff continues, but no robotic computer voice.
    }
  }, [API, volume]);

  // ── Graceful EN/FR handoff during an active Talk session ──
  //
  // Replaces the raw setLang call on the language toggle buttons. Fresh
  // sessions (no messages) fall through to setLang immediately. Active
  // sessions run the five-step flow: freeze orb → speak farewell in
  // OLD lang → end backend session (triggers Bug 3 title regen) →
  // setLang flip → new session + re-intro in NEW lang → unfreeze.
  //
  // Overlay and re-intro strings are hardcoded as literals because the
  // t() closure captured at callback-creation time can't see the new
  // bundle until React re-renders. The i18n bundles still contain the
  // keys for documentation and future expansion.
  const handleLanguageHandoff = useCallback(async (newLang) => {
    if (!newLang || newLang === lang) return;
    if (langHandoffInFlightRef.current) return;

    const hasActiveSession = !!activeSession?.id && messages.length > 0;
    if (!hasActiveSession) {
      // No ceremony for fresh sessions — straight flip.
      setLang(newLang);
      return;
    }

    langHandoffInFlightRef.current = true;

    const OVERLAY = {
      en: 'Switching to English…',
      fr: 'Passage en français…',
    };
    const REINTRO = {
      en: "I'm ready in English. Tap to start.",
      fr: 'Je suis prêt en français. Appuyez pour commencer.',
    };

    try {
      // 1) Freeze orb + overlay
      setLangHandoffOverlay(OVERLAY[newLang] || '');
      setSessionJustEnded(true);  // blocks orb interaction via existing state
      clearInactivityTimers();
      setWaitingForContinue(false);
      waitingForContinueRef.current = false;

      // 2) Speak farewell in the CURRENT language. t() resolves in the
      //    current bundle because setLang hasn't run yet.
      const farewell = t(`lang_switching_farewell_to_${newLang}`);
      setMessages(prev => [...prev, { role: 'assistant', content: farewell, isSystemNotice: true }]);
      try { appendTranscriptEvent('message', 'assistant', farewell); } catch (_) {}
      await speakWithLang(farewell, lang);

      // 3) End current session (backend title regen from Bug 3 kicks in).
      //    2s cap so a slow backend doesn't stall the handoff.
      const endingId = activeSession.id;
      try {
        await Promise.race([
          axios.post(`${API}/sessions/${endingId}/end`).catch(() => {}),
          new Promise(resolve => setTimeout(resolve, 2000)),
        ]);
      } catch (_) {}

      // 4) Flip the language. Updates axios defaults + localStorage +
      //    i18n bundle via the I18nProvider useEffect.
      setLang(newLang);

      // 5) Refresh session history (new x-user-lang header) + create
      //    new session. Must complete before we unfreeze the orb —
      //    otherwise the user's next chat turn will post to the stale
      //    (ended) session ID and all downstream state (activeSession,
      //    inactivity timers) will be broken.
      //
      //    handleNewSession internally calls loadSessionHistory at its
      //    tail, so we only need to call it once. No outer timeout:
      //    the user is listening to the farewell + seeing the overlay
      //    during this window, so 2-4s of backend work is acceptable.
      //    If the backend is genuinely dead, handleNewSession's own
      //    try/catch logs the error and we proceed to step 6 anyway.
      try {
        await handleNewSession();
      } catch (_) {}

      // 6) Speak re-intro in the NEW language. Hardcoded literal to
      //    bypass the stale t() closure.
      const reintro = REINTRO[newLang] || REINTRO.en;
      setMessages(prev => [...prev, { role: 'assistant', content: reintro, isSystemNotice: true }]);
      try { appendTranscriptEvent('message', 'assistant', reintro); } catch (_) {}
      await speakWithLang(reintro, newLang);

      // 7) Clear overlay and re-enable.
      setLangHandoffOverlay(null);
      setSessionJustEnded(false);
      setState(STATES.IDLE);
    } catch (e) {
      console.error('[LANG_HANDOFF] Failed:', e);
      // Recovery: drop overlay, flip lang anyway, unfreeze orb.
      setLangHandoffOverlay(null);
      setSessionJustEnded(false);
      try { setLang(newLang); } catch (_) {}
    } finally {
      langHandoffInFlightRef.current = false;
    }
  }, [lang, activeSession, messages.length, t, setLang, clearInactivityTimers, speakWithLang, loadSessionHistory, handleNewSession, appendTranscriptEvent]);

  // ============================================
  // CONTACTS MANAGEMENT
  // ============================================
  
  // Load contacts for current user
  const loadContacts = useCallback(async () => {
    setIsLoadingContacts(true);
    try {
      const userId = getUserId();
      const response = await axios.get(`${API}/contacts/user/${userId}`);
      setContacts(response.data || []);
      
      // Also fetch primary caregiver specifically
      const primaryRes = await axios.get(`${API}/contacts/primary/${userId}`);
      setPrimaryCaregiver(primaryRes.data);
    } catch (err) {
      console.error('[CONTACTS] Load error:', err);
    } finally {
      setIsLoadingContacts(false);
    }
  }, []);
  
  // Create a new contact
  const handleCreateContact = useCallback(async (e) => {
    e?.preventDefault();
    setContactError('');
    
    if (!newContactForm.display_name.trim()) {
      setContactError('Name is required');
      return;
    }
    
    // Validate primary caregiver has phone or email
    if (newContactForm.is_primary_caregiver && !newContactForm.phone && !newContactForm.email) {
      setContactError('Primary caregiver must have phone or email');
      return;
    }
    
    try {
      const userId = getUserId();
      await axios.post(`${API}/contacts`, {
        user_id: userId,
        display_name: newContactForm.display_name.trim(),
        phone: newContactForm.phone.trim() || null,
        email: newContactForm.email.trim() || null,
        relationship: newContactForm.relationship.trim() || null,
        is_caregiver: newContactForm.is_caregiver || newContactForm.is_primary_caregiver,
        is_primary_caregiver: newContactForm.is_primary_caregiver,
        notes: newContactForm.notes.trim() || null
      });
      
      // Reset form and reload
      setNewContactForm({
        display_name: '', phone: '', email: '', relationship: '',
        is_caregiver: false, is_primary_caregiver: false, notes: ''
      });
      setShowAddContactForm(false);
      await loadContacts();
    } catch (err) {
      console.error('[CONTACTS] Create error:', err);
      setContactError(err.response?.data?.detail || 'Failed to create contact');
    }
  }, [newContactForm, loadContacts]);
  
  // Update an existing contact
  const handleUpdateContact = useCallback(async (contactId, updates) => {
    setContactError('');
    try {
      await axios.put(`${API}/contacts/${contactId}`, updates);
      setEditingContact(null);
      await loadContacts();
    } catch (err) {
      console.error('[CONTACTS] Update error:', err);
      setContactError(err.response?.data?.detail || 'Failed to update contact');
    }
  }, [loadContacts]);
  
  // Delete a contact
  const handleDeleteContact = useCallback(async (contactId) => {
    if (!window.confirm('Are you sure you want to remove this contact?')) return;
    
    try {
      await axios.delete(`${API}/contacts/${contactId}`);
      await loadContacts();
    } catch (err) {
      console.error('[CONTACTS] Delete error:', err);
    }
  }, [loadContacts]);
  
  // Toggle caregiver status
  const handleToggleCaregiver = useCallback(async (contact, field) => {
    const updates = {};
    
    if (field === 'is_primary_caregiver') {
      // When setting as primary, validate phone/email
      if (!contact.is_primary_caregiver && !contact.phone && !contact.email) {
        setContactError('Primary caregiver must have phone or email');
        return;
      }
      updates.is_primary_caregiver = !contact.is_primary_caregiver;
      if (updates.is_primary_caregiver) {
        updates.is_caregiver = true;
      }
    } else {
      updates.is_caregiver = !contact.is_caregiver;
      // If un-checking caregiver, also un-check primary
      if (!updates.is_caregiver && contact.is_primary_caregiver) {
        updates.is_primary_caregiver = false;
      }
    }
    
    await handleUpdateContact(contact.id, updates);
  }, [handleUpdateContact]);
  
  // Open edit form for a contact
  const handleOpenEdit = useCallback((contact) => {
    setEditingContact(contact);
    setEditForm({
      display_name: contact.display_name || '',
      phone: contact.phone || '',
      email: contact.email || '',
      relationship: contact.relationship || '',
      is_caregiver: contact.is_caregiver || false,
      is_primary_caregiver: contact.is_primary_caregiver || false,
      notes: contact.notes || ''
    });
    setShowAddContactForm(false); // Close add form if open
  }, []);

  // Save edits to a contact
  const handleSaveEdit = useCallback(async () => {
    setContactError('');
    if (!editForm.display_name.trim()) {
      setContactError('Name is required');
      return;
    }
    if (editForm.is_primary_caregiver && !editForm.phone && !editForm.email) {
      setContactError('Primary caregiver must have phone or email');
      return;
    }
    await handleUpdateContact(editingContact.id, {
      display_name: editForm.display_name.trim(),
      phone: editForm.phone.trim() || null,
      email: editForm.email.trim() || null,
      relationship: editForm.relationship.trim() || null,
      is_caregiver: editForm.is_caregiver || editForm.is_primary_caregiver,
      is_primary_caregiver: editForm.is_primary_caregiver,
      notes: editForm.notes.trim() || null
    });
    setEditingContact(null);
  }, [editForm, editingContact, handleUpdateContact]);

  // Toggle notification preference for a contact
  const handleToggleNotification = useCallback(async (contact, field) => {
    await handleUpdateContact(contact.id, {
      [field]: !contact[field]
    });
  }, [handleUpdateContact]);

  // Open in-app call/contact modal for any contact
  const handleContactCall = useCallback((contact) => {
    if (!contact) return;
    setCallModalContact(contact);
    sessionLogger.log('contact_call_modal_open', { contactId: contact.id, displayName: contact.display_name });
  }, [sessionLogger]);

  // Legacy alias — primary caregiver button still works
  const handleCallPrimaryCaregiver = useCallback(() => {
    if (primaryCaregiver) handleContactCall(primaryCaregiver);
  }, [primaryCaregiver, handleContactCall]);

  const resetToIdle = useCallback((turnId) => {
    logTurn(turnId || 'unknown', 'RESET_TO_IDLE');
    
    if (activeTurnRef.current === turnId) {
      activeTurnRef.current = null;
    }
    isProcessingRef.current = false;
    
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      // Restore audio element's onended handler to prevent stale contact handlers
      audioRef.current.onended = null; // React's onEnded prop handles the rest
    }
    
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      try {
        mediaRecorderRef.current.stop();
      } catch (e) {}
    }

    // v4.8.8: Detach wave audio hook so the waveform returns to idle
    // when the user interrupts speech by tapping the orb.
    try { detachAudio(); } catch (_) {}

    setState(STATES.IDLE);
    setErrorMessage('');
  }, [detachAudio]);

  useEffect(() => {
    return () => {
      resetToIdle('unmount');
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, [resetToIdle]);

  // Process response for hyperlinks (for transcript)
  const processResponseForLinks = (text) => {
    const urlPattern = /\[([^\]]+)\]\(([^)]+)\)/g;
    return text.replace(urlPattern, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-[var(--sidekick-primary)] underline hover:opacity-80">$1</a>');
  };

  /**
   * resolveContactIntent — v2
   * 
   * Detects when the user wants to reach a specific person from their contact list,
   * or needs help in a way that warrants contacting a caregiver.
   * 
   * Returns: { matched: boolean, contact: Contact|null, reason: string }
   *   - matched: true if we should open the call modal
   *   - contact: the specific Contact object to show, or null if ambiguous
   *   - reason: 'explicit_name' | 'role_match' | 'caregiver_distress' | 'primary_caregiver'
   */
  const resolveContactIntent = useCallback((message, contactList, primaryCaregiverContact) => {
    const t = message.toLowerCase().trim();

    // ── 1. Explicit request to call/reach someone ──────────────────────────
    const callVerbs = /\b(call|reach|contact|get|notify|text|email|ring|phone|message|talk to|speak to|connect me with|send.*to|tell)\b/i;
    const hasCallVerb = callVerbs.test(t);

    // Exclude descriptions of RECEIVING a call/text/email from someone else.
    // "Someone called me" / "They texted me" / "I got a call from Microsoft" all
    // match callVerbs above, but they are fraud reports — not requests to call a contact.
    const isIncomingEvent = /\b((someone|they|he|she|caller|it|a person|a man|a woman|the company|the bank|irs|microsoft|amazon|apple|google|social security|medicare)\s+(called|texted|emailed|messaged|phoned|contacted|reached out|sent|wrote)|(got|received|getting)\s+(a\s+)?(call|text|email|message|voicemail|letter|notification)|(a call|a text|an email|a message|a voicemail)\s+(came|from|saying|about|claiming)|they\s+(told|asked|said|wanted)\s+me|i\s+(was|got|have been)\s+(called|contacted|emailed|texted|reached|phoned)|i\s+received\b)\b/i.test(t);

    if (isIncomingEvent) {
      return { matched: false, contact: null, reason: 'none' };
    }

    // ── 2. Distress / emergency signals (always escalate to primary caregiver) ──
    const distressPatterns = [
      /i('m| am) (scared|afraid|worried|confused|lost|hurt|sick|not (ok|okay|well|feeling well))/i,
      /something('s| is) wrong/i,
      /i need (help|someone|assistance|support)/i,
      /i('m| am) (having|in) (trouble|pain|an emergency|a problem)/i,
      /call (for )?help/i,
      /emergency/i,
      /don('t| not) feel (safe|well|good)/i,
      /i fell/i,
      /can('t| not) (breathe|move|get up|stand)/i,
      /help( me)?!?$/i,
    ];
    const isDistress = distressPatterns.some(p => p.test(t));

    // Only escalate for physical emergencies — fraud reports like "I need help, I was scammed"
    // match distress patterns but must go to the LLM, not the call modal.
    const isPhysicalEmergency = /\b(emergency|911|ambulance|fell|can'?t breathe|can'?t move|can'?t get up|chest pain|unconscious|fire|bleeding|hurt badly|injury)\b/i.test(t);

    if (isDistress && isPhysicalEmergency && primaryCaregiverContact) {
      return { matched: true, contact: primaryCaregiverContact, reason: 'caregiver_distress' };
    }

    if (!hasCallVerb && !isDistress) {
      return { matched: false, contact: null, reason: 'none' };
    }

    // ── 3. Role-based matching (e.g. "call my daughter", "get my doctor") ──
    const roleKeywords = [
      { pattern: /\b(primary caregiver|main caregiver|my caregiver|caregiver)\b/i, role: 'primary_caregiver' },
      { pattern: /\b(daughter|son|child|kid|grandchild|granddaughter|grandson)\b/i, role: 'family' },
      { pattern: /\b(wife|husband|spouse|partner|significant other)\b/i, role: 'family' },
      { pattern: /\b(mother|mom|mum|father|dad|parent|brother|sister|sibling)\b/i, role: 'family' },
      { pattern: /\b(doctor|dr\.?|physician|nurse|therapist|helper|assistant)\b/i, role: 'caregiver' },
      { pattern: /\b(friend|neighbour|neighbor|emergency contact)\b/i, role: 'contact' },
    ];

    for (const { pattern, role } of roleKeywords) {
      if (pattern.test(t)) {
        if (role === 'primary_caregiver' && primaryCaregiverContact) {
          return { matched: true, contact: primaryCaregiverContact, reason: 'primary_caregiver' };
        }
        // Find first caregiver in list for caregiver role
        if (role === 'caregiver') {
          const cg = contactList.find(c => c.is_caregiver || c.is_primary_caregiver);
          if (cg) return { matched: true, contact: cg, reason: 'role_match' };
        }
        // Fall through to name match for other roles
      }
    }

    // ── 4. Name-based matching (e.g. "call Sarah", "get John") ──
    // Extract potential name tokens: capitalized words after a call verb
    // Also match against display_name and relationship fields
    if (contactList.length > 0) {
      // Try direct name match first (case-insensitive substring)
      for (const contact of contactList) {
        const nameTokens = contact.display_name.toLowerCase().split(/\s+/);
        const relToken = (contact.relationship || '').toLowerCase();
        // Match if any word of the contact's name appears in the message
        const nameMatch = nameTokens.some(token => token.length > 2 && t.includes(token));
        const relMatch = relToken.length > 2 && t.includes(relToken);
        if (nameMatch || relMatch) {
          return { matched: true, contact, reason: 'explicit_name' };
        }
      }
    }

    // ── 5. Generic "call someone" with no specific match → primary caregiver ──
    if (hasCallVerb && primaryCaregiverContact) {
      return { matched: true, contact: primaryCaregiverContact, reason: 'primary_caregiver' };
    }

    // ── 6. No contacts saved — signal the mismatch so UI can prompt to add one ──
    if (hasCallVerb && contactList.length === 0) {
      return { matched: true, contact: null, reason: 'no_contacts' };
    }

    return { matched: false, contact: null, reason: 'none' };
  }, []);

  // Audio event handlers - must be defined before submitMessage which references handleAudioEnded
  const handleAudioEnded = useCallback(() => {
    setIsVoicePlaying(false);
  }, []);
  const handleAudioError = useCallback(() => {
    setIsVoicePlaying(false);
    resetToIdle(activeTurnRef.current);
  }, [resetToIdle]);

  const submitMessage = useCallback(async (text, turnId, mode = 'text') => {
    if (!text.trim()) return;

    // Bug 4: Any user input cancels the inactivity nudge/farewell flow so
    // the session doesn't end under them mid-sentence. The next message
    // count change will re-arm the 30s timer via the effect.
    if (waitingForContinueRef.current || nudgeTimerRef.current || farewellTimerRef.current) {
      if (nudgeTimerRef.current) { clearTimeout(nudgeTimerRef.current); nudgeTimerRef.current = null; }
      if (farewellTimerRef.current) { clearTimeout(farewellTimerRef.current); farewellTimerRef.current = null; }
      if (waitingForContinueRef.current) {
        setWaitingForContinue(false);
        waitingForContinueRef.current = false;
      }
    }

    logTurn(turnId, 'SUBMIT_START', `mode=${mode}`);
    sessionLogger.log('message_sent', { turnId, mode, text: text.substring(0, 100) });
    
    // Infer intent from user message (invisible to user)
    const inferredIntent = inferIntent(text);
    logTurn(turnId, 'INTENT_INFERRED', inferredIntent);
    sessionLogger.log('intent_inferred', { turnId, intent: inferredIntent });
    
    // ── Contact intent resolution (v2) ────────────────────────────────────────
    // Uses live contacts list + primaryCaregiver state, not legacy caregiverStorage
    const contactResolution = resolveContactIntent(text, contacts, primaryCaregiver);

    if (contactResolution.matched) {
      const { contact, reason } = contactResolution;

      // ── No contacts saved at all ──
      if (reason === 'no_contacts') {
        const noContactMsg = "I'd like to help you reach someone, but you haven't saved any contacts yet. "
          + "Tap the Contacts button at the bottom of the screen to add someone you trust.";
        setMessages(prev => [...prev, { role: 'user', content: text }]);
        setMessages(prev => [...prev, { role: 'assistant', content: noContactMsg }]);
        conversationStorage.add('user', text, currentSessionId);
        conversationStorage.add('assistant', noContactMsg, currentSessionId);
        appendTranscriptEvent('message', 'assistant', noContactMsg);
        // Speak the message
        if (volume > 0) {
          try {
            const ttsRes = await axios.post(`${API}/speak`, { text: noContactMsg, voice: 'nova' },
              { responseType: 'blob', timeout: TIMEOUTS.TTS });
            await playTtsBlob(ttsRes.data, ttsRes.headers?.['content-type']);
          } catch (_) {}
        }
        resetToIdle(turnId);
        return;
      }

      // ── Contact found — build spoken response + open call modal ──
      let spokenResponse;
      if (reason === 'caregiver_distress') {
        spokenResponse = `I'm opening your contact for ${contact.display_name} right now. `
          + (contact.phone ? `You can call them at ${contact.phone}.` : `You can reach them by email.`);
      } else if (reason === 'primary_caregiver') {
        spokenResponse = `Here's ${contact.display_name}${contact.relationship ? `, your ${contact.relationship}` : ''}.`
          + (contact.phone ? ` Their number is ${contact.phone}.` : '');
      } else {
        // explicit_name or role_match
        spokenResponse = `I found ${contact.display_name}${contact.relationship ? ` — ${contact.relationship}` : ''}.`
          + (contact.phone ? ` Their number is ${contact.phone}.` : '')
          + (contact.email && !contact.phone ? ` You can email them at ${contact.email}.` : '');
      }

      // Add to messages and transcript
      setMessages(prev => [...prev, { role: 'user', content: text }]);
      setMessages(prev => [...prev, { role: 'assistant', content: spokenResponse }]);
      conversationStorage.add('user', text, currentSessionId);
      conversationStorage.add('assistant', spokenResponse, currentSessionId);
      appendTranscriptEvent('message', 'assistant', spokenResponse);
      sessionLogger.log('contact_resolved', {
        turnId,
        reason,
        contactId: contact.id,
        displayName: contact.display_name
      });

      // Speak it
      if (volume > 0) {
        try {
          const ttsRes = await axios.post(`${API}/speak`, { text: spokenResponse, voice: 'nova' },
            { responseType: 'blob', timeout: TIMEOUTS.TTS });
          if (ttsRes.data) {
            const url = URL.createObjectURL(new Blob([ttsRes.data], { type: 'audio/mpeg' }));
            await ensureAudioContext();
            if (audioRef.current) {
              audioRef.current.src = url;
              audioRef.current.volume = volume;
              // Open call modal after audio finishes
              audioRef.current.onended = () => {
                setCallModalContact(contact);
                audioRef.current.onended = handleAudioEnded; // restore default handler
              };
              try {
                await audioRef.current.play();
                // Set a fallback timeout in case onended doesn't fire (e.g., headless browser)
                setTimeout(() => {
                  setCallModalContact(contact);
                }, 3000);
              } catch (playErr) {
                setCallModalContact(contact); // fallback: open immediately if audio fails
              }
            }
          } else {
            setCallModalContact(contact);
          }
        } catch (err) {
          setCallModalContact(contact); // open modal even if TTS fails
        }
      } else {
        // Muted — open modal immediately
        setCallModalContact(contact);
      }

      resetToIdle(turnId);
      return;
    }
    // ── End contact intent resolution ─────────────────────────────────────────
    
    setMessages(prev => [...prev, { role: 'user', content: text }]);
    conversationStorage.add('user', text, currentSessionId);
    appendTranscriptEvent('message', 'user', text);
    
    // Auto-generate session title from first user message (ref avoids stale closure on messages)
    const sessionId = activeSession?.id;
    if (!sessionTitleSetRef.current && sessionId) {
      sessionTitleSetRef.current = true;
      const autoTitle = generateSessionTitle(text);
      console.log('[SESSION] Setting title:', autoTitle, 'for session:', sessionId);
      axios.patch(`${API}/sessions/${sessionId}/title`, { title: autoTitle })
        .then(() => {
          setActiveSession(prev => prev ? { ...prev, title: autoTitle } : prev);
          setSessionHistory(prev => prev.map(s => s.id === sessionId ? { ...s, title: autoTitle } : s));
        })
        .catch(err => console.warn('[SESSION] Title update failed:', err?.message));
    }
    
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;
    
    // Safety net: if turn is still active after 30s, force-reset (Safari audio bug workaround)
    const thinkingSafetyTimer = setTimeout(() => {
      if (activeTurnRef.current === turnId) {
        console.error('[SAFETY] Thinking timeout after 30s — force-resetting orb. turnId:', turnId);
        sessionLogger.log('thinking_safety_timeout', { turnId });
        setErrorMessage('Something went wrong, please try again.');
        setState(STATES.ERROR);
        setTimeout(() => resetToIdle(turnId), 2500);
      }
    }, TIMEOUTS.THINKING_SAFETY);

    try {
      setState(STATES.PROCESSING);

      // ============================================
      // WEBSITE_RESEARCH: Route to /api/research/site/panel
      // Returns simplified, senior-friendly side panel format
      // Uses fetchWebsitePanel helper (requirement B: single source of truth)
      // ============================================
      if (inferredIntent === INTENTS.WEBSITE_RESEARCH) {
        const extractedUrl = extractUrlFromMessage(text);
        
        if (extractedUrl) {
          logTurn(turnId, 'WEBSITE_RESEARCH_START', extractedUrl);
          sessionLogger.log('research_start', { turnId, url: extractedUrl });
          
          // Extract hostname from URL for fetchWebsitePanel
          let hostname;
          try {
            hostname = new URL(extractedUrl.startsWith('http') ? extractedUrl : `https://${extractedUrl}`).hostname;
          } catch {
            hostname = extractedUrl.replace(/^https?:\/\//, '').replace(/\/.*$/, '');
          }
          
          // Store hostname and use single fetch helper (requirement B)
          setLastCheckedHostname(hostname);
          const panelData = await fetchWebsitePanel(hostname, text);
          
          if (!panelData) {
            // Request was superseded by a newer one
            resetToIdle(turnId);
            return;
          }
          
          logTurn(turnId, 'WEBSITE_PANEL_SUCCESS', panelData);
          sessionLogger.log('panel_success', { turnId, verdict: panelData.verdict });
          
          // Build a short message for the transcript
          let summaryMessage = `I checked **${panelData.domain}**.`;
          if (panelData.brand_name) {
            summaryMessage = `I checked **${panelData.brand_name}** (${panelData.domain}).`;
          }
          summaryMessage += ` ${panelData.one_liner}`;
          
          setMessages(prev => [...prev, { 
            role: 'assistant', 
            content: summaryMessage,
            panelData: panelData
          }]);
          conversationStorage.add('assistant', summaryMessage, currentSessionId);
          appendTranscriptEvent('website_check', 'assistant', summaryMessage, { hostname: panelData?.domain });
          recordInteraction();
          
          resetToIdle(turnId);
          return;
        }
        
        // No URL found — speak the prompt AND open the website check panel so user can type
        const askForUrlMessage = "Sure — to make sure I get the spelling right, please type the website address below.";
        setMessages(prev => [...prev, { role: 'assistant', content: askForUrlMessage }]);
        conversationStorage.add('assistant', askForUrlMessage, currentSessionId);
        appendTranscriptEvent('message', 'assistant', askForUrlMessage);

        // Open the website check panel so the user has an input field
        // Mark that we're in a website research flow so handleWebsiteCheckConfirm knows to flow back
        setShowWebsiteCheckPanel(true);
        setWebsiteCheckInput('');
        setWebsiteCheckConfirm(null);
        setIsInWebsiteResearchFlow(true); // Track that we're mid-conversation

        // Speak the prompt
        if (volume > 0) {
          try {
            const ttsResponse = await axios.post(`${API}/speak`, {
              text: askForUrlMessage,
              voice: 'nova',
              turn_id: turnId
            }, {
              signal,
              timeout: TIMEOUTS.TTS,
              responseType: 'blob',
              validateStatus: status => status < 500
            });

            if (ttsResponse.status === 200 && ttsResponse.data) {
              const audioBlob = new Blob([ttsResponse.data], { type: 'audio/mpeg' });
              const audioUrl = URL.createObjectURL(audioBlob);
              await ensureAudioContext();
              if (audioRef.current) {
                audioRef.current.src = audioUrl;
                audioRef.current.volume = volume;
                await audioRef.current.play();
              }
            }
          } catch (ttsErr) {
            console.warn('TTS skipped:', ttsErr.message);
          }
        }

        resetToIdle(turnId);
        return;
      }
      
      // ============================================
      // OTHER INTENTS: Route to /api/chat
      // ============================================
      sessionLogger.log('chat_start', { turnId });
      console.log('[CHAT] request sent', { turnId, intent: inferredIntent });

      // Pass inferred intent to backend for context-aware responses
      const chatResponse = await axios.post(`${API}/chat`, {
        session_id: activeSession?.id || currentSessionId,
        message: text,
        turn_id: turnId,
        inferred_intent: inferredIntent,
        lang: lang || 'en',
      }, { signal, timeout: TIMEOUTS.CHAT });
      console.log('[CHAT] response received, status:', chatResponse.status);
      
      // DEBUG: Log full chat response to inspect audio field
      console.log('[CHAT RESPONSE DEBUG]', {
        status: chatResponse.status,
        hasAudio: !!chatResponse.data.audio,
        audioSize: chatResponse.data.audio?.length,
        audioFallback: chatResponse.data.audio_fallback,
        ttsError: chatResponse.data.tts_error,  // ElevenLabs status code / sentinel when audio skipped
        chatError: chatResponse.data.chat_error,  // LLM-layer failure: 401=bad key, 404=bad model, 429=busy
        intent: chatResponse.data.detected_intent,
        responseLength: chatResponse.data.response?.length,
        fullResponse: chatResponse.data
      });

      // The reply is the canned error fallback — the LLM call itself failed.
      // Voice is absent because error turns never reach TTS (by design).
      if (chatResponse.data.chat_error != null) {
        console.error('[CHAT LLM FAILED] reason:', chatResponse.data.chat_error,
          '— 401=Anthropic key invalid/expired, 404=CLAUDE_MODEL id invalid, 429=rate-limited. Fix on Railway env.');
      }
      
      sessionLogger.log('chat_success', { turnId, responseLength: chatResponse.data.response?.length });
      
      if (chatResponse.data.error) {
        throw new Error(chatResponse.data.error);
      }
      
      const assistantMessage = chatResponse.data.response;
      if (!assistantMessage) {
        throw new Error('Empty response');
      }
      
      // Check for links and show card
      const links = extractLinks(assistantMessage);
      if (links.length > 0) {
        setLinkCardData(links);
        sessionLogger.log('link_card_shown', { turnId, links: links.length });
      }
      
      // Process for hyperlinks in transcript
      const processedMessage = processResponseForLinks(assistantMessage);
      
      console.log('[CHAT] response body parsed, message length:', assistantMessage.length);
      setMessages(prev => [...prev, { role: 'assistant', content: assistantMessage, html: processedMessage }]);
      conversationStorage.add('assistant', assistantMessage, currentSessionId);
      appendTranscriptEvent('message', 'assistant', assistantMessage);
      recordInteraction();
      console.log('[CHAT] updating UI state — message added to conversation');

      // CRITICAL: Reset orb to IDLE immediately after text is shown.
      // TTS is non-blocking — the user can see and read the response.
      // This prevents iOS/Safari from getting stuck in "thinking" or "speaking" forever.
      clearTimeout(thinkingSafetyTimer);
      resetToIdle(turnId);

      // Voice is ElevenLabs-only. The fire-and-forget TTS IIFE below plays
      // the ElevenLabs audio; we no longer start the browser's robotic
      // speechSynthesis voice (it used to overlap ElevenLabs in the web app).

      // Check if session should end
      if (checkSessionEnd(text, assistantMessage)) {
        sessionLogger.log('session_end_natural', { turnId });
        setSessionJustEnded(true);
        setTimeout(async () => {
          // Save/close session on backend
          try {
            if (activeSession?.id) {
              await axios.post(`${API}/sessions/${activeSession.id}/end`).catch(() => {});
            }
          } catch (_) {}
          // Reload history so the ended session appears
          await loadSessionHistory();
          // Start a new session on the backend
          await handleNewSession();
          // Clear local transcript
          setLiveTranscript([]);
          // Visual feedback: reset ended state
          setSessionJustEnded(false);
          setState(STATES.IDLE);
        }, 3500);
      }
      
      // TTS — fire-and-forget: orb is already IDLE, text is already shown.
      // Audio playback runs in background. If it fails, user still has the text.
      (async () => { try {
        sessionLogger.log('tts_start', { turnId });
        
        console.log('[TTS SECTION REACHED]', { 
          turnId, 
          hasAudio: !!chatResponse.data.audio,
          audioFallback: chatResponse.data.audio_fallback,
          audioLength: chatResponse.data.audio?.length 
        });
        
        let audioData = null;
        let audioPlayed = false;
        
        // First, try to use audio from chat response if available
        if (chatResponse.data.audio && !chatResponse.data.audio_fallback) {
          try {
            console.log('[USING CHAT AUDIO]', { audioSize: chatResponse.data.audio.length });
            sessionLogger.log('tts_use_chat_audio', { turnId, audioSize: chatResponse.data.audio.length });
            
            // Decode base64 audio to binary
            const binaryString = atob(chatResponse.data.audio);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
              bytes[i] = binaryString.charCodeAt(i);
            }
            
            // Create audio blob from binary data
            const audioBlob = new Blob([bytes], { type: 'audio/mpeg' });
            const audioUrl = URL.createObjectURL(audioBlob);
            
            if (audioRef.current) {
              // Cancel browser speech — ElevenLabs audio is available
              if (window.speechSynthesis) { window.speechSynthesis.cancel(); detachAudio(); }
              audioRef.current.src = audioUrl;
              audioRef.current.volume = volume;

              await new Promise((resolve, reject) => {
                let settled = false;
                const settle = (fn) => (...args) => { if (!settled) { settled = true; fn(...args); } };
                const onEnded = settle(() => { cleanup(); detachAudio(); resolve(); });
                const onError = settle((e) => { cleanup(); detachAudio(); reject(e); });
                const cleanup = () => {
                  clearTimeout(safetyTimer);
                  audioRef.current?.removeEventListener('ended', onEnded);
                  audioRef.current?.removeEventListener('error', onError);
                  URL.revokeObjectURL(audioUrl);
                };
                // Safari may never fire ended/error on audio — safety timeout
                const safetyTimer = setTimeout(() => {
                  console.warn('[CHAT AUDIO] Safety timeout — ended event never fired (Safari bug)');
                  sessionLogger.log('tts_audio_timeout', { turnId, source: 'chat_audio' });
                  settle(() => { cleanup(); detachAudio(); resolve(); })();
                }, TIMEOUTS.AUDIO_PLAY);
                audioRef.current.addEventListener('ended', onEnded);
                audioRef.current.addEventListener('error', onError);
                console.log('[PLAYING CHAT AUDIO]');
                audioRef.current.play().catch(settle((e) => { cleanup(); detachAudio(); reject(e); }));
                attachAudioElement(audioRef.current);
              });
              
              audioPlayed = true;
              console.log('[VOICE] voice: elevenlabs (chat inline audio)');
              sessionLogger.log('tts_chat_audio_played', { turnId, engine: 'elevenlabs' });
            }
          } catch (chatAudioErr) {
            console.log('[CHAT AUDIO ERROR]', chatAudioErr);
            sessionLogger.log('tts_chat_audio_error', { turnId, error: chatAudioErr.message });
            // Fall through to speak endpoint
          }
        } else {
          console.log('[CHAT AUDIO NOT AVAILABLE] reason:', chatResponse.data.tts_error, {
            hasAudio: !!chatResponse.data.audio,
            audioFallback: chatResponse.data.audio_fallback,
            ttsError: chatResponse.data.tts_error  // 401=dead key, 402=quota, 429=rate-limit, "network"=timeout
          });
        }
        
        // If chat audio wasn't available or failed, fall back to /speak endpoint
        if (!audioPlayed) {
          sessionLogger.log('tts_speak_endpoint_fallback', { turnId });

          try {
            const ttsResponse = await axios.post(`${API}/speak`, {
              text: assistantMessage,
              voice: 'nova',
              turn_id: turnId,
              lang: lang || 'en',
            }, { timeout: TIMEOUTS.TTS, responseType: 'blob' });

            if (activeTurnRef.current !== turnId) return;

            // Backend returns JSON (not audio) when ElevenLabs fails (401, quota, etc.)
            const contentType = ttsResponse.headers?.['content-type'] || '';
            const isAudio = contentType.includes('audio/') && ttsResponse.data.size > 100;

            if (isAudio) {
              console.log('[VOICE] voice: elevenlabs (speak endpoint)');
              sessionLogger.log('tts_success', { turnId, engine: 'elevenlabs', audioSize: ttsResponse.data.size });

              const audioBlob = new Blob([ttsResponse.data], { type: 'audio/mpeg' });
              const audioUrl = URL.createObjectURL(audioBlob);

              if (audioRef.current) {
                // Cancel browser speech — ElevenLabs audio is better
                if (window.speechSynthesis) { window.speechSynthesis.cancel(); detachAudio(); }
                audioRef.current.src = audioUrl;
                audioRef.current.volume = volume;

                await new Promise((resolve, reject) => {
                  let settled = false;
                  const settle = (fn) => (...args) => { if (!settled) { settled = true; fn(...args); } };
                  const onEnded = settle(() => { cleanup(); detachAudio(); resolve(); });
                  const onError = settle((e) => { cleanup(); detachAudio(); reject(e); });
                  const cleanup = () => {
                    clearTimeout(safetyTimer);
                    audioRef.current?.removeEventListener('ended', onEnded);
                    audioRef.current?.removeEventListener('error', onError);
                    URL.revokeObjectURL(audioUrl);
                  };
                  // Safari may never fire ended/error on audio — safety timeout
                  const safetyTimer = setTimeout(() => {
                    console.warn('[SPEAK AUDIO] Safety timeout — ended event never fired (Safari bug)');
                    sessionLogger.log('tts_audio_timeout', { turnId, source: 'speak_endpoint' });
                    settle(() => { cleanup(); detachAudio(); resolve(); })();
                  }, TIMEOUTS.AUDIO_PLAY);
                  audioRef.current.addEventListener('ended', onEnded);
                  audioRef.current.addEventListener('error', onError);
                  audioRef.current.play().catch(settle((e) => { cleanup(); detachAudio(); reject(e); }));
                  attachAudioElement(audioRef.current);
                });
              }
              audioPlayed = true;
            } else {
              // /speak returned JSON fallback — ElevenLabs is down/expired
              console.log('[VOICE] /speak returned fallback JSON — ElevenLabs unavailable');
              sessionLogger.log('tts_elevenlabs_unavailable', { turnId, contentType });
            }
          } catch (speakErr) {
            if (speakErr.name === 'AbortError' || speakErr.name === 'CanceledError') throw speakErr;
            console.log('[VOICE] /speak endpoint error:', speakErr.message);
            sessionLogger.log('tts_speak_error', { turnId, error: speakErr.message });
          }
        }

        // ElevenLabs-only: if all server TTS failed, stay silent rather than
        // falling back to the browser's robotic computer voice.
        if (!audioPlayed) {
          console.log('[VOICE] ElevenLabs unavailable — no audio (browser fallback disabled)');
          sessionLogger.log('tts_no_voice', { turnId });
        }
      } catch (ttsErr) {
        console.log('[TTS ERROR]', ttsErr?.message);
        sessionLogger.log('tts_error', { turnId, error: ttsErr?.message });
      } })(); // end fire-and-forget TTS

      logTurn(turnId, 'TURN_COMPLETE');

    } catch (error) {
      clearTimeout(thinkingSafetyTimer);
      if (error.name === 'AbortError' || error.name === 'CanceledError') {
        logTurn(turnId, 'TURN_ABORTED');
        resetToIdle(turnId);
        return;
      }

      logTurn(turnId, 'TURN_ERROR', error.message);
      sessionLogger.log('turn_error', { turnId, error: error.message });
      setState(STATES.ERROR);
      setErrorMessage(error.message?.substring(0, 80) || 'Something went wrong');

      setTimeout(() => {
        if (activeTurnRef.current === turnId) resetToIdle(turnId);
      }, 2500);
      return;
    }
    // Note: resetToIdle already called after message shown (before TTS)
  }, [currentSessionId, volume, resetToIdle, sessionLogger, conversationStorage, caregiverStorage, recordInteraction, checkSessionEnd, navigate, appendTranscriptEvent, activeSession, loadSessionHistory, handleNewSession, contacts, primaryCaregiver, resolveContactIntent, ensureAudioContext, handleAudioEnded]);

  const processTranscription = useCallback(async (text, turnId) => {
    await submitMessage(text, turnId, 'voice');
  }, [submitMessage]);

  const recordAndTranscribeViaServer = useCallback(async (turnId) => {
    // Legacy fallback: record audio and send to backend /transcribe endpoint.
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true, deviceId: 'default' }
      });

      streamRef.current = stream;
      const audioTrack = stream.getAudioTracks()[0];
      if (!audioTrack) throw new Error('No audio track');
      audioTrack.enabled = true;

      let mimeType = 'audio/webm;codecs=opus';
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = 'audio/webm';
        if (!MediaRecorder.isTypeSupported(mimeType)) {
          mimeType = 'audio/mp4';
          if (!MediaRecorder.isTypeSupported(mimeType)) mimeType = '';
        }
      }

      const recorderOptions = mimeType ? { mimeType } : {};
      const mediaRecorder = new MediaRecorder(stream, recorderOptions);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) audioChunksRef.current.push(event.data);
      };

      mediaRecorder.onstop = async () => {
        logTurn(turnId, 'RECORDING_STOPPED');
        sessionLogger.log('mic_stop', { turnId, chunks: audioChunksRef.current.length });

        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
          streamRef.current = null;
        }

        if (activeTurnRef.current !== turnId) return;

        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType || 'audio/webm' });

        if (audioBlob.size < 1000) {
          setState(STATES.ERROR);
          setErrorMessage('No audio captured');
          sessionLogger.log('stt_error', { turnId, reason: 'audio_too_short' });
          setTimeout(() => resetToIdle(turnId), 2000);
          return;
        }

        setState(STATES.PROCESSING);
        abortControllerRef.current = new AbortController();

        try {
          sessionLogger.log('stt_start', { turnId });

          const formData = new FormData();
          const extension = mimeType.includes('mp4') ? 'mp4' : 'webm';
          formData.append('audio', audioBlob, `recording.${extension}`);
          formData.append('turn_id', turnId);

          const transcribeResponse = await axios.post(`${API}/transcribe`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            signal: abortControllerRef.current.signal,
            timeout: TIMEOUTS.STT
          });

          if (transcribeResponse.data.error) throw new Error(transcribeResponse.data.error);

          const transcribedText = transcribeResponse.data.text;
          if (!transcribedText?.trim()) throw new Error("I didn't catch that");

          sessionLogger.log('stt_success', { turnId, text: transcribedText.substring(0, 50) });
          await processTranscription(transcribedText, turnId);

        } catch (error) {
          if (error.name === 'AbortError' || error.name === 'CanceledError') return;

          sessionLogger.log('stt_error', { turnId, error: error.message });
          setState(STATES.ERROR);
          setErrorMessage(error.message?.substring(0, 80) || 'I had trouble hearing you');
          setTimeout(() => resetToIdle(turnId), 2500);
        }
      };

      mediaRecorder.onerror = (event) => {
        sessionLogger.log('recorder_error', { turnId, error: event.error?.message });
        resetToIdle(turnId);
      };

      mediaRecorder.start(250);
      setState(STATES.LISTENING);

    } catch (error) {
      sessionLogger.log('mic_error', { turnId, error: error.message });

      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }

      setState(STATES.ERROR);
      if (error.name === 'NotAllowedError') setErrorMessage('Please allow microphone access');
      else if (error.name === 'NotFoundError') setErrorMessage('No microphone found');
      else setErrorMessage('Could not access microphone');

      setTimeout(() => resetToIdle(turnId), 3000);
    }
  }, [ensureAudioContext, processTranscription, resetToIdle, sessionLogger]);

  const startRecording = useCallback(async (turnId) => {
    try {
      logTurn(turnId, 'RECORDING_START');
      sessionLogger.log('mic_start', { turnId });

      await ensureAudioContext();

      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (!SpeechRecognition) {
        // Browser does not support Web Speech API; fall back to backend transcription.
        logTurn(turnId, 'SPEECH_RECOGNITION_UNSUPPORTED');
        sessionLogger.log('stt_browser_unsupported', { turnId });
        return await recordAndTranscribeViaServer(turnId);
      }

      const recognition = new SpeechRecognition();
      recognition.continuous = true;   // Keep listening until user taps again
      recognition.interimResults = true;
      recognition.lang = lang === 'fr' ? 'fr-CA' : 'en-US';
      recognition.maxAlternatives = 1;

      // WALKIE-TALKIE: Accumulate all speech until user taps again
      // Do NOT auto-send on isFinal — that fires on every pause
      let accumulatedTranscript = '';
      let lastInterimTranscript = '';
      let stoppedByUser = false;

      recognition.onstart = () => {
        logTurn(turnId, 'SPEECH_RECOGNITION_STARTED');
        sessionLogger.log('stt_browser_start', { turnId });
      };

      recognition.onresult = (event) => {
        // Rebuild full transcript from all results
        let fullTranscript = '';
        let currentInterim = '';
        for (let i = 0; i < event.results.length; i++) {
          if (event.results[i].isFinal) {
            fullTranscript += event.results[i][0].transcript;
          } else {
            currentInterim += event.results[i][0].transcript;
          }
        }
        accumulatedTranscript = fullTranscript;
        lastInterimTranscript = currentInterim;
        // Store on recognition so stopRecording can access it
        recognition._accumulatedTranscript = (fullTranscript + ' ' + currentInterim).trim();
      };

      recognition.onerror = (event) => {
        logTurn(turnId, 'SPEECH_RECOGNITION_ERROR', event.error);
        sessionLogger.log('stt_browser_error', { turnId, error: event.error });

        // "no-speech" and "aborted" are not real errors in continuous mode
        if (event.error === 'no-speech' || event.error === 'aborted') {
          return;  // Silently ignore — stay in LISTENING state
        }

        // Real errors: microphone denied, not found, etc.
        setState(STATES.ERROR);
        setErrorMessage('I had trouble hearing you');
        setTimeout(() => resetToIdle(turnId), 2500);
      };

      recognition.onend = async () => {
        logTurn(turnId, 'SPEECH_RECOGNITION_ENDED');
        sessionLogger.log('stt_browser_end', { turnId });

        // Only process if user explicitly tapped stop (walkie-talkie)
        if (!stoppedByUser) {
          // Browser killed recognition (timeout, focus loss, etc.)
          // Restart it to keep listening
          if (recognitionRef.current === recognition) {
            try { recognition.start(); } catch (_) {
              // Can't restart — process what we have
              const text = (accumulatedTranscript + ' ' + lastInterimTranscript).trim();
              if (text) {
                setState(STATES.PROCESSING);
                await processTranscription(text, turnId);
              } else {
                setState(STATES.ERROR);
                setErrorMessage("I didn't catch that — tap the orb and try again");
                setTimeout(() => resetToIdle(turnId), 2500);
              }
            }
          }
          return;
        }

        // User tapped stop — send accumulated transcript
        const text = (accumulatedTranscript + ' ' + lastInterimTranscript).trim();
        if (text) {
          logTurn(turnId, 'SPEECH_RECOGNITION_RESULT', text.substring(0, 50));
          sessionLogger.log('stt_browser_success', { turnId, text: text.substring(0, 50) });
          setState(STATES.PROCESSING);
          await processTranscription(text, turnId);
        } else {
          // No transcript at all — fall back to server transcription
          recognitionRef.current = null;
          await recordAndTranscribeViaServer(turnId);
        }
      };

      // Mark stop as user-initiated from outside
      recognition._stopByUser = () => { stoppedByUser = true; };

      recognitionRef.current = recognition;
      recognition.start();
      setState(STATES.LISTENING);

      // 60-second safety maximum — generous walkie-talkie window
      const silenceTimeout = setTimeout(() => {
        if (recognitionRef.current && !stoppedByUser) {
          logTurn(turnId, 'MAX_LISTEN_TIMEOUT');
          sessionLogger.log('stt_max_timeout', { turnId });
          stoppedByUser = true;  // Treat timeout as intentional stop
          try { recognitionRef.current.stop(); } catch (_) {}
        }
      }, 60000);
      // Store timeout so we can clear it on manual stop
      recognition._silenceTimeout = silenceTimeout;

    } catch (error) {
      sessionLogger.log('stt_browser_error', { turnId, error: error.message });

      setState(STATES.ERROR);
      if (error.message.includes('not supported')) {
        setErrorMessage('Speech recognition not supported in this browser');
      } else if (error.name === 'NotAllowedError') {
        setErrorMessage('Please allow microphone access');
      } else if (error.name === 'NotFoundError') {
        setErrorMessage('No microphone found');
      } else {
        setErrorMessage('Could not start speech recognition');
      }

      setTimeout(() => resetToIdle(turnId), 3000);
    }
  }, [ensureAudioContext, processTranscription, resetToIdle, sessionLogger, recordAndTranscribeViaServer, state]);

  const stopRecording = useCallback((turnId) => {
    if (recognitionRef.current) {
      // Clear silence timeout
      if (recognitionRef.current._silenceTimeout) {
        clearTimeout(recognitionRef.current._silenceTimeout);
      }
      // Signal user-initiated stop so onend knows to process
      if (recognitionRef.current._stopByUser) {
        recognitionRef.current._stopByUser();
      }
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
  }, []);

  const handleOrbTap = useCallback(async () => {
    const turnId = generateTurnId();
    const isRecordingActive = recognitionRef.current !== null;

    // Clear ended session state on any tap
    if (sessionJustEnded) setSessionJustEnded(false);

    // ── WALKIE-TALKIE STATE MACHINE ──────────────────────────────────
    // TAP 1 (IDLE): Start listening
    // TAP 2 (LISTENING): Stop listening → process speech
    // TAP during SPEAKING: Stop audio, go back to idle
    // TAP during PROCESSING: Safety reset to idle

    if (state === STATES.LISTENING || isRecordingActive) {
      // ── SECOND TAP: Stop recording and send to AI ──
      setState(STATES.PROCESSING);
      stopRecording(activeTurnRef.current);

    } else if (state === STATES.IDLE || sessionJustEnded) {
      // ── FIRST TAP: Start listening ──
      // Speak pending reminder first if one is waiting
      if (pendingReminders.length > 0 && !reminderDismissed) {
        const reminder = pendingReminders[0];
        const reminderText = `Reminder from your caregiver: ${reminder.title}. ${reminder.body}`;
        try {
          const ttsRes = await axios.post(`${API}/speak`, { text: reminderText, voice: 'nova' },
            { responseType: 'blob', timeout: 15000 });
          if (ttsRes.data) {
            const url = URL.createObjectURL(new Blob([ttsRes.data], { type: 'audio/mpeg' }));
            await ensureAudioContext();
            if (audioRef.current) {
              audioRef.current.src = url;
              audioRef.current.volume = volume;
              await new Promise(resolve => {
                audioRef.current.onended = resolve;
                audioRef.current.play().catch(resolve);
              });
            }
          }
        } catch (_) {}
        await handleAcknowledgeReminder(reminder.id);
      }
      activeTurnRef.current = turnId;
      isProcessingRef.current = true;
      await startRecording(turnId);

    } else if (state === STATES.SPEAKING) {
      // ── TAP during playback: stop audio, reset to idle ──
      resetToIdle(activeTurnRef.current);

    } else if (state === STATES.PROCESSING) {
      // ── Safety net: rescue from stuck processing state ──
      resetToIdle(activeTurnRef.current);
    }
  }, [state, sessionJustEnded, startRecording, stopRecording, resetToIdle, pendingReminders, reminderDismissed, volume, ensureAudioContext, handleAcknowledgeReminder]);

  const handleKeyboardSubmit = useCallback(async () => {
    console.log('[TEXT] submit triggered', { text: keyboardInput?.substring(0, 50), isProcessing: isProcessingRef.current });
    if (!keyboardInput.trim() || isProcessingRef.current) {
      console.log('[TEXT] submit blocked', { empty: !keyboardInput.trim(), processing: isProcessingRef.current });
      return;
    }

    const turnId = generateTurnId();
    const text = keyboardInput.trim();
    setKeyboardInput('');
    setShowKeyboard(false);

    activeTurnRef.current = turnId;
    isProcessingRef.current = true;
    console.log('[TEXT] calling submitMessage', { turnId, textLen: text.length });
    await submitMessage(text, turnId, 'keyboard');
    console.log('[TEXT] submitMessage returned', { turnId });
  }, [keyboardInput, submitMessage]);

  // Stop any currently playing side panel audio
  const stopSidePanelAudio = useCallback(() => {
    // v3.9.7: Invalidate any in-flight TTS requests
    sidePanelTtsRequestIdRef.current++;
    
    if (sidePanelAudioElementRef.current) {
      sidePanelAudioElementRef.current.pause();
      sidePanelAudioElementRef.current.currentTime = 0;
    }
    setIsVoicePlaying(false);
  }, []);

  // Play side panel TTS (with auto-stop of previous audio)
  // Cross-scan conflicts are handled by activeScorecardWsRef — old WS messages blocked.
  // stopSidePanelAudio() stops any playing audio before starting new playback.
  const playSidePanelTTS = useCallback(async (voiceScript, forcePlay = false) => {
    // Don't play if muted (unless forcePlay for manual replay)
    if (isSidePanelMuted && !forcePlay) {
      return;
    }

    if (!voiceScript) return;

    // Stop any previous audio first
    if (sidePanelAudioElementRef.current) {
      sidePanelAudioElementRef.current.pause();
      sidePanelAudioElementRef.current.currentTime = 0;
    }

    try {
      setIsVoicePlaying(true);

      const ttsResponse = await axios.post(`${API}/speak`, {
        text: voiceScript,
        voice: 'nova',
        lang: lang || 'en',
      }, {
        timeout: getTtsTimeout(voiceScript),
        responseType: 'blob',
        validateStatus: status => status < 500
      });

      if (ttsResponse.status === 200 && ttsResponse.data && ttsResponse.data.size > 100) {
        const audioBlob = new Blob([ttsResponse.data], { type: 'audio/mpeg' });
        const audioUrl = URL.createObjectURL(audioBlob);

        // Clean up previous URL
        if (sidePanelAudioRef.current) {
          URL.revokeObjectURL(sidePanelAudioRef.current);
        }
        sidePanelAudioRef.current = audioUrl;

        // Create or reuse dedicated audio element
        if (!sidePanelAudioElementRef.current) {
          sidePanelAudioElementRef.current = new Audio();
          sidePanelAudioElementRef.current.addEventListener('ended', () => {
            setIsVoicePlaying(false);
          });
          sidePanelAudioElementRef.current.addEventListener('error', () => {
            setIsVoicePlaying(false);
          });
        }

        sidePanelAudioElementRef.current.src = audioUrl;
        sidePanelAudioElementRef.current.volume = volume;

        await sidePanelAudioElementRef.current.play();
      } else {
        // ElevenLabs-only: backend TTS failed — stay silent, no computer voice.
        console.log('[TTS] Backend returned', ttsResponse.status, '— no audio (browser fallback disabled)');
        setIsVoicePlaying(false);
        return;
      }
    } catch (err) {
      // ElevenLabs-only: backend TTS error — stay silent, no computer voice.
      console.log('[TTS] Backend error — no audio (browser fallback disabled)');
      setIsVoicePlaying(false);
      return;
    }
  }, [API, isSidePanelMuted, volume, lang]);

  // Keep refs synced for WebSocket closure access
  useEffect(() => { isSidePanelMutedRef.current = isSidePanelMuted; }, [isSidePanelMuted]);
  useEffect(() => { volumeRef.current = volume; }, [volume]);
  useEffect(() => { playSidePanelTTSRef.current = playSidePanelTTS; }, [playSidePanelTTS]);

  // v3.5: Website Check Mode handlers
  const normalizeWebsiteInput = useCallback((input) => {
    // Trim, lowercase, strip https://, http://, www.
    let normalized = input.trim().toLowerCase();
    normalized = normalized.replace(/^https?:\/\//, '');
    normalized = normalized.replace(/^www\./, '');
    // Remove trailing slashes and paths
    normalized = normalized.split('/')[0];
    return normalized;
  }, []);

  const handleWebsiteCheckSubmit = useCallback(async () => {
    const rawInput = websiteCheckInput.trim();
    if (!rawInput || isCheckingWebsite) return;
    
    const normalized = normalizeWebsiteInput(rawInput);
    
    // If no dot (brand only), ask for exact domain
    if (!normalized.includes('.')) {
      setWebsiteCheckConfirm({
        hostname: normalized,
        needsConfirm: true,
        isBrandOnly: true,
        message: `"${normalized}" looks like a brand name. Can you type the exact website address (e.g., ${normalized}.com)?`
      });
      return;
    }
    
    // Show confirmation chip for ambiguity check
    setWebsiteCheckConfirm({
      hostname: normalized,
      needsConfirm: true,
      isBrandOnly: false,
      message: null
    });
  }, [websiteCheckInput, isCheckingWebsite, normalizeWebsiteInput]);

  // v3.9.6: Single fetch helper for website panel (one source of truth)
  // All website panel fetches MUST use this function (requirement B)
  const fetchRequestIdRef = useRef(0); // Concurrency guard: ignore stale responses
  
  const fetchWebsitePanel = useCallback(async (hostname, questionOverride) => {
    console.log('[SCAN] fetchWebsitePanel called for:', hostname);
    if (!hostname) return null;

    // Check sessionStorage cache (2-minute TTL, final results only)
    const cacheKey = `panel_${hostname}_${lang || 'en'}`;
    try {
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        const { data, ts } = JSON.parse(cached);
        if (Date.now() - ts < 2 * 60 * 1000 && data.is_final) {
          console.log('[SCAN] Cache hit for', hostname);
          setSidePanelData(data);
          setIsWebsiteSidePanelOpen(true);
          setState(STATES.IDLE);
          setIsFetchingWebsitePanel(false);
          return data;
        }
      }
    } catch (_) {}

    // Increment request ID for concurrency guard
    const thisRequestId = ++fetchRequestIdRef.current;

    setIsFetchingWebsitePanel(true);
    setSidePanelError(null);
    setPanelActionState({
      saving: false, saved: false,
      flagging: false, flagged: false,
      askingCaregiver: false, askedCaregiver: false,
      notifyingCaregiver: false, notifiedCaregiver: false,
    });
    setState(STATES.PROCESSING);
    stopSidePanelAudio();
    setSidePanelData(null);
    setRiskResearchData(null);

    try {
      const response = await axios.post(`${API}/scan/fast`, {
        hostname,
        source: 'WEBAPP',
        lang: lang || 'en',
      }, { timeout: 10000 });

      // Ignore stale response
      if (thisRequestId !== fetchRequestIdRef.current) {
        setState(STATES.IDLE);
        return null;
      }

      const panelData = response.data;
      console.log('[SCAN] Fast scan result:', panelData.verdict, panelData.confidence, panelData.score);

      // Cache final results
      if (panelData.is_final) {
        try {
          sessionStorage.setItem(cacheKey, JSON.stringify({ data: panelData, ts: Date.now() }));
        } catch (_) {}
      }

      // Map fast_scan fields to FullSiteReport expected fields
      const mappedData = {
        ...panelData,
        ai_looks_good: panelData.looks_good || [],
        ai_watch_out_for: panelData.watch_out_for || [],
        ai_site_identity: panelData.site_identity || null,
        one_liner: panelData.site_identity || null,
        is_preliminary: false,
        ai_status: 'complete',
      };

      // v5.3.3: When Tier 2 will auto-trigger, show Investigating state
      // instead of committing to the Tier 1 verdict
      const willAutoDeep = panelData.auto_deep_scan
          && !['unknown_site', 'confirm_address', 'probably_typo'].includes(panelData.typo_gate?.action);

      if (willAutoDeep) {
        // Set investigating state — suppress Tier 1 committed display
        setSidePanelData({
          ...mappedData,
          _investigating: true,  // FullSiteReport checks this flag
        });
      } else {
        setSidePanelData(mappedData);
      }
      setIsWebsiteSidePanelOpen(true);

      // Close website check input panel
      setShowWebsiteCheckPanel(false);
      setWebsiteCheckInput('');
      setWebsiteCheckConfirm(null);

      // Play voice immediately — but skip when investigating (Tier 2 will speak)
      if (!willAutoDeep && !isSidePanelMuted && volume > 0 && panelData.voice_script) {
        // Don't play voice for dead_domain typo gate (no scorecard shown)
        if (panelData.typo_gate?.action !== 'dead_domain') {
          playSidePanelTTS(panelData.voice_script);
        }
      }

      // Auto-trigger Tier 2
      if (willAutoDeep) {
        triggerDeepScan(hostname, mappedData);
      }

      setState(STATES.IDLE);
      setErrorMessage('');
      return mappedData;

    } catch (err) {
      if (thisRequestId === fetchRequestIdRef.current) {
        setSidePanelError('Could not check that website. Try again?');
        setState(STATES.IDLE);
      }
      return null;
    } finally {
      if (thisRequestId === fetchRequestIdRef.current) {
        setIsFetchingWebsitePanel(false);
      }
    }
  }, [API, stopSidePanelAudio, isSidePanelMuted, volume, playSidePanelTTS, lang]);

  // Patience voice — reassures user every 15 seconds during deep scan (uses ElevenLabs TTS)
  const startPatienceVoice = useCallback(() => {
    if (patienceTimerRef.current) clearInterval(patienceTimerRef.current);
    patienceCountRef.current = 0;
    const msgs = [
      "Thank you for your patience. We're still checking \u2014 almost halfway done.",
      "Still looking into it. Thank you for waiting.",
      "Almost done. Just a few more seconds.",
    ];
    patienceTimerRef.current = setInterval(() => {
      if (!isSidePanelMutedRef.current || !volumeRef.current) return;
      const idx = Math.min(patienceCountRef.current, msgs.length - 1);
      patienceCountRef.current++;
      if (playSidePanelTTSRef.current) {
        playSidePanelTTSRef.current(msgs[idx]);
      }
    }, 15000);
  }, []);

  const stopPatienceVoice = useCallback(() => {
    if (patienceTimerRef.current) {
      clearInterval(patienceTimerRef.current);
      patienceTimerRef.current = null;
    }
    patienceCountRef.current = 0;
    stopSidePanelAudio();
  }, [stopSidePanelAudio]);

  // Tier 2 deep scan — synchronized sequential pipeline
  const triggerDeepScan = useCallback(async (hostname, tier1Data) => {
    if (isDeepScanning) return;
    if (tier1Data?.tier2_loaded) return;
    setIsDeepScanning(true);

    console.log('[DEEP_SCAN] Starting for:', hostname);

    // Show progress — no text banner for confirmed addresses, just progress bar
    const wasConfirm = tier1Data?.typo_gate?.action === 'confirm_address'
                       || tier1Data?.typo_gate?.action === 'confirmed';

    setSidePanelData(prev => prev ? {
      ...prev,
      auto_deep_scan: true,
      communication: wasConfirm
        ? { banner: null, deep_scan_cta: 'hidden' }
        : { banner: { type: 'warning', text: 'Looking deeper \u2014 this takes about 15 seconds' }, deep_scan_cta: 'hidden' },
    } : prev);

    // Confirm ack voice (for UNKNOWN after user confirms)
    if (wasConfirm && !isSidePanelMutedRef.current && volumeRef.current > 0) {
      stopSidePanelAudio();
      playSidePanelTTS("Got it. We're running a thorough check now. Please hold on and don't enter any information just yet.");
    }

    // Start patience voice (every 15 seconds)
    startPatienceVoice();

    try {
      const response = await axios.post(`${API}/scan/deep`, {
        hostname,
        tier1_data: tier1Data || {},
      }, { timeout: 30000 });

      // Stop patience before final voice
      stopPatienceVoice();

      const deepData = response.data;
      console.log('[DEEP_SCAN] Complete:', deepData.verdict, deepData.score, deepData.latency_ms + 'ms');

      if (deepData.status === 'error') {
        // v5.3.4: Degraded state instead of falling back to Tier 1 verdict
        setSidePanelData(prev => prev ? {
          ...prev,
          auto_deep_scan: false,
          _investigating: false,
          _degraded: true,
          tier2_failed: true,
          communication: {
            banner: { type: 'danger', text: 'HAVN could not fully check this site. Proceed with caution.' },
            deep_scan_cta: 'hidden',
          },
        } : prev);
        return;
      }

      // ONE smooth update — score, identity, flags, voice all arrive together
      setSidePanelData(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          tier2_loaded: true,
          auto_deep_scan: false,
          _investigating: false,
          score_pending: false,
          score: deepData.score ?? prev.preliminary_score ?? prev.score,
          verdict: deepData.verdict ?? prev.verdict,
          typo_gate: { ...prev.typo_gate, action: 'completed' },
          ai_site_identity: deepData.ai_site_identity || prev.ai_site_identity,
          site_identity: deepData.site_identity || prev.site_identity,
          one_liner: deepData.ai_site_identity || prev.one_liner,
          ai_looks_good: deepData.ai_looks_good || prev.ai_looks_good,
          ai_watch_out_for: deepData.ai_watch_out_for || prev.ai_watch_out_for,
          looks_good: deepData.ai_looks_good || prev.looks_good,
          watch_out_for: deepData.ai_watch_out_for || prev.watch_out_for,
          ai_what_people_report: deepData.ai_what_people_report || prev.ai_what_people_report,
          ai_before_you_proceed: deepData.ai_before_you_proceed || prev.ai_before_you_proceed,
          site_category_badge: deepData.site_category_badge || prev.site_category_badge,
          voice_script: deepData.voice_script || prev.voice_script,
          confidence: deepData.confidence || prev.confidence,
          confidence_label: deepData.confidence_label || prev.confidence_label,
          research_results_count: deepData.research_results_count || prev.research_results_count,
          communication: { banner: null, deep_scan_cta: 'hidden' },
          deep_scan_cta: 'hidden',
        };
      });

      // Play final voice AFTER card update (always in sync)
      if (deepData.voice_script && !isSidePanelMutedRef.current && volumeRef.current > 0) {
        stopSidePanelAudio();
        setTimeout(() => {
          playSidePanelTTS(deepData.voice_script);
        }, 300);
      }

      // Cache
      try {
        setSidePanelData(latest => {
          if (latest?.domain) {
            sessionStorage.setItem(`panel_${latest.domain}`, JSON.stringify({
              data: latest, ts: Date.now(),
            }));
          }
          return latest;
        });
      } catch (_) {}

    } catch (err) {
      stopPatienceVoice();
      console.error('[DEEP_SCAN] Failed:', err.message);
      // v5.3.4: Degraded state instead of falling back to Tier 1 verdict
      setSidePanelData(prev => prev ? {
        ...prev,
        auto_deep_scan: false,
        _investigating: false,
        _degraded: true,
        tier2_failed: true,
        communication: {
          banner: { type: 'danger', text: 'HAVN could not fully check this site. Proceed with caution.' },
          deep_scan_cta: 'hidden',
        },
      } : prev);
    } finally {
      stopPatienceVoice();
      setIsDeepScanning(false);
    }
  }, [API, stopSidePanelAudio, playSidePanelTTS, isDeepScanning, startPatienceVoice, stopPatienceVoice]);

  const handleWebsiteCheckConfirm = useCallback(async () => {
    if (!websiteCheckConfirm?.hostname || isCheckingWebsite) return;
    
    const hostname = websiteCheckConfirm.hostname;
    const wasInResearchFlow = isInWebsiteResearchFlow; // Capture before resetting
    setIsCheckingWebsite(true);
    setWebsiteCheckConfirm(null);
    setIsInWebsiteResearchFlow(false); // Reset the flow flag
    
    try {
      // Close the input panel
      setShowWebsiteCheckPanel(false);
      setWebsiteCheckInput('');
      
      // v3.9.6: Open panel BEFORE fetch so it shows loading state
      setLastCheckedHostname(hostname);
      setIsWebsiteSidePanelOpen(true);
      
      const panelData = await fetchWebsitePanel(hostname);
      
      // If this came from voice conversation flow, append result to messages and speak
      if (wasInResearchFlow && panelData) {
        // Build a short message for the transcript
        let summaryMessage = `I checked **${panelData.domain}**.`;
        if (panelData.brand_name) {
          summaryMessage = `I checked **${panelData.brand_name}** (${panelData.domain}).`;
        }
        summaryMessage += ` ${panelData.one_liner}`;
        
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: summaryMessage,
          panelData: panelData
        }]);
        conversationStorage.add('assistant', summaryMessage, currentSessionId);
        appendTranscriptEvent('website_check', 'assistant', summaryMessage, { hostname: panelData?.domain });
        recordInteraction();
        
        // Speak: waiting message for preliminary, real voice for final
        if (volume > 0) {
          if (panelData.is_preliminary) {
            await playSidePanelTTS("We're checking this site for you. Please hold on while we complete our research.", false);
          } else if (panelData.voice_script) {
            await playSidePanelTTS(panelData.voice_script, false);
          }
        }
      }
      
    } catch (error) {
      console.error('[WebsiteCheck] Error:', error);
      setState(STATES.ERROR);
      setErrorMessage('Could not check that website. Try again?');
      // Keep panel open but show error state
      setTimeout(() => {
        setState(STATES.IDLE);
        setErrorMessage('');
      }, 3000);
    } finally {
      setIsCheckingWebsite(false);
    }
  }, [websiteCheckConfirm, isCheckingWebsite, isInWebsiteResearchFlow, fetchWebsitePanel, currentSessionId, volume, playSidePanelTTS, appendTranscriptEvent, recordInteraction]);

  const handleWebsiteCheckEdit = useCallback(() => {
    // Put the hostname back in input for editing
    if (websiteCheckConfirm?.hostname) {
      setWebsiteCheckInput(websiteCheckConfirm.hostname);
    }
    setWebsiteCheckConfirm(null);
  }, [websiteCheckConfirm]);

  const handleOpenWebsiteCheck = useCallback(() => {
    setShowWebsiteCheckPanel(true);
    setShowKeyboard(false);
    setShowVolume(false);
    setShowTranscript(false);
    setWebsiteCheckInput('');
    setWebsiteCheckConfirm(null);
  }, []);

  const handleCloseWebsiteCheck = useCallback(() => {
    setShowWebsiteCheckPanel(false);
    setWebsiteCheckInput('');
    setWebsiteCheckConfirm(null);
  }, []);

  // Replay voice from side panel (manual, ignores mute)
  const handleReplayVoice = useCallback(async () => {
    if (!sidePanelData?.voice_script || isVoicePlaying) return;
    
    // Force play even if muted (user explicitly clicked replay)
    await playSidePanelTTS(sidePanelData.voice_script, true);
  }, [sidePanelData, isVoicePlaying, playSidePanelTTS]);

  // Toggle mute for side panel
  const handleToggleSidePanelMute = useCallback(() => {
    setIsSidePanelMuted(prev => {
      const newValue = !prev;
      try {
        localStorage.setItem('havn_side_panel_muted', String(newValue));
      } catch (err) {
        // localStorage unavailable - mute state still works in memory
      }
      return newValue;
    });
  }, []);

  // v4.5.24: Save/unsave site to favorites (toggle)
  const handleSaveFavorite = useCallback(async () => {
    if (!sidePanelData || panelActionState.saving) return;
    const domain = sidePanelData.domain;
    const alreadySaved = savedFavorites.has(domain);

    if (alreadySaved) {
      // Unsave
      setPanelActionState(p => ({ ...p, saving: true }));
      try {
        const userId = getUserId();
        await axios.delete(`${API}/favorites/${encodeURIComponent(userId)}/${encodeURIComponent(domain)}`);
        setSavedFavorites(prev => { const next = new Set(prev); next.delete(domain); return next; });
        setPanelActionState(p => ({ ...p, saving: false, saved: false }));
        sessionLogger.log('site_unsaved_favorite', { domain });
      } catch (err) {
        setPanelActionState(p => ({ ...p, saving: false }));
      }
    } else {
      // Save
      const verdict = sidePanelData.verdict;
      if (verdict !== 'SAFE' && verdict !== 'SAFE_WITH_CAUTION') return;
      setPanelActionState(p => ({ ...p, saving: true }));
      try {
        const userId = getUserId();
        await axios.post(`${API}/favorites`, {
          user_id: userId,
          domain,
          display_name: sidePanelData.brand_name || domain,
          verdict_at_save: verdict,
          score_at_save: sidePanelData.score || 0,
          saved_from: 'webapp'
        });
        setSavedFavorites(prev => new Set([...prev, domain]));
        setPanelActionState(p => ({ ...p, saving: false, saved: true }));
        sessionLogger.log('site_saved_favorite', { domain });
      } catch (err) {
        setPanelActionState(p => ({ ...p, saving: false }));
      }
    }
  }, [sidePanelData, panelActionState, savedFavorites, sessionLogger]);

  // v4.5.24: Flag site to community list (only when CAUTION/DANGER)
  const handleCommunityFlag = useCallback(async () => {
    if (!sidePanelData || panelActionState.flagging || panelActionState.flagged) return;
    const verdict = sidePanelData.verdict;
    const flaggableVerdicts = new Set(['CAUTION', 'CAUTION_HARD', 'DANGER']);
    if (!flaggableVerdicts.has(verdict)) return;

    setPanelActionState(p => ({ ...p, flagging: true }));
    try {
      const userId = getUserId();
      // Map verdict to category
      let category = 'other';
      const bullets = (sidePanelData.bullets || sidePanelData.evidenceBullets || []).join(' ').toLowerCase();
      if (/tech.support|windows|microsoft|mcafee/.test(bullets)) category = 'tech_support_scam';
      else if (/phish|impersonat/.test(bullets)) category = 'phishing';
      else if (/casino|gambling|lottery/.test(bullets)) category = 'financial_scam';

      const res = await axios.post(`${API}/community/flag`, {
        user_id: userId,
        domain: sidePanelData.domain,
        category,
        verdict_at_flag: verdict
      });
      setFlaggedDomains(prev => new Set([...prev, sidePanelData.domain]));
      setPanelActionState(p => ({ ...p, flagging: false, flagged: true }));
      sessionLogger.log('site_community_flagged', { domain: sidePanelData.domain, count: res.data?.flag_count });
    } catch (err) {
      setPanelActionState(p => ({ ...p, flagging: false }));
    }
  }, [sidePanelData, panelActionState, sessionLogger]);

  // v4.5.24: Ask caregiver for opinion on this site
  const handleAskCaregiver = useCallback(async () => {
    if (!sidePanelData || !primaryCaregiver || panelActionState.askingCaregiver) return;
    setPanelActionState(p => ({ ...p, askingCaregiver: true }));
    try {
      const userId = getUserId();
      await axios.post(`${API}/caregiver/ask-opinion`, {
        user_id: userId,
        domain: sidePanelData.domain,
        current_verdict: sidePanelData.verdict,
      });
      setPanelActionState(p => ({ ...p, askingCaregiver: false, askedCaregiver: true }));
      sessionLogger.log('caregiver_asked_opinion', { domain: sidePanelData.domain });
    } catch {
      setPanelActionState(p => ({ ...p, askingCaregiver: false }));
    }
  }, [sidePanelData, primaryCaregiver, panelActionState, sessionLogger]);

  // v4.5.24: Notify caregiver of a risky site
  const handleNotifyCaregiver = useCallback(async () => {
    if (!sidePanelData || !primaryCaregiver || panelActionState.notifyingCaregiver) return;
    setPanelActionState(p => ({ ...p, notifyingCaregiver: true }));
    try {
      const userId = getUserId();
      await axios.post(`${API}/caregiver/notify-risk`, {
        user_id: userId,
        domain: sidePanelData.domain,
        risk_level: sidePanelData.verdict,
        category: 'other'
      });
      setPanelActionState(p => ({ ...p, notifyingCaregiver: false, notifiedCaregiver: true }));
      sessionLogger.log('caregiver_notified_risk', { domain: sidePanelData.domain });
    } catch {
      setPanelActionState(p => ({ ...p, notifyingCaregiver: false }));
    }
  }, [sidePanelData, primaryCaregiver, panelActionState, sessionLogger]);

  // Close side panel handler - stops audio and cleans up
  const handleCloseSidePanel = useCallback(() => {
    // Reset panel action state
    setPanelActionState({
      saving: false, saved: false,
      flagging: false, flagged: false,
      askingCaregiver: false, askedCaregiver: false,
      notifyingCaregiver: false, notifiedCaregiver: false,
    });

    // Stop patience voice
    if (patienceTimerRef.current) {
      clearInterval(patienceTimerRef.current);
      patienceTimerRef.current = null;
    }
    if (window.speechSynthesis) window.speechSynthesis.cancel();

    // Stop audio immediately
    stopSidePanelAudio();
    
    // Clean up audio URL
    if (sidePanelAudioRef.current) {
      URL.revokeObjectURL(sidePanelAudioRef.current);
      sidePanelAudioRef.current = null;
    }
    
    // Abort any pending API calls for this scan
    if (fetchRequestIdRef.current) {
      fetchRequestIdRef.current = null;
    }

    // Reset all state
    setSidePanelError(null);
    setIsFetchingWebsitePanel(false);
    setIsWebsiteSidePanelOpen(false);
    setSidePanelData(null);
    setState(STATES.IDLE);
    setErrorMessage('');

    // Reset website check input state so next conversation starts clean
    setShowWebsiteCheckPanel(false);
    setWebsiteCheckInput('');
    setWebsiteCheckConfirm(null);
    setLastCheckedHostname(null);
  }, [stopSidePanelAudio]);

  // ── Typo gate handlers ─────────────────────────────────────────────────
  const handleTypoGateConfirm = useCallback(async (hostname) => {
    console.log('[TYPO_GATE] User confirmed:', hostname);
    setSidePanelData(prev => prev ? {
      ...prev,
      typo_gate: { ...prev.typo_gate, action: 'confirmed' },
      auto_deep_scan: true,
    } : prev);
    triggerDeepScan(hostname, sidePanelData);
  }, [sidePanelData, triggerDeepScan]);

  const handleTypoGateReject = useCallback(() => {
    console.log('[TYPO_GATE] User wants to fix URL');
    handleCloseSidePanel();
    setShowWebsiteCheckPanel(true);
    setWebsiteCheckInput('');
  }, [handleCloseSidePanel]);

  // ── Lazy full report handler ────────────────────────────────────────────
  const handleGenerateFullReport = useCallback(async () => {
    if (isGeneratingReport || !sidePanelData?.domain) return;
    setIsGeneratingReport(true);

    console.log('[REPORT] Generating full report for:', sidePanelData.domain);

    try {
      const response = await axios.post(`${API}/scan/report`, {
        hostname: sidePanelData.domain,
        tier1_data: sidePanelData,
        scorecard_data: sidePanelData,
      }, { timeout: 35000 });

      if (response.data?.ok && response.data?.report) {
        console.log('[REPORT] Report ready, merging');
        setSidePanelData(prev => prev ? {
          ...prev,
          ai_full_report: { ...response.data.report, data_source: 'claude_ai' },
        } : prev);
      } else {
        console.error('[REPORT] Generation failed:', response.data?.error);
        alert('Could not generate the detailed report. Please try again.');
      }
    } catch (err) {
      console.error('[REPORT] Request failed:', err.message);
      alert('Report generation timed out. Please try again.');
    } finally {
      setIsGeneratingReport(false);
    }
  }, [API, sidePanelData, isGeneratingReport]);

  // ============================================
  // GREETING SYSTEM - with debug and force mode
  // ============================================
  
  // Play greeting TTS
  const playGreetingTTS = useCallback(async () => {
    // Synchronous ref guard — wins the race between the mount timer and the
    // first user interaction so the greeting never plays twice. forceGreet
    // (debug ?greet=1) intentionally bypasses both guards to allow replay.
    if (greetingTtsStartedRef.current && !forceGreet) return;
    if (ttsAttempted && !forceGreet) return;
    greetingTtsStartedRef.current = true;

    console.log('[Greeting] tts starting');
    setTtsAttempted(true);
    setTtsStatus('started');
    
    try {
      const response = await axios.post(`${API}/speak`, {
        text: getGreetingText(authUser, t),
        voice: 'nova'
      }, {
        responseType: 'blob',
        timeout: TIMEOUTS.TTS
      });
      
      const audioBlob = response.data;
      const audioUrl = URL.createObjectURL(audioBlob);

      // iOS fix: REUSE the primed element instead of constructing a fresh
      // Audio here — an element created after this awaited fetch is outside
      // any user gesture and WebKit will refuse its .play() on iPhone.
      if (!greetingAudioRef.current) greetingAudioRef.current = new Audio();
      greetingAudioRef.current.src = audioUrl;
      greetingAudioRef.current.volume = volume;
      
      greetingAudioRef.current.onended = () => {
        console.log('[Greeting] tts ended');
        setTtsStatus('ended');
        URL.revokeObjectURL(audioUrl);
        // Fade out greeting after audio ends
        setTimeout(() => {
          console.log('[Greeting] fade out');
          setGreetingVisible(false);
          if (!forceGreet) {
            sessionStorage.setItem('havn_greeting_played', 'true');
          }
        }, 1500);
      };
      
      greetingAudioRef.current.onerror = (e) => {
        console.log('[Greeting] tts failed (audio error)', e);
        setTtsStatus('failed');
        URL.revokeObjectURL(audioUrl);
        // Keep text visible for 3 seconds on error
        setTimeout(() => {
          console.log('[Greeting] fade out (after error)');
          setGreetingVisible(false);
        }, 3000);
      };
      
      await greetingAudioRef.current.play();
      console.log('[Greeting] tts playing');
      
    } catch (error) {
      console.log('[Greeting] tts blocked/failed:', error.message);
      setTtsStatus(error.name === 'NotAllowedError' ? 'blocked' : 'failed');
      // Keep text visible for 3 seconds
      setTimeout(() => {
        console.log('[Greeting] fade out (after tts failure)');
        setGreetingVisible(false);
      }, 3000);
    }
  }, [ttsAttempted, forceGreet, volume]);

  // Fetch the latest extension version when the install modal opens, so the
  // button both SHOWS the version and CACHE-BUSTS the download (see below).
  useEffect(() => {
    if (!showExtensionModal || extVersion) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/config/extension-version`, { timeout: 8000 });
        if (!cancelled && res.data?.version) setExtVersion(res.data.version);
      } catch (_) { /* non-fatal — button still downloads latest */ }
    })();
    return () => { cancelled = true; };
  }, [showExtensionModal, extVersion]);

  // Handle user interaction (enables TTS)
  const handleUserInteraction = useCallback(() => {
    // Always prime the audio elements on the very first gesture — this is the
    // only reliable unlock window on iOS (Safari and Chrome alike). Must run
    // synchronously, before any await.
    unlockAudioPlayback();
    if (!userInteracted) {
      console.log('[Greeting] user interacted');
      setUserInteracted(true);

      // If greeting is showing and TTS not yet played, play it now
      if (shouldShowGreeting && !ttsAttempted) {
        playGreetingTTS();
      }
    }
  }, [userInteracted, shouldShowGreeting, ttsAttempted, playGreetingTTS, unlockAudioPlayback]);

  // Initialize greeting on mount
  useEffect(() => {
    console.log('[Greeting] mounted');
    
    const sessionGreeted = sessionStorage.getItem('havn_greeting_played');
    
    // Determine if greeting should show
    const shouldShow = forceGreet || !sessionGreeted;
    console.log('[Greeting] shouldShow:', shouldShow, '(forceGreet:', forceGreet, ', sessionGreeted:', sessionGreeted, ')');
    
    setShouldShowGreeting(shouldShow);
    
    if (shouldShow) {
      console.log('[Greeting] rendered');
      setGreetingRendered(true);
      setGreetingVisible(true);
      
      // Try to play TTS immediately (may be blocked without user interaction)
      const timer = setTimeout(() => {
        playGreetingTTS();
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [forceGreet, playGreetingTTS]);

  // Add global click listener for user interaction
  useEffect(() => {
    const handleClick = () => handleUserInteraction();
    const handleTouch = () => handleUserInteraction();
    const handleKey = () => handleUserInteraction();

    document.addEventListener('click', handleClick, { once: true });
    document.addEventListener('touchstart', handleTouch, { once: true });
    // iOS: WebKit grants user activation on touchend (touchstart alone may not
    // qualify), so the audio unlock must also hook touchend to be reliable.
    document.addEventListener('touchend', handleTouch, { once: true });
    document.addEventListener('keydown', handleKey, { once: true });

    return () => {
      document.removeEventListener('click', handleClick);
      document.removeEventListener('touchstart', handleTouch);
      document.removeEventListener('touchend', handleTouch);
      document.removeEventListener('keydown', handleKey);
    };
  }, [handleUserInteraction]);

  // Cleanup greeting audio on unmount
  useEffect(() => {
    return () => {
      if (greetingAudioRef.current) {
        greetingAudioRef.current.pause();
        greetingAudioRef.current = null;
      }
    };
  }, []);

  // Debug state for overlay
  const debugState = {
    pathname: window.location.pathname,
    forceGreet,
    sessionGreeted: sessionStorage.getItem('havn_greeting_played'),
    shouldShowGreeting,
    greetingRendered,
    userInteracted,
    ttsAttempted,
    ttsStatus,
    volume,
    isMuted: volume === 0
  };

  // Orb variants - subtle wobble animation (premium, calm aesthetic)
  // Scale max ~1.06, no jarring movements, no translate arrays
  const orbVariants = {
    idle: { 
      scale: 1, 
      rotate: 0,
      transition: { duration: 0.3, ease: [0.4, 0, 0.2, 1] } 
    },
    listening: { 
      scale: [1, 1.04, 1.02, 1.04, 1],
      rotate: [0, -0.5, 0.5, -0.3, 0],
      transition: { duration: 1.8, repeat: Infinity, ease: "easeInOut" } 
    },
    processing: { 
      scale: [1, 1.02, 1, 1.02, 1],
      rotate: [0, 0.3, -0.3, 0.2, 0],
      transition: { duration: 1.5, repeat: Infinity, ease: "easeInOut" } 
    },
    speaking: { 
      scale: [1, 1.03, 1.01, 1.03, 1],
      rotate: [0, -0.6, 0.6, -0.4, 0],
      transition: { duration: 1.2, repeat: Infinity, ease: "easeInOut" } 
    },
    error: { 
      scale: 1.02,
      rotate: 0,
      transition: { duration: 0.3, ease: [0.4, 0, 0.2, 1] } 
    }
  };

  // Handle play intro button
  const handlePlayIntro = useCallback(() => {
    localStorage.removeItem('havn_intro_seen');
    navigate('/intro-video?force=1');
  }, [navigate]);

  // FIX 3D: Show translated status text
  const STATUS_KEYS = {
    [STATES.IDLE]: 'status_tap',
    [STATES.LISTENING]: 'status_listening',
    [STATES.PROCESSING]: 'status_thinking',
    [STATES.SPEAKING]: 'status_speaking',
    [STATES.ERROR]: 'status_retry',
  };
  const currentStatus = langHandoffOverlay
    ? langHandoffOverlay
    : sessionJustEnded
      ? 'Session saved — tap to start again'
      : errorMessage || t(STATUS_KEYS[state] || 'status_tap');

  return (
    <div className="havn-container" style={{
      ...BG_STYLE,
      backgroundColor: theme.background,
      // Override the BG_STYLE background-image with the theme-specific
      // background. Each theme defines backgroundImage in themes.js.
      backgroundImage: `
        radial-gradient(ellipse at center, transparent 0%, transparent 40%, rgba(0,0,0,0.12) 100%),
        url(${theme.backgroundImage || '/assets/Smooth_blue_gradient_backdrop.png'})
      `,
    }} data-testid="home-page">
      <div className="havn-bg-gradient" aria-hidden="true" />
      {themeName === 'havn' && (
        <div className="havn-blob-layer" aria-hidden="true">
          <div className="havn-blob havn-blob-1" />
          <div className="havn-blob havn-blob-2" />
          <div className="havn-blob havn-blob-3" />
          <div className="havn-blob havn-blob-4" />
        </div>
      )}

      <header className="havn-header" style={{
        position: 'absolute',
        top: '20px',
        left: '0',
        right: '0',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 24px',
        boxSizing: 'border-box',
        zIndex: 10,
      }}>
        {/* Wordmark — centered absolutely so it doesn't shift with flex children */}
        <img
          src="/havn-logo-white.png"
          alt="HAVN"
          data-testid="havn-wordmark"
          style={{
            position: 'absolute',
            top: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '160px',
            height: 'auto',
            objectFit: 'contain',
            pointerEvents: 'none',
            filter: `drop-shadow(0 1px 2px rgba(0,0,0,0.3)) ${theme.logoTint !== 'none' ? theme.logoTint : ''}`.trim(),
          }}
        />

        {/* Spacer to keep flex layout balanced */}
        <div style={{ width: '40px' }} />

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {/* Language toggle */}
          <div style={{ display: 'flex', borderRadius: '14px', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.12)' }}>
            {languages.map(l => (
              <button key={l.code} onClick={() => handleLanguageHandoff(l.code)}
                disabled={langHandoffInFlightRef.current}
                style={{
                  minWidth: '44px', minHeight: '44px', padding: '10px 14px',
                  fontSize: '13px', fontWeight: lang === l.code ? 700 : 500,
                  background: lang === l.code ? 'var(--havn-primary-10)' : 'rgba(0,0,0,0.15)',
                  color: lang === l.code ? 'var(--havn-primary)' : 'rgba(255,255,255,0.5)',
                  border: 'none', cursor: 'pointer', transition: 'all 0.15s',
                  lineHeight: '1', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  opacity: langHandoffInFlightRef.current ? 0.5 : 1,
                }}>{l.flag}</button>
            ))}
          </div>
          {/* Play Intro Button - top-right */}
          <button
            onClick={handlePlayIntro}
            className="play-intro-btn"
            aria-label="Play intro video"
            data-testid="play-intro-button"
          >
            <Play size={12} />
            <span>{t('play_intro')}</span>
          </button>
        </div>
      </header>

      {/* Extension Install + Theme Picker — top-left */}
      <div style={{ position: 'absolute', top: '20px', left: '20px', zIndex: 100, display: 'flex', alignItems: 'center', gap: '8px' }}>
        <button
          data-testid="extension-download-button"
          onClick={() => setShowExtensionModal(true)}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '6px',
            width: 'auto', maxWidth: 'fit-content',
            background: 'rgba(0,0,0,0.2)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
            border: 'none', textDecoration: 'none',
            padding: '6px 12px', borderRadius: '16px', cursor: 'pointer',
            transition: 'background-color 0.2s ease',
          }}
          onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.35)'}
          onMouseLeave={e => e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.2)'}
        >
          <Download size={16} style={{ color: 'rgba(232, 244, 255, 0.75)' }} />
          <span style={{ fontFamily: "'Inter', sans-serif", fontSize: '11px', fontWeight: 500, color: 'rgba(232, 244, 255, 0.75)', whiteSpace: 'nowrap' }}>{t('ext_install')}</span>
        </button>
        <ThemePicker
          currentUser={authUser}
          currentTheme={themeName}
          onChange={setTheme}
        />
        {authUser && (
          <button
            onClick={() => signOut()}
            title={t('sign_out')}
            style={{
              fontSize: '11px',
              fontFamily: "'Inter', sans-serif",
              fontWeight: 500,
              opacity: 0.6,
              background: 'none',
              border: 'none',
              padding: '6px 10px',
              borderRadius: 8,
              color: 'rgba(232, 244, 255, 0.75)',
              cursor: 'pointer',
              transition: 'opacity 0.15s ease',
            }}
            onMouseEnter={e => { e.currentTarget.style.opacity = '1'; }}
            onMouseLeave={e => { e.currentTarget.style.opacity = '0.6'; }}
            data-testid="header-signout-btn"
          >
            {t('sign_out')}
          </button>
        )}
      </div>

      {/* Extension Install Modal — two-option chooser → beta instructions or waitlist */}
      <AnimatePresence>
        {showExtensionModal && (
          <ExtensionModal onClose={() => setShowExtensionModal(false)} />
        )}
      </AnimatePresence>

      {/* Complete Your Profile Modal */}
      <AnimatePresence>
        {showProfileModal && (
          <ProfileCompleteModal
            onClose={() => setShowProfileModal(false)}
            onComplete={() => window.location.reload()}
          />
        )}
      </AnimatePresence>

      <main className="havn-main">
        <motion.div
          className="hero-orb-container no-select"
          onClick={handleOrbTap}
          role="button"
          aria-label={state === STATES.IDLE ? "Tap to start talking" : state === STATES.LISTENING ? "Tap to stop" : currentStatus}
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleOrbTap(); } }}
          data-testid="hero-orb-button"
        >
          <motion.img
            src={theme.orbImage || HERO_ORB_URL}
            alt=""
            className={`hero-orb hero-orb-themed ${state === STATES.LISTENING ? 'orb-state-caution' : 'orb-state-safe'}`}
            style={{ filter: theme.orbFilter || 'none' }}
            variants={orbVariants}
            animate={state}
            draggable={false}
            data-testid="hero-orb-image"
            onError={(e) => { e.target.src = HERO_ORB_URL; }}
          />
        </motion.div>

        {/* HavnTalkWave — sine waveform between orb and status labels */}
        {process.env.REACT_APP_ENABLE_WAVE === 'true' && (
          <div style={{ marginTop: 12, marginBottom: 8 }}>
            <HavnTalkWave state={waveState} amplitude={waveAmplitude} color={theme.primary} />
          </div>
        )}

        {/* Greeting text - forced visible when active, high z-index */}
        {greetingVisible && (
          <div
            style={{
              opacity: 1,
              display: 'block',
              zIndex: 9999,
              position: 'relative',
              textAlign: 'center',
              marginBottom: '8px'
            }}
            data-testid="greeting-text"
          >
            <p className="greeting-text" style={{ opacity: 1, margin: 0 }}>
              {getGreetingText(authUser, t)}
            </p>
          </div>
        )}

        {/* v3.6.0: Centered status text under orb - always visible, not in scorecard */}
        <motion.p
          className={`status-text status-text-centered ${state === STATES.LISTENING ? 'listening' : ''} ${state === STATES.ERROR ? 'error' : ''}`}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          key={currentStatus}
          data-testid="status-text"
        >
          {currentStatus}
        </motion.p>

        {/* v3.6.0: "Not sure? Just tap." helper - visible on idle, centered under orb */}
        {state === STATES.IDLE && !greetingVisible && (
          <motion.p 
            className="slogan-text slogan-text-centered" 
            initial={{ opacity: 0, y: 5 }} 
            animate={{ opacity: 0.7, y: 0 }} 
            exit={{ opacity: 0 }} 
            transition={{ delay: 0.15 }}
            data-testid="slogan-text"
          >
            {t('not_sure_tap')}
          </motion.p>
        )}
        
        {/* Caregiver Reminder Banner */}
        {pendingReminders.length > 0 && !reminderDismissed && (
          <motion.div
            className="reminder-banner"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            data-testid="reminder-banner"
          >
            <Bell size={14} className="reminder-banner-icon" />
            <div className="reminder-banner-text">
              <span className="reminder-banner-title">{pendingReminders[0].title}</span>
              <span className="reminder-banner-body">{pendingReminders[0].body}</span>
            </div>
            <button
              className="reminder-banner-ok"
              onClick={() => handleAcknowledgeReminder(pendingReminders[0].id)}
              data-testid="reminder-banner-ok"
            >
              Got it
            </button>
          </motion.div>
        )}
      </main>

      {/* v3.5: Website Check Panel - positioned to the right of orb */}
      <AnimatePresence>
        {showWebsiteCheckPanel && (
          <motion.div 
            className="website-check-panel"
            initial={{ opacity: 0, x: 20, scale: 0.95 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: 20, scale: 0.95 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            data-testid="website-check-panel"
          >
            <div className="website-check-header">
              <Globe size={18} className="website-check-icon" />
              <h3 className="website-check-title">{t('check_website')}</h3>
              <button 
                className="website-check-close" 
                onClick={handleCloseWebsiteCheck}
                data-testid="website-check-close"
              >
                <X size={18} />
              </button>
            </div>
            
            <p className="website-check-helper">
              {t('check_website_helper')}
            </p>
            
            {websiteCheckConfirm ? (
              <div className="website-check-confirm">
                {websiteCheckConfirm.isBrandOnly ? (
                  <>
                    <p className="website-check-confirm-message">{websiteCheckConfirm.message}</p>
                    <button 
                      className="website-check-edit-btn"
                      onClick={handleWebsiteCheckEdit}
                      data-testid="website-check-edit"
                    >
                      Edit
                    </button>
                  </>
                ) : (
                  <>
                    <p className="website-check-confirm-text">
                      You typed <strong>{websiteCheckConfirm.hostname}</strong> — correct?
                    </p>
                    <div className="website-check-confirm-btns">
                      <button 
                        className="website-check-confirm-yes"
                        onClick={handleWebsiteCheckConfirm}
                        disabled={isCheckingWebsite}
                        data-testid="website-check-confirm-yes"
                      >
                        {isCheckingWebsite ? 'Checking...' : 'Yes'}
                      </button>
                      <button 
                        className="website-check-confirm-edit"
                        onClick={handleWebsiteCheckEdit}
                        data-testid="website-check-confirm-edit"
                      >
                        Edit
                      </button>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className="website-check-input-row">
                <input
                  type="text"
                  className="website-check-input"
                  placeholder={t('check_website_placeholder')}
                  value={websiteCheckInput}
                  onChange={(e) => setWebsiteCheckInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleWebsiteCheckSubmit(); }}
                  autoFocus
                  data-testid="website-check-input"
                />
                <button 
                  className="website-check-submit"
                  onClick={handleWebsiteCheckSubmit}
                  disabled={!websiteCheckInput.trim()}
                  data-testid="website-check-submit"
                >
                  <Send size={18} />
                </button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* LEFT PANEL: Session History */}
      <AnimatePresence>
        {showHistoryPanel && (
          <motion.div
            className="session-history-panel"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ type: 'spring', damping: 25 }}
            data-testid="session-history-panel"
          >
            <div className="history-panel-header">
              <History size={18} className="history-panel-icon" />
              <h3 className="history-panel-title">{t('session_history')}</h3>
              <button 
                className="history-panel-close" 
                onClick={() => { setShowHistoryPanel(false); setSelectedHistorySession(null); }}
                data-testid="history-panel-close"
              >
                <X size={18} />
              </button>
            </div>
            
            <div className="history-panel-actions">
              <button 
                className="history-new-session-btn"
                onClick={handleNewSession}
                data-testid="new-session-btn"
              >
                <Plus size={16} />
                <span>{t('new_session')}</span>
              </button>
            </div>
            
            <div className="history-panel-list">
              {sessionHistory.map((session) => (
                confirmDeleteSessionId === session.id ? (
                  // Bug 5: Inline delete confirmation — replaces the row
                  // temporarily to prevent accidental taps from seniors.
                  <div
                    key={session.id}
                    className="history-session-item history-session-item--confirm-delete"
                    data-testid={`history-session-confirm-${session.id}`}
                  >
                    <span className="history-session-confirm-text">{t('delete_session_confirm')}</span>
                    <div className="history-session-confirm-actions">
                      <button
                        type="button"
                        className="history-session-confirm-btn history-session-confirm-btn--danger"
                        onClick={(e) => { e.stopPropagation(); handleDeleteSession(session.id); }}
                        data-testid={`history-session-confirm-yes-${session.id}`}
                      >
                        {t('delete_yes')}
                      </button>
                      <button
                        type="button"
                        className="history-session-confirm-btn"
                        onClick={(e) => { e.stopPropagation(); setConfirmDeleteSessionId(null); }}
                        data-testid={`history-session-confirm-cancel-${session.id}`}
                      >
                        {t('delete_cancel')}
                      </button>
                    </div>
                  </div>
                ) : (
                <div
                  key={session.id}
                  className={`history-session-item ${selectedHistorySession?.id === session.id ? 'selected' : ''}`}
                  onClick={() => { setSelectedHistorySession(session); loadHistoryTranscript(session); }}
                  data-testid={`history-session-${session.id}`}
                >
                  <div className="history-session-info">
                    <span className="history-session-title">{session.title || t('untitled')}</span>
                    <span className="history-session-date">
                      {new Date(session.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                    </span>
                    <span className="history-session-stats">
                      {session.transcript_count || 0} {t('events')} · {session.websites_checked?.length || 0} {t('sites')}
                    </span>
                  </div>
                  {session.is_active && <span className="history-session-active-badge">{t('active')}</span>}
                  {/* Bug 5: Per-row delete trigger. Stops propagation so it
                      doesn't also open the transcript view. */}
                  <button
                    type="button"
                    className="history-session-delete-btn"
                    aria-label={t('delete_session_aria')}
                    onClick={(e) => { e.stopPropagation(); setConfirmDeleteSessionId(session.id); }}
                    data-testid={`history-session-delete-${session.id}`}
                  >
                    <Trash2 size={14} />
                  </button>
                  <ChevronRight size={16} className="history-session-arrow" />
                </div>
                )
              ))}

              {sessionHistory.length === 0 && (
                <div className="history-empty">
                  <p>{t('no_sessions')}</p>
                </div>
              )}

              {/* Bug 5: Clear-all footer. Only shown when there's something
                  to clear; collapses into an inline confirmation on click. */}
              {sessionHistory.length > 0 && (
                <div className="history-clear-all-footer">
                  {confirmClearAll ? (
                    <div className="history-clear-all-confirm">
                      <span className="history-clear-all-text">{t('clear_all_confirm')}</span>
                      <div className="history-clear-all-actions">
                        <button
                          type="button"
                          className="history-session-confirm-btn history-session-confirm-btn--danger"
                          onClick={handleClearAllSessions}
                          data-testid="clear-all-confirm-yes"
                        >
                          {t('clear_all_yes')}
                        </button>
                        <button
                          type="button"
                          className="history-session-confirm-btn"
                          onClick={() => setConfirmClearAll(false)}
                          data-testid="clear-all-confirm-cancel"
                        >
                          {t('delete_cancel')}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="history-clear-all-link"
                      onClick={() => setConfirmClearAll(true)}
                      data-testid="clear-all-link"
                    >
                      {t('clear_all_sessions')}
                    </button>
                  )}
                </div>
              )}

              {/* Bug 5: Transient error banner on delete failure. */}
              {deleteError && (
                <div className="history-delete-error" role="status">{t('delete_failed')}</div>
              )}
            </div>
            
            {selectedHistorySession && !selectedHistorySession.is_active && (
              <div className="history-session-actions">
                <button 
                  className="history-continue-btn"
                  onClick={() => handleRecallSession(selectedHistorySession.id)}
                  disabled={isRecallingSession}
                  data-testid="continue-session-btn"
                >
                  {isRecallingSession ? t('loading_session') : t('resume_session')}
                </button>
              </div>
            )}

            {/* FIX 2D: History transcript viewer */}
            {historyTranscriptView && (
              <div ref={historyTranscriptRef} className="history-transcript-view" data-testid="history-transcript-view">
                <div className="history-transcript-view-header">
                  <span className="history-transcript-view-title">
                    {historyTranscriptView.session?.title || 'Session'}
                  </span>
                  <button
                    className="history-transcript-view-close"
                    onClick={() => setHistoryTranscriptView(null)}
                    aria-label="Close transcript"
                  >
                    <X size={14} />
                  </button>
                </div>
                {isLoadingHistoryTranscript ? (
                  <p className="history-transcript-loading">Loading transcript...</p>
                ) : historyTranscriptView.events.length === 0 ? (
                  <p className="history-transcript-empty">No transcript for this session.</p>
                ) : (
                  <div className="history-transcript-events">
                    {historyTranscriptView.events.map((evt, i) => (
                      <div key={evt.id || i} className={`history-transcript-event history-transcript-event--${evt.speaker}`}>
                        <span className="history-transcript-event-speaker">
                          {evt.speaker === 'user' ? 'You' : evt.speaker === 'assistant' ? 'HAVN' : 'System'}
                        </span>
                        <span className="history-transcript-event-text">{evt.content}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* RIGHT PANEL: Current Transcript */}
      <AnimatePresence>
        {showTranscriptPanel && (
          <motion.div
            className="transcript-side-panel"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            transition={{ type: 'spring', damping: 25 }}
            data-testid="transcript-side-panel"
          >
            <div className="transcript-panel-header">
              <FileText size={18} className="transcript-panel-icon" />
              <h3 className="transcript-panel-title">Current Transcript</h3>
              <button 
                className="transcript-panel-close" 
                onClick={() => setShowTranscriptPanel(false)}
                data-testid="transcript-panel-close"
              >
                <X size={18} />
              </button>
            </div>
            
            {activeSession && (
              <div className="transcript-session-info">
                <span className="transcript-session-name">{activeSession.title || 'Current Session'}</span>
                <span className="transcript-session-events">{liveTranscript.length} events</span>
              </div>
            )}
            
            <div className="transcript-panel-list">
              {liveTranscript.map((event) => (
                <div 
                  key={event.id}
                  className={`transcript-event ${event.speaker}`}
                  data-testid={`transcript-event-${event.id}`}
                >
                  <div className="transcript-event-header">
                    <span className="transcript-event-speaker">
                      {event.speaker === 'user' ? 'You' : event.speaker === 'assistant' ? 'HAVN' : 'System'}
                    </span>
                    <span className="transcript-event-time">
                      {new Date(event.timestamp).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                    </span>
                  </div>
                  <p className="transcript-event-content">{event.content}</p>
                  {event.type === 'website_check' && event.metadata?.hostname && (
                    <span className="transcript-event-meta">
                      <Globe size={12} /> {event.metadata.hostname}
                    </span>
                  )}
                </div>
              ))}
              
              {liveTranscript.length === 0 && (
                <div className="transcript-empty">
                  <p>No conversation yet. Tap the orb to start talking.</p>
                </div>
              )}
              
              <div ref={transcriptEndRef} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* LEFT PANEL: Contacts */}
      <AnimatePresence>
        {showContactsPanel && (
          <motion.div
            className="contacts-panel"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ type: 'spring', damping: 25 }}
            data-testid="contacts-panel"
          >
            <div className="contacts-panel-header">
              <User size={18} className="contacts-panel-icon" />
              <h3 className="contacts-panel-title">Contacts</h3>
              <button 
                className="contacts-panel-close" 
                onClick={() => { setShowContactsPanel(false); setShowAddContactForm(false); setEditingContact(null); }}
                data-testid="contacts-panel-close"
              >
                <X size={18} />
              </button>
            </div>
            
            <div className="contacts-panel-actions">
              <button 
                className="contacts-add-btn"
                onClick={() => { setShowAddContactForm(!showAddContactForm); setEditingContact(null); }}
                data-testid="add-contact-btn"
              >
                <Plus size={16} />
                Add Contact
              </button>
            </div>
            
            {contactError && (
              <div className="contacts-error" data-testid="contact-error">
                <AlertTriangle size={14} />
                <span>{contactError}</span>
                <button onClick={() => setContactError('')}><X size={14} /></button>
              </div>
            )}
            
            {/* Add Contact Form */}
            {showAddContactForm && (
              <div className="contact-form" data-testid="add-contact-form">
                <input
                  type="text"
                  placeholder="Name *"
                  value={newContactForm.display_name}
                  onChange={(e) => setNewContactForm(prev => ({ ...prev, display_name: e.target.value }))}
                  className="contact-form-input"
                  data-testid="contact-name-input"
                />
                <input
                  type="tel"
                  placeholder="Phone"
                  value={newContactForm.phone}
                  onChange={(e) => setNewContactForm(prev => ({ ...prev, phone: e.target.value }))}
                  className="contact-form-input"
                  data-testid="contact-phone-input"
                />
                <input
                  type="email"
                  placeholder="Email"
                  value={newContactForm.email}
                  onChange={(e) => setNewContactForm(prev => ({ ...prev, email: e.target.value }))}
                  className="contact-form-input"
                  data-testid="contact-email-input"
                />
                <input
                  type="text"
                  placeholder="Relationship (e.g., Daughter)"
                  value={newContactForm.relationship}
                  onChange={(e) => setNewContactForm(prev => ({ ...prev, relationship: e.target.value }))}
                  className="contact-form-input"
                  data-testid="contact-relationship-input"
                />
                <textarea
                  placeholder="Memory notes (e.g., takes blood pressure meds, prefers texts)"
                  value={newContactForm.notes || ''}
                  onChange={(e) => setNewContactForm(prev => ({ ...prev, notes: e.target.value }))}
                  className="contact-form-input contact-form-notes"
                  rows={2}
                  data-testid="contact-notes-input"
                />
                
                <div className="contact-form-checkboxes">
                  <label className="contact-checkbox">
                    <input
                      type="checkbox"
                      checked={newContactForm.is_caregiver}
                      onChange={(e) => setNewContactForm(prev => ({ 
                        ...prev, 
                        is_caregiver: e.target.checked,
                        is_primary_caregiver: e.target.checked ? prev.is_primary_caregiver : false
                      }))}
                      data-testid="contact-caregiver-checkbox"
                    />
                    <span>Caregiver</span>
                  </label>
                  <label className="contact-checkbox">
                    <input
                      type="checkbox"
                      checked={newContactForm.is_primary_caregiver}
                      onChange={(e) => setNewContactForm(prev => ({ 
                        ...prev, 
                        is_primary_caregiver: e.target.checked,
                        is_caregiver: e.target.checked ? true : prev.is_caregiver
                      }))}
                      data-testid="contact-primary-checkbox"
                    />
                    <span>Primary Caregiver</span>
                  </label>
                </div>
                
                <div className="contact-form-buttons">
                  <button 
                    className="contact-form-cancel"
                    onClick={() => { setShowAddContactForm(false); setNewContactForm({ display_name: '', phone: '', email: '', relationship: '', is_caregiver: false, is_primary_caregiver: false, notes: '' }); }}
                    data-testid="contact-cancel-btn"
                  >
                    Cancel
                  </button>
                  <button 
                    className="contact-form-save"
                    onClick={handleCreateContact}
                    data-testid="contact-save-btn"
                  >
                    <Check size={16} />
                    Save
                  </button>
                </div>
              </div>
            )}
            
            {/* Edit Contact Form */}
            {editingContact && (
              <div className="contact-form contact-form--edit" data-testid="edit-contact-form">
                <div className="contact-form-edit-header">
                  <span className="contact-form-edit-title">Edit {editingContact.display_name}</span>
                  <button
                    className="contact-form-edit-close"
                    onClick={() => setEditingContact(null)}
                    aria-label="Cancel edit"
                  >
                    <X size={14} />
                  </button>
                </div>
                <input
                  type="text"
                  placeholder="Name *"
                  value={editForm.display_name}
                  onChange={(e) => setEditForm(prev => ({ ...prev, display_name: e.target.value }))}
                  className="contact-form-input"
                  data-testid="edit-contact-name"
                />
                <input
                  type="tel"
                  placeholder="Phone"
                  value={editForm.phone}
                  onChange={(e) => setEditForm(prev => ({ ...prev, phone: e.target.value }))}
                  className="contact-form-input"
                  data-testid="edit-contact-phone"
                />
                <input
                  type="email"
                  placeholder="Email"
                  value={editForm.email}
                  onChange={(e) => setEditForm(prev => ({ ...prev, email: e.target.value }))}
                  className="contact-form-input"
                  data-testid="edit-contact-email"
                />
                <input
                  type="text"
                  placeholder="Relationship (e.g., Daughter)"
                  value={editForm.relationship}
                  onChange={(e) => setEditForm(prev => ({ ...prev, relationship: e.target.value }))}
                  className="contact-form-input"
                  data-testid="edit-contact-relationship"
                />
                <textarea
                  placeholder="Notes (e.g., takes blood pressure meds, prefers texts)"
                  value={editForm.notes}
                  onChange={(e) => setEditForm(prev => ({ ...prev, notes: e.target.value }))}
                  className="contact-form-input contact-form-notes"
                  rows={2}
                  data-testid="edit-contact-notes"
                />
                <div className="contact-form-checkboxes">
                  <label className="contact-checkbox">
                    <input
                      type="checkbox"
                      checked={editForm.is_caregiver}
                      onChange={(e) => setEditForm(prev => ({
                        ...prev,
                        is_caregiver: e.target.checked,
                        is_primary_caregiver: e.target.checked ? prev.is_primary_caregiver : false
                      }))}
                    />
                    <span>Caregiver</span>
                  </label>
                  <label className="contact-checkbox">
                    <input
                      type="checkbox"
                      checked={editForm.is_primary_caregiver}
                      onChange={(e) => setEditForm(prev => ({
                        ...prev,
                        is_primary_caregiver: e.target.checked,
                        is_caregiver: e.target.checked ? true : prev.is_caregiver
                      }))}
                    />
                    <span>Primary Caregiver</span>
                  </label>
                </div>
                <div className="contact-form-buttons">
                  <button
                    className="contact-form-cancel"
                    onClick={() => setEditingContact(null)}
                  >
                    Cancel
                  </button>
                  <button
                    className="contact-form-save"
                    onClick={handleSaveEdit}
                    data-testid="edit-contact-save"
                  >
                    <Check size={16} />
                    Save
                  </button>
                </div>
              </div>
            )}
            
            {/* Contacts List */}
            <div className="contacts-list">
              {isLoadingContacts ? (
                <div className="contacts-loading">Loading contacts...</div>
              ) : contacts.length === 0 ? (
                <div className="contacts-empty">
                  <User size={32} className="contacts-empty-icon" />
                  <p>No contacts yet. Add someone you trust.</p>
                </div>
              ) : (
                contacts.map((contact) => (
                  <div 
                    key={contact.id}
                    className={`contact-card ${contact.is_primary_caregiver ? 'primary' : ''}`}
                    data-testid={`contact-card-${contact.id}`}
                  >
                    <div className="contact-card-header">
                      <div className="contact-card-name-row">
                        <span className="contact-card-name">{contact.display_name}</span>
                        {contact.is_primary_caregiver && (
                          <span className="contact-primary-badge" data-testid="primary-badge">
                            <Star size={12} /> Main Caregiver
                          </span>
                        )}
                      </div>
                      {contact.relationship && (
                        <span className="contact-card-relationship">{contact.relationship}</span>
                      )}
                    </div>
                    
                    <div className="contact-card-info">
                      {contact.phone && (
                        <button
                          className="contact-card-phone contact-card-phone--btn"
                          onClick={() => handleContactCall(contact)}
                          data-testid={`contact-call-btn-${contact.id}`}
                        >
                          <Phone size={14} /> {contact.phone}
                        </button>
                      )}
                      {contact.email && (
                        <a href={`mailto:${contact.email}`} className="contact-card-email">
                          <Mail size={14} /> {contact.email}
                        </a>
                      )}
                      {contact.notes && (
                        <p className="contact-card-notes" data-testid={`contact-notes-${contact.id}`}>
                          📝 {contact.notes}
                        </p>
                      )}
                    </div>
                    
                    <div className="contact-card-toggles">
                      <label className="contact-toggle">
                        <input
                          type="checkbox"
                          checked={contact.is_caregiver}
                          onChange={() => handleToggleCaregiver(contact, 'is_caregiver')}
                          data-testid={`toggle-caregiver-${contact.id}`}
                        />
                        <span>Caregiver</span>
                      </label>
                      <label className="contact-toggle">
                        <input
                          type="checkbox"
                          checked={contact.is_primary_caregiver}
                          onChange={() => handleToggleCaregiver(contact, 'is_primary_caregiver')}
                          data-testid={`toggle-primary-${contact.id}`}
                        />
                        <span>Primary</span>
                      </label>
                      <label className="contact-toggle contact-toggle--notify" title="Notify by email">
                        <input
                          type="checkbox"
                          checked={contact.notify_by_email || false}
                          onChange={() => handleToggleNotification(contact, 'notify_by_email')}
                          data-testid={`toggle-email-notify-${contact.id}`}
                        />
                        <span><Mail size={11} /> Email</span>
                      </label>
                      <label className="contact-toggle contact-toggle--notify" title="In-app notification">
                        <input
                          type="checkbox"
                          checked={contact.notify_in_app !== false}
                          onChange={() => handleToggleNotification(contact, 'notify_in_app')}
                          data-testid={`toggle-app-notify-${contact.id}`}
                        />
                        <span>🔔 In-app</span>
                      </label>
                    </div>
                    
                    <div className="contact-card-actions">
                      <button
                        className="contact-action-btn"
                        onClick={() => handleOpenEdit(contact)}
                        aria-label="Edit contact"
                        data-testid={`edit-contact-${contact.id}`}
                      >
                        <Edit2 size={14} />
                      </button>
                      <button 
                        className="contact-action-btn danger"
                        onClick={() => handleDeleteContact(contact.id)}
                        aria-label="Delete contact"
                        data-testid={`delete-contact-${contact.id}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
            
            {/* Caregiver Portal Access Code */}
            {accessCode && (
              <div className="contacts-access-code" data-testid="contacts-access-code">
                <span className="contacts-access-code-label">Caregiver portal code</span>
                <span className="contacts-access-code-value">{accessCode}</span>
                <span className="contacts-access-code-hint">Share this with your caregiver</span>
              </div>
            )}
            
            {/* Call Primary Caregiver - shown when primary exists */}
            {primaryCaregiver && (
              <div className="contacts-call-primary">
                <button 
                  className="call-primary-btn"
                  onClick={() => handleContactCall(primaryCaregiver)}
                  data-testid="call-primary-caregiver-btn"
                >
                  <Phone size={18} />
                  Contact {primaryCaregiver.display_name}
                </button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* In-app Contact / Call Modal */}
      <AnimatePresence>
        {callModalContact && (
          <motion.div
            className="call-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setCallModalContact(null)}
            data-testid="call-modal-overlay"
          >
            <motion.div
              className="call-modal"
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
              data-testid="call-modal"
            >
              {/* Contact avatar + name */}
              <div className="call-modal-avatar">
                <User size={32} />
              </div>
              <h3 className="call-modal-name">{callModalContact.display_name}</h3>
              {callModalContact.relationship && (
                <p className="call-modal-relationship">{callModalContact.relationship}</p>
              )}
              {callModalContact.is_primary_caregiver && (
                <span className="call-modal-primary-badge">
                  <Star size={12} /> Primary Caregiver
                </span>
              )}

              {/* Action buttons */}
              <div className="call-modal-actions">
                {callModalContact.phone && (
                  <a
                    href={`tel:${callModalContact.phone}`}
                    className="call-modal-btn call-modal-btn--phone"
                    onClick={() => {
                      sessionLogger.log('contact_call_phone', {
                        contactId: callModalContact.id,
                        displayName: callModalContact.display_name
                      });
                      setCallModalContact(null);
                    }}
                    data-testid="call-modal-phone-btn"
                  >
                    <Phone size={20} />
                    <span>Call {callModalContact.phone}</span>
                  </a>
                )}
                {callModalContact.email && (
                  <a
                    href={`mailto:${callModalContact.email}`}
                    className="call-modal-btn call-modal-btn--email"
                    onClick={() => {
                      sessionLogger.log('contact_email', {
                        contactId: callModalContact.id,
                        displayName: callModalContact.display_name
                      });
                      setCallModalContact(null);
                    }}
                    data-testid="call-modal-email-btn"
                  >
                    <Mail size={20} />
                    <span>Email {callModalContact.email}</span>
                  </a>
                )}
                {!callModalContact.phone && !callModalContact.email && (
                  <p className="call-modal-no-contact">No phone or email saved for this contact.</p>
                )}
              </div>

              {/* Notes reminder */}
              {callModalContact.notes && (
                <p className="call-modal-notes">📝 {callModalContact.notes}</p>
              )}

              <button
                className="call-modal-dismiss"
                onClick={() => setCallModalContact(null)}
                data-testid="call-modal-dismiss"
              >
                Cancel
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Unified utility bar - all controls in one row */}
      <nav className="utility-bar" aria-label="Utility controls">
        <button
          className={`utility-btn ${showHistoryPanel ? 'active' : ''}`}
          onClick={() => { setShowHistoryPanel(!showHistoryPanel); setShowTranscriptPanel(false); loadSessionHistory(); }}
          data-testid="history-panel-button"
        >
          <History className="utility-btn-icon" />
          <span className="utility-btn-label">{t('nav_history')}</span>
        </button>

        <button
          className={`utility-btn ${showVolume ? 'active' : ''}`}
          onClick={() => { setShowVolume(!showVolume); setShowKeyboard(false); setShowTranscript(false); setShowWebsiteCheckPanel(false); }}
          data-testid="volume-button"
        >
          {volume === 0 ? <VolumeX className="utility-btn-icon" /> : <Volume2 className="utility-btn-icon" />}
          <span className="utility-btn-label">{t('nav_volume')}</span>
        </button>

        <button
          className={`utility-btn ${showTranscriptPanel ? 'active' : ''}`}
          onClick={() => { setShowTranscriptPanel(!showTranscriptPanel); setShowHistoryPanel(false); }}
          data-testid="transcript-panel-button"
        >
          <FileText className="utility-btn-icon" />
          <span className="utility-btn-label">{t('nav_transcript')}</span>
        </button>

        <button
          className={`utility-btn ${showKeyboard ? 'active' : ''}`}
          onClick={() => { setShowKeyboard(!showKeyboard); setShowVolume(false); setShowTranscript(false); setShowWebsiteCheckPanel(false); }}
          data-testid="type-button"
        >
          <Keyboard className="utility-btn-icon" />
          <span className="utility-btn-label">{t('nav_type')}</span>
        </button>

        <button
          className={`utility-btn ${showWebsiteCheckPanel ? 'active' : ''}`}
          onClick={handleOpenWebsiteCheck}
          data-testid="website-check-button"
        >
          <Globe className="utility-btn-icon" />
          <span className="utility-btn-label">{t('nav_website')}</span>
        </button>

        <button
          className="utility-btn"
          onClick={() => navigate('/trusted-sites')}
          data-testid="trusted-sites-button"
        >
          <Bookmark className="utility-btn-icon" />
          <span className="utility-btn-label">{t('nav_sites')}</span>
        </button>

        <button
          className={`utility-btn ${showContactsPanel ? 'active' : ''}`}
          onClick={() => { setShowContactsPanel(!showContactsPanel); setShowHistoryPanel(false); setShowTranscriptPanel(false); loadContacts(); }}
          data-testid="contacts-panel-button"
        >
          <User className="utility-btn-icon" />
          <span className="utility-btn-label">{t('nav_contacts')}</span>
        </button>
      </nav>

      <audio ref={audioRef} onEnded={handleAudioEnded} onError={handleAudioError} data-testid="audio-player" />

      {/* Link Card Pop-up */}
      <AnimatePresence>
        {linkCardData && (
          <LinkCard 
            links={linkCardData} 
            onDismiss={() => setLinkCardData(null)} 
          />
        )}
      </AnimatePresence>

      {/* Website Scorecard — Unified single card */}
      <AnimatePresence>
        {isWebsiteSidePanelOpen && (
          <FullSiteReport
            data={sidePanelData}
            loading={isFetchingWebsitePanel}
            error={sidePanelError}
            onClose={handleCloseSidePanel}
            onReplayVoice={handleReplayVoice}
            onRetry={() => lastCheckedHostname && fetchWebsitePanel(lastCheckedHostname)}
            isPlaying={isVoicePlaying}
            isMuted={isSidePanelMuted}
            onToggleMute={handleToggleSidePanelMute}
            onSaveFavorite={handleSaveFavorite}
            onCommunityFlag={handleCommunityFlag}
            onAskCaregiver={primaryCaregiver ? handleAskCaregiver : null}
            onNotifyCaregiver={primaryCaregiver ? handleNotifyCaregiver : null}
            caregiverName={primaryCaregiver?.display_name || null}
            isAlreadySaved={savedFavorites.has(sidePanelData?.domain)}
            isAlreadyFlagged={flaggedDomains.has(sidePanelData?.domain)}
            actionState={panelActionState}
            onLookDeeper={() => {
              if (lastCheckedHostname && !isDeepScanning) triggerDeepScan(lastCheckedHostname, sidePanelData);
            }}
            isDeepScanning={isDeepScanning}
            onGenerateReport={handleGenerateFullReport}
            isGeneratingReport={isGeneratingReport}
            onTypoGateConfirm={() => handleTypoGateConfirm(lastCheckedHostname)}
            onTypoGateReject={handleTypoGateReject}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {(showTranscript || showKeyboard) && (
          <motion.div className="overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => { setShowTranscript(false); setShowKeyboard(false); }} />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showVolume && (
          <motion.div className="volume-panel" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }} data-testid="volume-panel">
            <VolumeX size={20} className={volume === 0 ? 'text-[var(--sidekick-primary)]' : 'text-[var(--sidekick-text-secondary)]'} />
            <input type="range" min="0" max="1" step="0.1" value={volume} onChange={(e) => setVolume(parseFloat(e.target.value))} className="volume-slider" />
            <Volume2 size={20} className={volume === 1 ? 'text-[var(--sidekick-primary)]' : 'text-[var(--sidekick-text-secondary)]'} />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Type Panel - Clean modal for text input */}
      <AnimatePresence>
        {showKeyboard && (
          <motion.div 
            className="type-panel-overlay" 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            exit={{ opacity: 0 }}
            onClick={() => setShowKeyboard(false)}
            data-testid="type-panel-overlay"
          >
            <motion.div 
              className="type-panel" 
              initial={{ y: 50, opacity: 0, scale: 0.95 }} 
              animate={{ y: 0, opacity: 1, scale: 1 }} 
              exit={{ y: 50, opacity: 0, scale: 0.95 }} 
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
              data-testid="type-panel"
            >
              <div className="type-panel-header">
                <h3 className="type-panel-title">Tell me what happened</h3>
                <button 
                  className="type-panel-close" 
                  onClick={() => setShowKeyboard(false)}
                  data-testid="type-panel-close"
                >
                  <X size={20} />
                </button>
              </div>
              <div className="type-panel-body">
                <textarea 
                  className="type-panel-input" 
                  placeholder="Type what happened…" 
                  value={keyboardInput} 
                  onChange={(e) => setKeyboardInput(e.target.value)} 
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleKeyboardSubmit(); } }} 
                  rows={3} 
                  autoFocus 
                  data-testid="type-panel-input" 
                />
              </div>
              <div className="type-panel-footer">
                <button 
                  className="type-panel-send" 
                  onClick={handleKeyboardSubmit} 
                  disabled={!keyboardInput.trim() || isProcessingRef.current}
                  data-testid="type-panel-send"
                >
                  <Send size={18} />
                  <span>Send</span>
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Conversation History Panel */}
      <AnimatePresence>
        {showTranscript && (
          <motion.div className="transcript-panel custom-scrollbar" initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }} transition={{ type: 'spring', damping: 25, stiffness: 300 }} data-testid="transcript-panel">
            <div className="transcript-header">
              <h2 className="transcript-title">Conversation</h2>
              <button className="transcript-close" onClick={() => setShowTranscript(false)}><X size={24} /></button>
            </div>
            <div className="transcript-messages">
              {messages.length === 0 ? (
                <p className="transcript-empty">Your conversation will appear here.</p>
              ) : (
                messages.map((msg, index) => (
                  <div key={index} className={`transcript-message ${msg.role}${msg.isNudge ? ' is-nudge' : ''}${msg.isSystemNotice ? ' is-system-notice' : ''}`}>
                    {msg.html ? (
                      <p className="transcript-message-text" dangerouslySetInnerHTML={{ __html: msg.html }} />
                    ) : (
                      <p className="transcript-message-text">{msg.content}</p>
                    )}
                  </div>
                ))
              )}
              {/* Bug 4: Quick-reply buttons under the inactivity nudge. */}
              {waitingForContinue && (
                <div className="transcript-nudge-actions" data-testid="inactivity-nudge-actions">
                  <button
                    type="button"
                    className="transcript-nudge-btn transcript-nudge-btn--primary"
                    onClick={handleContinueYes}
                    data-testid="nudge-continue-yes"
                  >
                    {t('continue_yes')}
                  </button>
                  <button
                    type="button"
                    className="transcript-nudge-btn transcript-nudge-btn--secondary"
                    onClick={handleContinueNoEnd}
                    data-testid="nudge-continue-no"
                  >
                    {t('continue_no_end')}
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
